import React, { useState, useMemo, useRef, useEffect } from "react";
import * as THREE from "three";
import {
  ALL_LAYERS,
  BOX_FACES,
  DEFAULT_DESIGN,
  DEFAULT_VIEWS,
  DESIGN_VERSION,
  EXAMPLES,
  FIXINGS,
  FRAMING_ONLY,
  GUTTER,
  LAYERS,
  LOCATIONS,
  LAYER_COLOURS,
  ORIENTATIONS,
  OSB_T,
  PALETTES,
  PART_LAYER,
  PIR_BOARDS,
  planTape,
  planWrap,
  planRoofWrap,
  WRAP_ROLL_W,
  tapeExtra,
  tapeTrade,
  TAPE_W,
  TAPE_MIN_LAP,
  BATTEN_MIN,
  PIR_STAGGER_MIN,
  PIR_T,
  ROOF_PIR_MIN,
  SECTIONS,
  SHEETS,
  SIDES,
  STOCK,
  VIEW_DEPTH,
  allocateSheets,
  angledSheetCuts,
  boxesToSTL,
  buildEnvelope,
  buildFrame,
  buildRoof,
  buildWalls,
  buildBattens,
  buildCladding,
  CLAD_MIN,
  joistBattens,
  checkSupport,
  cornersOf,
  darken,
  extents,
  facingOf,
  fallOf,
  floorInsulation,
  furringCover,
  furringWidths,
  furringPieces,
  heatLoads,
  fmt,
  hexInt,
  inflate,
  lighten,
  m2,
  makeSection,
  massingBoxes,
  migrateDesign,
  packStock,
  planFixings,
  planSheets,
  podBoxes,
  quantise,
  priceTotal,
  r,
  rollUp,
  stepPlaces,
  sideLabel,
  viewForSide,
  wallBox,
  wallInsulation,
  windowSizes,
} from "./engine";

/* ------------------------------- 3D ------------------------------- */

function Model({ boxes, footprint, ghost, dims = [], showDims = true,
  view, captureRef, onPick }) {
  const mount = useRef(null);
  const s = useRef({});
  const pickRef = useRef(onPick);
  pickRef.current = onPick;

  useEffect(() => {
    const el = mount.current;
    if (!el) return;
    const st = s.current;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xe6e7e0);
    const camera = new THREE.PerspectiveCamera(38, 1, 100, 400000);
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    el.appendChild(renderer.domElement);
    const canvas = renderer.domElement;
    canvas.style.display = "block";
    canvas.style.touchAction = "none";
    canvas.tabIndex = 0;
    canvas.setAttribute("aria-label", "3D model. Drag to orbit, arrow keys to pan.");

    /* Dimension text is HTML projected onto the canvas — crisper than a
       sprite and it inherits the page's typography. */
    const labelLayer = document.createElement("div");
    labelLayer.className = "dimlayer";
    el.appendChild(labelLayer);

    scene.add(new THREE.AmbientLight(0xffffff, 0.74));
    const key = new THREE.DirectionalLight(0xffffff, 0.52);
    key.position.set(-1, 2.2, 1.4);
    scene.add(key);
    const fill = new THREE.DirectionalLight(0xffffff, 0.2);
    fill.position.set(2, 0.6, -1.5);
    scene.add(fill);

    const group = new THREE.Group();
    scene.add(group);
    Object.assign(st, {
      scene, camera, renderer, group, el,
      theta: -1.27, phi: 0.92, radius: 9000,
      target: new THREE.Vector3(0, 0, 0), lastW: 0, lastH: 0,
      labelLayer, labels: [],
    });

    const render = () => {
      const { radius, phi, theta, target } = st;
      camera.position.set(
        target.x + radius * Math.sin(phi) * Math.sin(theta),
        target.y + radius * Math.cos(phi),
        target.z + radius * Math.sin(phi) * Math.cos(theta)
      );
      camera.lookAt(target);
      renderer.render(scene, camera);
      st.labels.forEach(({ el: node, v }) => {
        const q = v.clone().project(camera);
        node.style.transform =
          `translate(-50%,-50%) translate(${(q.x * 0.5 + 0.5) * st.lastW}px,` +
          `${(-q.y * 0.5 + 0.5) * st.lastH}px)`;
        node.style.opacity = q.z > 1 ? "0" : "1";
      });
    };
    st.render = render;

    /* Pan moves the orbit target across the screen plane, so it behaves the
       same whichever way the model is currently turned. */
    const panBy = (dx, dy) => {
      const camPos = new THREE.Vector3(
        st.radius * Math.sin(st.phi) * Math.sin(st.theta),
        st.radius * Math.cos(st.phi),
        st.radius * Math.sin(st.phi) * Math.cos(st.theta)
      );
      const forward = camPos.clone().negate().normalize();
      const right = new THREE.Vector3().crossVectors(forward, new THREE.Vector3(0, 1, 0)).normalize();
      const up = new THREE.Vector3().crossVectors(right, forward).normalize();
      st.target.addScaledVector(right, dx);
      st.target.addScaledVector(up, dy);
      render();
    };
    st.panBy = panBy;

    const applyView = (v) => {
      const fit = st.fit || { L: 4000, W: 3000, focusY: 1000 };
      const preset = v || { theta: -1.27, phi: 0.92, zoom: 1.9, focus: 1 };
      st.theta = preset.theta;
      st.phi = Math.max(0.08, Math.min(Math.PI / 2 - 0.02, preset.phi));
      st.radius = Math.max(1200, Math.max(fit.L, fit.W) * (preset.zoom || 1.9));
      st.target.set(0, fit.focusY * (preset.focus == null ? 1 : preset.focus), 0);
      render();
    };
    st.applyView = applyView;
    st.resetView = () => applyView(st.view);
    st.capture = () => {
      const fit = st.fit || { L: 1, W: 1, focusY: 1 };
      return {
        theta: Math.round(st.theta * 1000) / 1000,
        phi: Math.round(st.phi * 1000) / 1000,
        zoom: Math.round((st.radius / Math.max(fit.L, fit.W)) * 1000) / 1000,
        focus: Math.round((st.target.y / (fit.focusY || 1)) * 1000) / 1000,
      };
    };

    const resize = () => {
      const w = Math.floor(el.clientWidth);
      const h = Math.floor(el.clientHeight);
      if (!w || !h || (w === st.lastW && h === st.lastH)) return;
      st.lastW = w;
      st.lastH = h;
      renderer.setSize(w, h, true);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      render();
    };
    st.resize = resize;

    let pending = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(pending);
      pending = requestAnimationFrame(resize);
    });
    ro.observe(el);

    const pointers = new Map();
    let pinch = null;
    let midpoint = null;
    /* Where a press started and how far it has travelled, so a click can be
       told from the start of an orbit. */
    let press = null;
    const raycaster = new THREE.Raycaster();
    const onDown = (e) => {
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      press = pointers.size === 1
        ? { id: e.pointerId, x: e.clientX, y: e.clientY, moved: 0 }
        : null;
      canvas.setPointerCapture(e.pointerId);
      canvas.focus({ preventScroll: true });
    };

    /* Nearest solid under the cursor. Ghosted pieces are pickable — being
       able to click the thing you can see through is rather the point. */
    const pickAt = (e) => {
      const fn = pickRef.current;
      if (!fn) return;
      const rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      raycaster.setFromCamera(
        new THREE.Vector2(
          ((e.clientX - rect.left) / rect.width) * 2 - 1,
          -((e.clientY - rect.top) / rect.height) * 2 + 1
        ),
        st.camera
      );
      const hit = raycaster
        .intersectObjects(st.group.children, false)
        .find((i) => i.object.isMesh && i.object.userData.box);
      fn(hit ? hit.object.userData.box : null);
    };
    const onMove = (e) => {
      const prev = pointers.get(e.pointerId);
      if (!prev) return;
      if (press && press.id === e.pointerId)
        press.moved += Math.hypot(e.clientX - prev.x, e.clientY - prev.y);
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.size === 1) {
        st.theta -= (e.clientX - prev.x) * 0.006;
        st.phi = Math.max(0.08, Math.min(Math.PI / 2 - 0.02, st.phi - (e.clientY - prev.y) * 0.006));
        render();
      } else if (pointers.size === 2) {
        const [a, b] = [...pointers.values()];
        const dist = Math.hypot(a.x - b.x, a.y - b.y);
        const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
        if (pinch && dist > 0)
          st.radius = Math.max(1200, Math.min(50000, st.radius * (pinch / dist)));
        if (midpoint) {
          const perPx = (2 * st.radius * Math.tan((38 * Math.PI) / 360)) / (st.lastH || 1);
          panBy(-(mid.x - midpoint.x) * perPx, (mid.y - midpoint.y) * perPx);
        }
        pinch = dist;
        midpoint = mid;
        render();
      }
    };
    const onUp = (e) => {
      /* Four pixels of slack, so a click with a shaky hand is still a click
         and the smallest deliberate orbit is still an orbit. */
      if (press && press.id === e.pointerId && press.moved < 4 && pointers.size === 1)
        pickAt(e);
      press = null;
      pointers.delete(e.pointerId);
      if (pointers.size < 2) { pinch = null; midpoint = null; }
    };
    const onWheel = (e) => {
      e.preventDefault();
      st.radius = Math.max(1200, Math.min(50000, st.radius * (1 + e.deltaY * 0.0014)));
      render();
    };

    const onKey = (e) => {
      const step = st.radius * (e.shiftKey ? 0.22 : 0.06);
      const orbit = e.altKey;
      const moves = {
        ArrowLeft: () => (orbit ? (st.theta += 0.12) : panBy(-step, 0)),
        ArrowRight: () => (orbit ? (st.theta -= 0.12) : panBy(step, 0)),
        ArrowUp: () => (orbit
          ? (st.phi = Math.max(0.08, st.phi - 0.08))
          : panBy(0, step)),
        ArrowDown: () => (orbit
          ? (st.phi = Math.min(Math.PI / 2 - 0.02, st.phi + 0.08))
          : panBy(0, -step)),
        "+": () => (st.radius = Math.max(1200, st.radius * 0.9)),
        "=": () => (st.radius = Math.max(1200, st.radius * 0.9)),
        "-": () => (st.radius = Math.min(50000, st.radius * 1.1)),
        _: () => (st.radius = Math.min(50000, st.radius * 1.1)),
        Home: () => st.resetView(),
        "0": () => st.resetView(),
      };
      const fn = moves[e.key];
      if (!fn) return;
      e.preventDefault();
      fn();
      render();
    };

    canvas.addEventListener("keydown", onKey);
    canvas.addEventListener("pointerdown", onDown);
    canvas.addEventListener("pointermove", onMove);
    canvas.addEventListener("pointerup", onUp);
    canvas.addEventListener("pointercancel", onUp);
    canvas.addEventListener("wheel", onWheel, { passive: false });

    resize();
    st.ready = true;
    if (captureRef) captureRef.current = { capture: () => st.capture(), reset: () => st.resetView() };
    if (st.rebuild) st.rebuild();

    return () => {
      cancelAnimationFrame(pending);
      ro.disconnect();
      canvas.removeEventListener("keydown", onKey);
      canvas.removeEventListener("pointerdown", onDown);
      canvas.removeEventListener("pointermove", onMove);
      canvas.removeEventListener("pointerup", onUp);
      canvas.removeEventListener("pointercancel", onUp);
      canvas.removeEventListener("wheel", onWheel);
      renderer.dispose();
      canvas.remove();
      labelLayer.remove();
    };
  }, []);

  useEffect(() => {
    const st = s.current;
    const rebuild = () => {
      const { group, render, resize } = st;
      if (!group) return;
      while (group.children.length) {
        const ch = group.children.pop();
        ch.traverse?.((o) => {
          o.geometry?.dispose?.();
          if (Array.isArray(o.material)) o.material.forEach((m) => m.dispose?.());
          else o.material?.dispose?.();
        });
      }

      const { L, W, d } = footprint;
      const cx = footprint.cx != null ? footprint.cx : L / 2;
      const cz = footprint.cz != null ? footprint.cz : W / 2;
      /* A box is see-through either because it says so — glass always is —
         or because the view is in see-through mode and it's a layer that
         participates. depthWrite off so framing behind stays visible. */
      const cache = new Map();
      const matFor = (color, soft, opacity) => {
        const k = `${color}-${soft}-${opacity}`;
        if (!cache.has(k))
          cache.set(
            k,
            new THREE.MeshLambertMaterial({
              color,
              transparent: soft,
              opacity: soft ? opacity : 1,
              depthWrite: !soft,
              side: soft ? THREE.DoubleSide : THREE.FrontSide,
            })
          );
        return cache.get(k);
      };

      const edges = new Map();
      const edgeFor = (color, opacity) => {
        const k = `${color}-${opacity}`;
        if (!edges.has(k))
          edges.set(k, new THREE.LineBasicMaterial({ color, transparent: true, opacity }));
        return edges.get(k);
      };
      const edge = edgeFor(0x2b3831, 0.5);
      const edgeSoft = edgeFor(0x5a4a22, ghost ? 0.9 : 0.55);

      /* Indexed geometry from the eight corners: a plain box, a wedge and a
         sheared roof member are all the same eight points. Indexed so
         EdgesGeometry finds the twelve real edges and no diagonals. */
      const INDICES = [];
      BOX_FACES.forEach((f) => {
        INDICES.push(f.v[0], f.v[1], f.v[2], f.v[0], f.v[2], f.v[3]);
      });

      boxes.forEach((b) => {
        if (b.dx <= 0 || b.dz <= 0) return;
        if (b.dy <= 0 && !b.slopeTop) return;
        const soft = !!b.translucent || (ghost && b.ghostable);
        const op = b.opacity != null ? b.opacity : 0.32;

        const pts = cornersOf(b);
        const verts = new Float32Array(24);
        pts.forEach((p, i) => {
          verts[i * 3] = p[0] - cx;
          verts[i * 3 + 1] = p[1];
          verts[i * 3 + 2] = p[2] - cz;
        });
        const geo = new THREE.BufferGeometry();
        geo.setAttribute("position", new THREE.BufferAttribute(verts, 3));
        geo.setIndex(INDICES);
        geo.computeVertexNormals();

        const mesh = new THREE.Mesh(geo, matFor(b.color, soft, op));
        if (soft) mesh.renderOrder = 2;
        /* What this solid is, for click-to-identify. The raycaster only looks
           at meshes, so the edge lines below are left untagged. */
        mesh.userData.box = b;
        group.add(mesh);

        if (!b.plain) {
          const em = b.edgeHex != null
            ? edgeFor(b.edgeHex, 0.8)
            : soft || b.ghostable ? edgeSoft : edge;
          const lines = new THREE.LineSegments(new THREE.EdgesGeometry(geo, 15), em);
          if (soft) lines.renderOrder = 3;
          group.add(lines);
        }
      });

      st.labelLayer.innerHTML = "";
      st.labels = [];
      if (showDims && dims.length) {
        const dimMat = new THREE.LineBasicMaterial({ color: 0x1b2420 });
        const tick = Math.max(40, Math.max(L, W) / 90);
        const v = (a) => new THREE.Vector3(a[0] - cx, a[1], a[2] - cz);

        dims.forEach((dm) => {
          const a = v(dm.from);
          const b = v(dm.to);
          const line = new THREE.Line(new THREE.BufferGeometry().setFromPoints([a, b]), dimMat);
          group.add(line);

          /* End ticks, drawn on whichever axes the dimension doesn't run along. */
          const dir = b.clone().sub(a).normalize();
          const alt = Math.abs(dir.y) > 0.5
            ? new THREE.Vector3(1, 0, 0)
            : new THREE.Vector3(0, 1, 0);
          const perp = dir.clone().cross(alt).normalize().multiplyScalar(tick);
          [a, b].forEach((pt) => {
            group.add(new THREE.Line(
              new THREE.BufferGeometry().setFromPoints([
                pt.clone().sub(perp), pt.clone().add(perp),
              ]), dimMat));
          });

          const node = document.createElement("span");
          node.className = dm.strong ? "dimlabel strong" : "dimlabel";
          node.textContent = dm.label;
          st.labelLayer.appendChild(node);
          st.labels.push({ el: node, v: a.clone().add(b).multiplyScalar(0.5) });
        });
      }

      /* Ground: a plane big enough to read as endless, with a grid just above
         it so the two never fight over the same depth. */
      const gy = footprint.groundY != null ? footprint.groundY : 0;
      const span = Math.max(L, W);
      /* Plane and grid follow the centring so the pod never drifts off them. */
      /* Semi-transparent and not depth-writing, so anything below ground —
         an exploded bottom plate, say — still reads instead of disappearing.
         Drawn first so it never sorts in front of the building. */
      const plane = new THREE.Mesh(
        new THREE.PlaneGeometry(span * 30, span * 30),
        new THREE.MeshLambertMaterial({
          color: 0xd7dbd0, transparent: true, opacity: 0.5,
          depthWrite: false, side: THREE.DoubleSide,
        })
      );
      plane.rotation.x = -Math.PI / 2;
      plane.position.set(0, gy, 0);
      plane.renderOrder = -2;
      group.add(plane);

      const gridSpan = Math.ceil((span * 4) / 1000) * 1000;
      const grid = new THREE.GridHelper(gridSpan, gridSpan / 500, 0xa8b0a4, 0xc6ccc0);
      grid.position.set(0, gy + 1, 0);
      grid.material.transparent = true;
      grid.material.opacity = 0.45;
      grid.material.depthWrite = false;
      grid.renderOrder = -1;
      group.add(grid);

      const focusY = footprint.focusY != null ? footprint.focusY : d / 2;
      st.fit = { L, W, focusY };
      st.view = view;
      const sig = `${L}x${W}x${cx}x${cz}`;
      if (st.fitSig !== sig) {
        st.fitSig = sig;
        st.applyView(view);
      }
      resize?.();
      render?.();
    };
    st.rebuild = rebuild;
    if (!st.ready) return undefined;
    /* Dragging the explode slider fires a change per pointer move, and each
       rebuild recreates every geometry. Coalescing to one per frame means a
       burst of events costs one rebuild, not ten. */
    const queued = requestAnimationFrame(rebuild);
    return () => cancelAnimationFrame(queued);
  }, [boxes, footprint, ghost, dims, showDims, view]);

  return <div ref={mount} className="viewport" />;
}

/* ------------------------------- SVG ------------------------------- */

function PlanView({ frame, plan, L, W, spacing, gutter, wall, fixings }) {
  const pad = 320;
  const tick = 46;
  const anchors = new Set(frame.anchors.map((a) => r(a)));

  return (
    <svg viewBox={`${-pad} ${-pad} ${L + pad * 2} ${W + pad * 2}`} className="draw" preserveAspectRatio="xMidYMid meet">
      {frame.members.map((m, i) => (
        <rect key={i} x={m.x0} y={m.z0} width={m.dx} height={m.dz}
          className={m.role === "Noggin" ? "mem noggin" : "mem"} />
      ))}
      {plan.xJoints.map((j, i) => (
        <line key={i} x1={j} y1={-60} x2={j} y2={W + 60} className="joint faint" />
      ))}
      <FixingMarks panel={fixings} />

      {wall && wall.side !== "none" && (() => {
        const t = 220;
        const g = wall.gap;
        const ox = wall.offsetX || 0;
        const oz = wall.offsetZ || 0;
        const box =
          wall.side === "left" ? { x: ox - g - t, y: -200, w: t, h: W + 400 }
          : wall.side === "right" ? { x: ox + wall.bL + g, y: -200, w: t, h: W + 400 }
          : wall.side === "rear" ? { x: -200, y: oz - g - t, w: L + 400, h: t }
          : { x: -200, y: oz + wall.bW + g, w: L + 400, h: t };
        const vert = wall.side === "left" || wall.side === "right";
        return (
          <g>
            <rect x={box.x} y={box.y} width={box.w} height={box.h} className="wallband" />
            <text
              x={vert ? (wall.side === "left" ? ox - g / 2 : ox + wall.bL + g / 2) : L / 2}
              y={vert ? W / 2 : wall.side === "rear" ? oz - g / 2 + 20 : oz + wall.bW + g / 2 + 20}
              className="dimtext tiny" textAnchor="middle">
              {fmt(g)}
            </text>
          </g>
        );
      })()}

      {gutter && (
        <g>
          <line
            x1={gutter.side === "left" ? -70 : gutter.side === "right" ? L + 70 : 0}
            y1={gutter.side === "front" ? W + 70 : gutter.side === "rear" ? -70 : 0}
            x2={gutter.side === "left" ? -70 : gutter.side === "right" ? L + 70 : L}
            y2={gutter.side === "front" ? W + 70 : gutter.side === "rear" ? -70 : W}
            className="gutterline"
          />
          <text
            x={gutter.side === "left" ? -130 : gutter.side === "right" ? L + 130 : L / 2}
            y={gutter.side === "front" ? W + 150 : gutter.side === "rear" ? -100 : W / 2}
            className="dimtext small" textAnchor="middle">
            gutter {fmt(gutter.run)}
          </text>
        </g>
      )}

      <g className="dim">
        <line x1={0} y1={-190} x2={L} y2={-190} />
        <line x1={0} y1={-190 - tick / 2} x2={0} y2={-190 + tick / 2} />
        <line x1={L} y1={-190 - tick / 2} x2={L} y2={-190 + tick / 2} />
        <text x={L / 2} y={-220} className="dimtext" textAnchor="middle">{fmt(L)}</text>
      </g>
      <g className="dim">
        <line x1={-190} y1={0} x2={-190} y2={W} />
        <line x1={-190 - tick / 2} y1={0} x2={-190 + tick / 2} y2={0} />
        <line x1={-190 - tick / 2} y1={W} x2={-190 + tick / 2} y2={W} />
        <text x={-220} y={W / 2} className="dimtext" textAnchor="middle" transform={`rotate(-90 -220 ${W / 2})`}>
          {fmt(W)}
        </text>
      </g>
      <g className="dim">
        {frame.centres.map((c, i) => (
          <g key={i}>
            <line x1={c} y1={W + 80} x2={c} y2={W + 140} className={anchors.has(r(c)) ? "anchor" : ""} />
            <text x={c} y={W + 214} className="dimtext tiny" textAnchor="middle">{fmt(c)}</text>
          </g>
        ))}
        <text x={L / 2} y={W + 296} className="dimtext small" textAnchor="middle">
          joist centres · widest bay {fmt(frame.maxBay)} of {fmt(spacing)} max
        </text>
      </g>
    </svg>
  );
}

function SheetView({ plan, support, L, W, gap, caption, kind = "osb", outline }) {
  const pad = 250;
  let full = 0;
  const ob = outline || { x: 0, z: 0, dx: L, dz: W };
  return (
    <svg viewBox={`${-pad} ${-pad} ${L + pad * 2} ${W + pad * 2}`} className="draw" preserveAspectRatio="xMidYMid meet">
      <rect x={ob.x} y={ob.z} width={ob.dx} height={ob.dz} className="deckoutline" />
      {plan.pieces.map((pc, i) => (
        <g key={i}>
          <rect x={pc.x} y={pc.z} width={pc.dx} height={pc.dz}
            className={`${kind === "pir" ? "pir" : "sheet"}${pc.full ? "" : " iscut"}`} />
          <text x={pc.x + pc.dx / 2} y={pc.z + pc.dz / 2 - 40} className="sheetlabel"
            textAnchor="middle" dominantBaseline="middle">
            {pc.full ? `${++full}` : "cut"}
          </text>
          <text x={pc.x + pc.dx / 2} y={pc.z + pc.dz / 2 + 90} className="sheetdim"
            textAnchor="middle" dominantBaseline="middle">
            {fmt(pc.dx)} × {fmt(pc.dz)}
          </text>
        </g>
      ))}
      {support &&
        support.joints.map((j, i) =>
          j.axis === "x" ? (
            <line key={i} x1={j.pos} y1={-40} x2={j.pos} y2={W + 40} className={j.ok ? "joint ok" : "joint bad"} />
          ) : (
            <line key={i} x1={-40} y1={j.pos} x2={L + 40} y2={j.pos} className={j.ok ? "joint ok" : "joint bad"} />
          )
        )}
      {caption && (
        <text x={L / 2} y={-130} className="dimtext small" textAnchor="middle">{caption}</text>
      )}
    </svg>
  );
}

/* One board, drawn with everything cut out of it in its place and the
   offcuts shaded. This is what settles an argument: the pieces either tile
   the board or they visibly do not, where a column of figures can only be
   taken on trust. The 3000 × 1891 roof tray on 2400 × 1200 boards is the
   case it was built for — two boards give 5.760 m² against 5.673 m² of tray,
   so the 600 × 145 left over is exactly the 0.087 m² the arithmetic demands
   and you can see where it sits. */
function BoardView({ board, cuts, left, label }) {
  const pad = 140;
  return (
    <svg viewBox={`${-pad} ${-pad} ${board.l + pad * 2} ${board.w + pad * 2}`}
      className="draw" preserveAspectRatio="xMidYMid meet">
      <rect x={0} y={0} width={board.l} height={board.w} className="deckoutline" />
      {left.map((x, i) => (
        <rect key={`o${i}`} x={x.x} y={x.y} width={x.l} height={x.w} className="offcutrect" />
      ))}
      {cuts.map((c, i) => (
        <g key={i}>
          <rect x={c.at.x} y={c.at.y} width={c.dx} height={c.dz} className="pir iscut" />
          <text x={c.at.x + c.dx / 2} y={c.at.y + c.dz / 2 - 45} className="sheetlabel"
            textAnchor="middle" dominantBaseline="middle">{i + 1}</text>
          <text x={c.at.x + c.dx / 2} y={c.at.y + c.dz / 2 + 60} className="sheetdim"
            textAnchor="middle" dominantBaseline="middle">
            {fmt(c.dx)} × {fmt(c.dz)}
          </text>
        </g>
      ))}
      {left.map((x, i) => (
        <text key={`t${i}`} x={x.x + x.l / 2} y={x.y + x.w / 2} className="sheetdim"
          textAnchor="middle" dominantBaseline="middle">
          {fmt(x.l)} × {fmt(x.w)} left
        </text>
      ))}
      <text x={board.l / 2} y={-55} className="dimtext small" textAnchor="middle">{label}</text>
    </svg>
  );
}

/* --------------------------- number input --------------------------- */

/* A clamped controlled input turns "1670" into "1200" on the first
   keystroke, so out-of-range drafts wait for Enter or blur. */
function NumberField({ value, onCommit, min, max, step = 10, ariaLabel }) {
  const [draft, setDraft] = useState(String(value));
  const [editing, setEditing] = useState(false);
  const timer = useRef(0);

  useEffect(() => {
    if (!editing) setDraft(String(value));
  }, [value, editing]);
  useEffect(() => () => clearTimeout(timer.current), []);

  /* Precision comes from the step: millimetres stay whole, a g-value or a
     conductivity keeps its decimals. */
  const snap = (n) => quantise(n, { min, max, step });

  const commit = (raw) => {
    clearTimeout(timer.current);
    const n = Number(raw);
    setEditing(false);
    if (raw.trim() === "" || Number.isNaN(n)) return void setDraft(String(value));
    const clamped = snap(n);
    setDraft(String(clamped));
    if (clamped !== value) onCommit(clamped);
  };

  const onChange = (e) => {
    const raw = e.target.value;
    setDraft(raw);
    setEditing(true);
    clearTimeout(timer.current);
    const n = Number(raw);
    if (raw !== "" && !Number.isNaN(n) && n >= min && n <= max)
      timer.current = setTimeout(() => {
        setEditing(false);
        /* Snapped, so the debounced path can never write out of range —
           it used to commit an unclamped round(). */
        const next = snap(n);
        if (next !== value) onCommit(next);
      }, 500);
  };

  const out = draft !== "" && (Number(draft) < min || Number(draft) > max);

  return (
    <input type="number" inputMode={stepPlaces(step) > 0 ? "decimal" : "numeric"}
      value={draft} min={min} max={max} step={step}
      aria-label={ariaLabel} className={out ? "pending" : undefined}
      onChange={onChange} onBlur={(e) => commit(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") { commit(e.currentTarget.value); e.currentTarget.blur(); }
        else if (e.key === "Escape") { clearTimeout(timer.current); setDraft(String(value)); setEditing(false); }
      }} />
  );
}

const Chips = ({ opts, value, set }) => (
  <div className="chips">
    {opts.map((o) => (
      <button key={String(o.value)} className={o.value === value ? "chip on" : "chip"}
        onClick={() => set(o.value)} aria-pressed={o.value === value}>
        {o.label}
      </button>
    ))}
  </div>
);

const MultiChips = ({ opts, values, toggle }) => (
  <div className="toggles">
    {opts.map((o) => {
      const on = values.includes(o.value);
      return (
        <button key={o.value} className={on ? "toggle on" : "toggle"} aria-pressed={on}
          onClick={() => toggle(o.value)}>
          {o.label}
        </button>
      );
    })}
  </div>
);

/* Fixings on a drawing. Deliberately quiet: a screw is a hairline tick and a
   hanger a small open square, both under the dimensions and both non-scaling
   so they stay the same size on screen at any zoom. They are there to be
   read when you look for them, not to compete with the framing. */
const FixingMarks = ({ panel, show = true, mapY = null }) => {
  if (!show || !panel || !panel.points.length) return null;
  const kinds = { framing: [], noggin: [], hanger: [], sheet: [] };
  panel.points.forEach((pt) => kinds[pt.kind].push(pt));
  /* A wall's b runs up from the floor; SVG y runs down from the top of the
     sheathed face. A deck needs no such flip, so the default is identity. */
  const Y = mapY || ((b) => b);
  const ticks = (list, cls, len) =>
    list.map((pt, i) => (
      <line key={i} className={cls}
        x1={pt.a - len} y1={Y(pt.b) - len} x2={pt.a + len} y2={Y(pt.b) + len} />
    ));
  return (
    <g className="fixings" aria-hidden="true">
      {kinds.sheet.map((pt, i) => (
        <circle key={i} className="fix sheet" cx={pt.a} cy={Y(pt.b)} r={9} />
      ))}
      {ticks(kinds.framing, "fix framing", 16)}
      {ticks(kinds.noggin, "fix noggin", 16)}
      {kinds.hanger.map((pt, i) => (
        <rect key={i} className="fix hanger" x={pt.a - 22} y={Y(pt.b) - 22}
          width={44} height={44} />
      ))}
    </g>
  );
};

/* Units that take an "s" in the order summary. "each", "m" and "m²" do not —
   and "each" matters more now that every fixing line uses it. */
const PLURAL_UNITS = new Set(["sheet", "board", "length", "unit"]);

const Field = ({ label, hint, children }) => (
  <div className="field">
    <label>{label}</label>
    {children}
    {hint && <p className="hint">{hint}</p>}
  </div>
);

function ElevationView({ wall, walls, label, tint, headerHex, fixings, battens }) {
  const { frame, plan, sheath } = wall;
  const H = walls.height;
  const skirt = walls.skirt;
  const lift = wall.lift || 0;
  const sheathH = wall.sheathH != null ? wall.sheathH : walls.sheathH;
  const pad = 300;
  const tick = 40;
  const shift = (wall.shift || 0) + (wall.start || 0);
  const anchors = new Set(frame.anchors.map((a) => r(a)));
  const op = frame.opening;
  const fl = wall.fall;
  const tapered = !!(wall.tapered && fl && fl.on);
  const shiftOnly = wall.shift || 0;

  /* SVG y runs from 0 at the top of the wall down to sheathH at the
     underside of the floor frame. World height h maps to H - h. */
  /* SVG y = 0 at the top of the sheathed face, which the fall can push above
     the wall plate. Everything is measured down from there. */
  const up = (y, dy) => H + lift - y - dy;

  return (
    <svg viewBox={`${-pad - shift} ${-pad} ${sheath + pad * 2} ${sheathH + pad * 2}`}
      className="draw" preserveAspectRatio="xMidYMid meet">
      {skirt > 0 && (
        <rect x={-shift} y={H + lift} width={sheath} height={skirt} className="skirtzone" />
      )}
      {lift > 0 && !wall.furringInHeader && (() => {
        const fl = wall.fall;
        const tapered = wall.tapered && fl && fl.on;
        const yAt = (p2) => up(H + (tapered ? fl.heightAt(p2 - 0) : lift), 0);
        const x0 = 0;
        const x1 = frame.len;
        return (
          <polygon className="furring"
            points={tapered
              ? `${x0},${up(H, 0)} ${x1},${up(H, 0)} ${x1},${yAt(x1)} ${x0},${yAt(x0)}`
              : `${x0},${up(H, 0)} ${x1},${up(H, 0)} ${x1},${up(H + lift, 0)} ${x0},${up(H + lift, 0)}`} />
        );
      })()}
      {/* Battens go on the outside of the sheathing, so they are drawn over
          it. Their coordinates run along the face from the sheathing's left
          edge and up from its bottom, while the SVG runs down from the top
          of the face — the same flip the fixings and the noggin rows need,
          in both directions at once. */}
      {battens && battens.runs.map((x, i) => (
        <rect key={`bt${i}`} x={x.a - shift} y={sheathH - x.b - x.db}
          width={x.da} height={x.db}
          className={x.kind === "horizontal" ? "batten rail" : "batten"} />
      ))}
      <defs>
        <pattern id="hatch" width="18" height="18" patternUnits="userSpaceOnUse"
          patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="18" stroke="#9E3427" strokeWidth="4" opacity="0.5" />
        </pattern>
      </defs>
      {(wall.cutPieces || plan.pieces).map((pc, i) => {
        const cls = pc.full ? "sheet ghosted" : "sheet iscut ghosted";
        const isTop = tapered && pc.z < 1;
        if (!isTop)
          return (
            <rect key={`s${i}`} x={pc.x - shift} y={pc.z} width={pc.dx} height={pc.dz}
              className={cls} />
          );
        /* Top row on a tapered wall: the sheet arrives square and the top is
           cut to the fall, so draw what's left and hatch what comes off. */
        const xa = pc.x - shift;
        const xb = xa + pc.dx;
        const ya = lift - fl.heightAt(pc.x - shiftOnly);
        const yb = lift - fl.heightAt(pc.x - shiftOnly + pc.dx);
        const base = pc.z + pc.dz;
        return (
          <g key={`s${i}`}>
            <polygon className="waste"
              points={`${xa},${pc.z} ${xb},${pc.z} ${xb},${yb} ${xa},${ya}`} />
            <polygon className={cls}
              points={`${xa},${ya} ${xb},${yb} ${xb},${base} ${xa},${base}`} />
          </g>
        );
      })}
      <rect x={-shift} y={0} width={sheath} height={sheathH} className="sheathband" />
      {frame.members.map((m, i) => {
        const style = m.isHeader
          ? (headerHex ? { fill: headerHex, stroke: "#2A2A2A" } : undefined)
          : tint ? { fill: tint, stroke: "#3B3B3B" } : undefined;
        const cls = m.isHeader ? "mem header" : m.role === "Noggin" ? "mem noggin" : "mem";
        if (m.shearY) {
          /* Tilted header: constant depth, so top and underside stay parallel. */
          const rise = m.shearY * m.dx;
          return (
            <polygon key={i} className={cls} style={style}
              points={`${m.x},${up(m.y, 0)} ${m.x + m.dx},${up(m.y + rise, 0)} `
                + `${m.x + m.dx},${up(m.y + m.dy + rise, 0)} ${m.x},${up(m.y + m.dy, 0)}`} />
          );
        }
        if (m.slopeTopY) {
          const yb = up(m.y, 0);
          return (
            <polygon key={i} className={cls} style={style}
              points={`${m.x},${yb} ${m.x + m.dx},${yb} `
                + `${m.x + m.dx},${up(m.slopeTopY[1], 0)} ${m.x},${up(m.slopeTopY[0], 0)}`} />
          );
        }
        return (
          <rect key={i} x={m.x} y={up(m.y, m.dy)} width={m.dx} height={m.dy}
            className={cls} style={style} />
        );
      })}
      <FixingMarks panel={fixings} mapY={(b) => up(b, 0)} />
      {/* Ply seams, drawn as lines across the header face. */}
      {frame.members.filter((m) => m.plies > 1).map((m, i) =>
        Array.from({ length: m.plies - 1 }, (_, k) => (
          <line key={`${i}-${k}`} x1={m.x} x2={m.x + m.dx}
            y1={up(m.y, m.dy) + ((k + 1) * m.dy) / m.plies}
            y2={up(m.y, m.dy) + ((k + 1) * m.dy) / m.plies}
            className="plyseam" />
        ))
      )}
      {op && (
        <>
          <rect x={op.x0} y={up(op.sillY, op.roH)} width={op.roW} height={op.roH}
            className="rough" />
          {(() => {
            const g = wall.gapMm;
            const fwv = Math.min(wall.frameWidth || 50, (op.roW - 2 * g) / 2 - 1, (op.roH - 2 * g) / 2 - 1);
            const fx = op.x0 + g;
            const fy = up(op.sillY + g, op.roH - 2 * g);
            return (
              <>
                <rect x={fx} y={fy} width={op.roW - 2 * g} height={op.roH - 2 * g}
                  className="winframe" />
                <rect x={fx + fwv} y={fy + fwv} width={op.roW - 2 * g - 2 * fwv}
                  height={op.roH - 2 * g - 2 * fwv} className="glazing" />
              </>
            );
          })()}
          <g className="dim">
            <line x1={op.x0 + wall.gapMm} y1={up(op.sillY, 0) + 90}
              x2={op.x1 - wall.gapMm} y2={up(op.sillY, 0) + 90} />
            <text x={(op.x0 + op.x1) / 2} y={up(op.sillY, 0) + 175} className="dimtext small"
              textAnchor="middle">
              {fmt(op.roW - 2 * wall.gapMm)} frame · {fmt(op.roW)} rough
            </text>
            <line x1={op.x1 + 120} y1={up(0, 0)} x2={op.x1 + 120} y2={up(op.sillY + wall.gapMm, 0)} />
            <text x={op.x1 + 210} y={up(op.sillY / 2, 0)} className="dimtext tiny" textAnchor="middle"
              transform={`rotate(-90 ${op.x1 + 210} ${up(op.sillY / 2, 0)})`}>
              {fmt(op.sillY + wall.gapMm)} sill
            </text>
          </g>
        </>
      )}
      {wall.joints.map((j, i) => (
        <line key={`j${i}`} x1={j.pos - shift} y1={-30} x2={j.pos - shift} y2={sheathH + 30}
          className={j.ok ? "joint ok" : "joint bad"} />
      ))}

      <g className="dim">
        <line x1={0} y1={-160} x2={frame.len} y2={-160} />
        <line x1={0} y1={-160 - tick / 2} x2={0} y2={-160 + tick / 2} />
        <line x1={frame.len} y1={-160 - tick / 2} x2={frame.len} y2={-160 + tick / 2} />
        <text x={frame.len / 2} y={-190} className="dimtext" textAnchor="middle">{fmt(frame.len)}</text>
        <text x={frame.len / 2} y={-280} className="dimtext small" textAnchor="middle">
          {label} frame · sheathing {fmt(sheath)} × {fmt(sheathH)}
          {lift > 0 ? ` · furring ${wall.tapered ? "tapered" : "packer"} ${fmt(lift)}` : ""}
        </text>
      </g>

      <g className="dim">
        <line x1={-160 - shift} y1={0} x2={-160 - shift} y2={H} />
        <line x1={-160 - shift - tick / 2} y1={0} x2={-160 - shift + tick / 2} y2={0} />
        <line x1={-160 - shift - tick / 2} y1={H} x2={-160 - shift + tick / 2} y2={H} />
        <text x={-190 - shift} y={H / 2} className="dimtext" textAnchor="middle"
          transform={`rotate(-90 ${-190 - shift} ${H / 2})`}>{fmt(H)}</text>
      </g>

      {skirt > 0 && (
        <g className="dim">
          <line x1={sheath - shift + 160} y1={H} x2={sheath - shift + 160} y2={sheathH} />
          <line x1={sheath - shift + 160 - tick / 2} y1={H} x2={sheath - shift + 160 + tick / 2} y2={H} />
          <line x1={sheath - shift + 160 - tick / 2} y1={sheathH} x2={sheath - shift + 160 + tick / 2} y2={sheathH} />
          <text x={sheath - shift + 250} y={H + skirt / 2} className="dimtext tiny" textAnchor="middle"
            transform={`rotate(-90 ${sheath - shift + 250} ${H + skirt / 2})`}>
            {fmt(skirt)} over floor
          </text>
        </g>
      )}

      <g className="dim">
        {frame.centres.map((c, i) => (
          <line key={i} x1={c} y1={sheathH + 60} x2={c} y2={sheathH + 120}
            className={anchors.has(r(c)) ? "anchor" : ""} />
        ))}
        <text x={frame.len / 2} y={sheathH + 210} className="dimtext small" textAnchor="middle">
          stud centres · widest bay {fmt(frame.maxBay)}
        </text>
      </g>
    </svg>
  );
}

/* ------------------------------- app ------------------------------- */

/* Anthracite, as window frames actually come — not a palette choice. */
const FRAME_COLOUR = 0x383e42;

/* Colours are derived from the chosen palette, so the 3D views, the legend
   and the elevation tint can never disagree about what a wall looks like. */
function makeColours(pal, byWall) {
  const sideInt = Object.fromEntries(
    Object.entries(pal.sides).map(([k, v]) => [k, hexInt(v)])
  );
  const sideLight = Object.fromEntries(
    Object.entries(pal.sides).map(([k, v]) => [k, lighten(v, 0.42)])
  );
  return {
    timber: hexInt(pal.timber), noggin: hexInt(pal.noggin), osb: hexInt(pal.osb),
    osbCut: hexInt(pal.osbCut), upstand: hexInt(pal.upstand), pir: hexInt(pal.pir),
    batten: hexInt(pal.batten), tape: hexInt(pal.tape), wrap: hexInt(pal.wrap),
    cladding: hexInt(pal.cladding),
    glass: hexInt(pal.glass), gutter: hexInt(pal.gutter), wall: hexInt(pal.neighbour),
    header: hexInt(pal.header),
    wallblock: hexInt(pal.wallblock), floorblock: hexInt(pal.floorblock),
    sides: sideInt, sideLight, byWall, hex: pal.sides,
  };
}

export default function PodStudio() {
  const [nav, setNav] = useState("start");

  /* ---- appearance ---- */
  const [paletteId, setPaletteId] = useState(DEFAULT_DESIGN.paletteId);
  const [colourBy, setColourBy] = useState(DEFAULT_DESIGN.colourBy);
  const [explode, setExplode] = useState(DEFAULT_DESIGN.explode);
  const [customSections, setCustomSections] = useState(DEFAULT_DESIGN.customSections);
  const [prices, setPrices] = useState(DEFAULT_DESIGN.prices);
  const [currency, setCurrency] = useState(DEFAULT_DESIGN.currency);
  const [furrOn, setFurrOn] = useState(DEFAULT_DESIGN.furrOn);
  const [furrLow, setFurrLow] = useState(DEFAULT_DESIGN.furrLow);
  const [furrHigh, setFurrHigh] = useState(DEFAULT_DESIGN.furrHigh);
  const [furrId, setFurrId] = useState(DEFAULT_DESIGN.furrId);
  const [newT, setNewT] = useState(44);
  const [newD, setNewD] = useState(94);
  const [newNote, setNewNote] = useState("");

  /* ---- global stock, set once in Materials ---- */
  const [sheetFor, setSheetFor] = useState(DEFAULT_DESIGN.sheetFor);
  const [enabled, setEnabled] = useState(DEFAULT_DESIGN.enabled);
  const [pirBoardId, setPirBoardId] = useState(DEFAULT_DESIGN.pirBoardId);
  const [stock, setStock] = useState(DEFAULT_DESIGN.stock);
  const [gap, setGap] = useState(DEFAULT_DESIGN.gap);
  const [stagger, setStagger] = useState(DEFAULT_DESIGN.stagger);
  const [goal, setGoal] = useState(DEFAULT_DESIGN.goal);

  /* ---- fixings, counted off the framing rather than guessed ---- */
  /* ---- energy ---- */
  const [locationId, setLocationId] = useState(DEFAULT_DESIGN.locationId);
  const [siteWinter, setSiteWinter] = useState(DEFAULT_DESIGN.siteWinter);
  const [siteSummer, setSiteSummer] = useState(DEFAULT_DESIGN.siteSummer);
  const [frontFaces, setFrontFaces] = useState(DEFAULT_DESIGN.frontFaces);
  const [indoorWinter, setIndoorWinter] = useState(DEFAULT_DESIGN.indoorWinter);
  const [indoorSummer, setIndoorSummer] = useState(DEFAULT_DESIGN.indoorSummer);
  const [airChanges, setAirChanges] = useState(DEFAULT_DESIGN.airChanges);
  const [internalGain, setInternalGain] = useState(DEFAULT_DESIGN.internalGain);
  const [shadingFactor, setShadingFactor] = useState(DEFAULT_DESIGN.shadingFactor);
  const [uGlazing, setUGlazing] = useState(DEFAULT_DESIGN.uGlazing);
  const [gGlazing, setGGlazing] = useState(DEFAULT_DESIGN.gGlazing);
  const [uDoorSolid, setUDoorSolid] = useState(DEFAULT_DESIGN.uDoorSolid);
  const [lambdaPir, setLambdaPir] = useState(DEFAULT_DESIGN.lambdaPir);

  const [fixOn, setFixOn] = useState(DEFAULT_DESIGN.fixOn);
  const [fixJointScrews, setFixJointScrews] = useState(DEFAULT_DESIGN.fixJointScrews);
  const [fixNogginScrews, setFixNogginScrews] = useState(DEFAULT_DESIGN.fixNogginScrews);
  const [fixHangers, setFixHangers] = useState(DEFAULT_DESIGN.fixHangers);
  const [fixSheetPitch, setFixSheetPitch] = useState(DEFAULT_DESIGN.fixSheetPitch);

  /* ---- assembly / print ---- */
  const [asmMode, setAsmMode] = useState(DEFAULT_DESIGN.asmMode);
  const [asmScale, setAsmScale] = useState(DEFAULT_DESIGN.asmScale);
  const [asmInclude, setAsmInclude] = useState(DEFAULT_DESIGN.asmInclude);
  const [asmGhost, setAsmGhost] = useState(DEFAULT_DESIGN.asmGhost);
  const [asmMinFeature, setAsmMinFeature] = useState(DEFAULT_DESIGN.asmMinFeature);

  /* ---- heights ---- */
  const [groundClear, setGroundClear] = useState(DEFAULT_DESIGN.groundClear);
  const [targetHeight, setTargetHeight] = useState(DEFAULT_DESIGN.targetHeight);
  const [boundaryH, setBoundaryH] = useState(DEFAULT_DESIGN.boundaryH);
  const [show, setShow] = useState(DEFAULT_DESIGN.show);
  const [renderMode, setRenderMode] = useState(DEFAULT_DESIGN.renderMode);
  const shown = (id) => show.includes(id);
  const toggleLayer = (id) =>
    setShow((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  const [views, setViews] = useState(DEFAULT_DESIGN.views);

  /* ---- wall ---- */
  const [wallSide, setWallSide] = useState(DEFAULT_DESIGN.wallSide);
  const [wallGap, setWallGap] = useState(DEFAULT_DESIGN.wallGap);
  const [wallGapFrom, setWallGapFrom] = useState(DEFAULT_DESIGN.wallGapFrom);

  /* ---- floor ---- */
  const [fL, setFL] = useState(DEFAULT_DESIGN.fL);
  const [fW, setFW] = useState(DEFAULT_DESIGN.fW);
  const [fSectionId, setFSectionId] = useState(DEFAULT_DESIGN.fSectionId);
  const [fSpacing, setFSpacing] = useState(DEFAULT_DESIGN.fSpacing);
  const [fDeckT, setFDeckT] = useState(DEFAULT_DESIGN.fDeckT);
  const [fAlign, setFAlign] = useState(DEFAULT_DESIGN.fAlign);
  const [fOrient, setFOrient] = useState(DEFAULT_DESIGN.fOrient);
  const [fJoist, setFJoist] = useState(DEFAULT_DESIGN.fJoist);
  const [fDouble, setFDouble] = useState(DEFAULT_DESIGN.fDouble);
  const [fNoggins, setFNoggins] = useState(DEFAULT_DESIGN.fNoggins);
  const [fPirT, setFPirT] = useState(DEFAULT_DESIGN.fPirT);
  const [wPirT, setWPirT] = useState(DEFAULT_DESIGN.wPirT);
  const [fBattenOn, setFBattenOn] = useState(DEFAULT_DESIGN.fBattenOn);
  const [fBattenPct, setFBattenPct] = useState(DEFAULT_DESIGN.fBattenPct);
  const [fBattenOnEdge, setFBattenOnEdge] = useState(DEFAULT_DESIGN.fBattenOnEdge);
  const [tapeOn, setTapeOn] = useState(DEFAULT_DESIGN.tapeOn);
  const [tapeW, setTapeW] = useState(DEFAULT_DESIGN.tapeW);
  const [tapeRoll, setTapeRoll] = useState(DEFAULT_DESIGN.tapeRoll);
  const [tapeWaste, setTapeWaste] = useState(DEFAULT_DESIGN.tapeWaste);
  const [tapeLayers, setTapeLayers] = useState(DEFAULT_DESIGN.tapeLayers);
  const [wrapOn, setWrapOn] = useState(DEFAULT_DESIGN.wrapOn);
  const [wrapRollW, setWrapRollW] = useState(DEFAULT_DESIGN.wrapRollW);
  const [wrapOverlap, setWrapOverlap] = useState(DEFAULT_DESIGN.wrapOverlap);
  const [wrapDrop, setWrapDrop] = useState(DEFAULT_DESIGN.wrapDrop);
  const [wrapRollLen, setWrapRollLen] = useState(DEFAULT_DESIGN.wrapRollLen);
  const [wrapTapeW, setWrapTapeW] = useState(DEFAULT_DESIGN.wrapTapeW);
  const [wrapTapeRoll, setWrapTapeRoll] = useState(DEFAULT_DESIGN.wrapTapeRoll);
  const [rWrapOn, setRWrapOn] = useState(DEFAULT_DESIGN.rWrapOn);
  const [rWrapOrient, setRWrapOrient] = useState(DEFAULT_DESIGN.rWrapOrient);
  const [pirPiece, setPirPiece] = useState(DEFAULT_DESIGN.pirPiece);
  const [pirPieceL, setPirPieceL] = useState(DEFAULT_DESIGN.pirPieceL);
  const [pirPieceW, setPirPieceW] = useState(DEFAULT_DESIGN.pirPieceW);
  const [pirPieceMax, setPirPieceMax] = useState(DEFAULT_DESIGN.pirPieceMax);
  const [pirTol, setPirTol] = useState(DEFAULT_DESIGN.pirTol);
  const [picked, setPicked] = useState(null);
  const [fTab, setFTab] = useState("model");

  /* ---- walls ---- */
  const [wSectionId, setWSectionId] = useState(DEFAULT_DESIGN.wSectionId);
  const [wLayout, setWLayout] = useState(DEFAULT_DESIGN.wLayout);
  const [wLap, setWLap] = useState(DEFAULT_DESIGN.wLap);
  const [battenOn, setBattenOn] = useState(DEFAULT_DESIGN.battenOn);
  const [battenId, setBattenId] = useState(DEFAULT_DESIGN.battenId);
  const [battenOnEdge, setBattenOnEdge] = useState(DEFAULT_DESIGN.battenOnEdge);
  const [battenStock, setBattenStock] = useState(DEFAULT_DESIGN.battenStock);
  const [cladding, setCladding] = useState(DEFAULT_DESIGN.cladding);
  const [cladOn, setCladOn] = useState(DEFAULT_DESIGN.cladOn);
  const [cladFace, setCladFace] = useState(DEFAULT_DESIGN.cladFace);
  const [cladGap, setCladGap] = useState(DEFAULT_DESIGN.cladGap);
  const [cladT, setCladT] = useState(DEFAULT_DESIGN.cladT);
  const [cladStock, setCladStock] = useState(DEFAULT_DESIGN.cladStock);
  const [cladSides, setCladSides] = useState(DEFAULT_DESIGN.cladSides);
  const [battenSpacing, setBattenSpacing] = useState(DEFAULT_DESIGN.battenSpacing);
  const [battenMode, setBattenMode] = useState(DEFAULT_DESIGN.battenMode);
  const [battenHSpacing, setBattenHSpacing] = useState(DEFAULT_DESIGN.battenHSpacing);
  const [battenHMode, setBattenHMode] = useState(DEFAULT_DESIGN.battenHMode);
  const [ceilingHeight, setCeilingHeight] = useState(DEFAULT_DESIGN.ceilingHeight);
  const [wSpacing, setWSpacing] = useState(DEFAULT_DESIGN.wSpacing);
  const [wStudAlign, setWStudAlign] = useState(DEFAULT_DESIGN.wStudAlign);
  const [wNoggins, setWNoggins] = useState(DEFAULT_DESIGN.wNoggins);
  const [wSheathT, setWSheathT] = useState(DEFAULT_DESIGN.wSheathT);
  const [wSheathAlign, setWSheathAlign] = useState(DEFAULT_DESIGN.wSheathAlign);
  const [wSheathOrient, setWSheathOrient] = useState(DEFAULT_DESIGN.wSheathOrient);
  const [wSheathCut, setWSheathCut] = useState(DEFAULT_DESIGN.wSheathCut);
  const [wTab, setWTab] = useState("model");

  /* ---- window ---- */
  const [winOn, setWinOn] = useState(DEFAULT_DESIGN.winOn);
  const [winSide, setWinSide] = useState(DEFAULT_DESIGN.winSide);
  const [winW, setWinW] = useState(DEFAULT_DESIGN.winW);
  const [winH, setWinH] = useState(DEFAULT_DESIGN.winH);
  const [winSill, setWinSill] = useState(DEFAULT_DESIGN.winSill);
  const [winGap, setWinGap] = useState(DEFAULT_DESIGN.winGap);
  const [winCentred, setWinCentred] = useState(DEFAULT_DESIGN.winCentred);
  const [winCentre, setWinCentre] = useState(DEFAULT_DESIGN.winCentre);
  const [headerId, setHeaderId] = useState(DEFAULT_DESIGN.headerId);
  const [winStyle, setWinStyle] = useState(DEFAULT_DESIGN.winStyle);
  const [glazedFix, setGlazedFix] = useState(DEFAULT_DESIGN.glazedFix);
  const [doorOn, setDoorOn] = useState(DEFAULT_DESIGN.doorOn);
  const [doorSide, setDoorSide] = useState(DEFAULT_DESIGN.doorSide);
  const [doorW, setDoorW] = useState(DEFAULT_DESIGN.doorW);
  const [doorH, setDoorH] = useState(DEFAULT_DESIGN.doorH);
  const [doorGap, setDoorGap] = useState(DEFAULT_DESIGN.doorGap);
  const [doorFrom, setDoorFrom] = useState(DEFAULT_DESIGN.doorFrom);
  const [doorOffset, setDoorOffset] = useState(DEFAULT_DESIGN.doorOffset);
  const [doorSolid, setDoorSolid] = useState(DEFAULT_DESIGN.doorSolid);
  const [doorFullHeader, setDoorFullHeader] = useState(DEFAULT_DESIGN.doorFullHeader);
  const [doorHeaderOnEdge, setDoorHeaderOnEdge] = useState(DEFAULT_DESIGN.doorHeaderOnEdge);
  const [wMinBay, setWMinBay] = useState(DEFAULT_DESIGN.wMinBay);
  const [spacingMode, setSpacingMode] = useState(DEFAULT_DESIGN.spacingMode);
  const [headerOnEdge, setHeaderOnEdge] = useState(DEFAULT_DESIGN.headerOnEdge);
  const [frameDepth, setFrameDepth] = useState(DEFAULT_DESIGN.frameDepth);
  const [frameWidth, setFrameWidth] = useState(DEFAULT_DESIGN.frameWidth);
  const [doorFrameWidth, setDoorFrameWidth] = useState(DEFAULT_DESIGN.doorFrameWidth);

  /* ---- roof ---- */
  const [ohLeft, setOhLeft] = useState(DEFAULT_DESIGN.ohLeft);
  const [ohRight, setOhRight] = useState(DEFAULT_DESIGN.ohRight);
  const [ohFront, setOhFront] = useState(DEFAULT_DESIGN.ohFront);
  const [ohRear, setOhRear] = useState(DEFAULT_DESIGN.ohRear);
  const [rSectionId, setRSectionId] = useState(DEFAULT_DESIGN.rSectionId);
  const [rSpacing, setRSpacing] = useState(DEFAULT_DESIGN.rSpacing);
  const [rJoist, setRJoist] = useState(DEFAULT_DESIGN.rJoist);
  const [rNoggins, setRNoggins] = useState(DEFAULT_DESIGN.rNoggins);
  const [rDeckT, setRDeckT] = useState(DEFAULT_DESIGN.rDeckT);
  const [rAlign, setRAlign] = useState(DEFAULT_DESIGN.rAlign);
  const [rOrient, setROrient] = useState(DEFAULT_DESIGN.rOrient);
  const [upstandId, setUpstandId] = useState(DEFAULT_DESIGN.upstandId);
  const [upstandOnEdge, setUpstandOnEdge] = useState(DEFAULT_DESIGN.upstandOnEdge);
  const [pirT, setPirT] = useState(DEFAULT_DESIGN.pirT);
  const [rPirMode, setRPirMode] = useState(DEFAULT_DESIGN.rPirMode);

  /* The roof's insulation is either a thickness you set or two layers of
     whatever the walls are using. The second reaches 100 mm off the wall's
     50 mm board, so the floor, the walls and the roof all cut from one pile
     instead of the roof needing a thickness of its own that nothing else can
     share. It needs a wall thickness to double, so with cavity insulation
     off it falls back to the set figure and the panel says why. */
  const roofPirLayers = rPirMode === "wallLayers" && wPirT > 0 ? 2 : 1;
  const roofPirT = roofPirLayers > 1 ? 2 * wPirT : pirT;
  const roofPirUnset = rPirMode === "wallLayers" && wPirT <= 0;
  const [topT, setTopT] = useState(DEFAULT_DESIGN.topT);
  const [topAlign, setTopAlign] = useState(DEFAULT_DESIGN.topAlign);
  const [topOrient, setTopOrient] = useState(DEFAULT_DESIGN.topOrient);
  const [epdmMargin, setEpdmMargin] = useState(DEFAULT_DESIGN.epdmMargin);
  const [gutterSide, setGutterSide] = useState(DEFAULT_DESIGN.gutterSide);
  const [rTab, setRTab] = useState("model");

  /* Anything assigned but no longer stocked falls back to the first
     enabled section, so the two can never drift apart. */
  const palette = PALETTES.find((x) => x.id === paletteId) || PALETTES[0];
  const C = useMemo(() => makeColours(palette, colourBy === "wall"), [palette, colourBy]);

  const allSections = useMemo(
    () => [...SECTIONS, ...customSections.filter((s) => s && s.id)],
    [customSections]
  );
  const sectionFor = (id) => {
    const pick = enabled.includes(id) ? id : enabled[0];
    return allSections.find((s) => s.id === pick) || allSections[0];
  };

  const addSection = () => {
    const made = makeSection(newT, newD, newNote);
    if (!made) return;
    if (allSections.some((s) => s.id === made.id)) {
      setEnabled((cur) => (cur.includes(made.id) ? cur : [...cur, made.id]));
      return;
    }
    setCustomSections((cur) => [...cur, made]);
    setEnabled((cur) => [...cur, made.id]);
    setNewNote("");
  };

  const removeSection = (id) => {
    setCustomSections((cur) => cur.filter((s) => s.id !== id));
    setEnabled((cur) => (cur.length > 1 ? cur.filter((x) => x !== id) : cur));
  };
  const boardFor = (thickness) => SHEETS.find((s) => s.id === (sheetFor[thickness] || "2397"));

  const floorSection = sectionFor(fSectionId);
  const roofSection = sectionFor(rSectionId);
  const upstandSection = sectionFor(upstandId);
  const furrSection = sectionFor(furrId);

  const fall = useMemo(
    () => fallOf({ gutterSide, buildingL: fL, buildingW: fW, furrLow, furrHigh, furrOn }),
    [gutterSide, fL, fW, furrLow, furrHigh, furrOn]
  );
  const wallSection = sectionFor(wSectionId);
  const battenSection = sectionFor(battenId);
  const headerSection = sectionFor(headerId);
  const floorBoard = boardFor(fDeckT);
  const roofBoard = boardFor(rDeckT);
  const topBoard = boardFor(topT);
  const sheathBoard = boardFor(wSheathT);
  const pirBoard = PIR_BOARDS.find((s) => s.id === pirBoardId);
  /* How high the underside of the roof is where it passes over the boundary,
     so the clearance can tell a collision from an oversail. Taken from the
     floor stack and the ceiling rather than from `roof`, which needs this to
     be settled before it runs. Where the fall makes a side vary along its
     run, the **lowest** point is the one a tall wall meets first. */
  const roofUnderY = useMemo(() => {
    const base = groundClear + floorSection.d + fDeckT + ceilingHeight;
    if (!fall.on) return base;
    const at = (v) => base + Math.max(0, fall.heightAt(v));
    const alongFall = (fall.axis === "z") === (wallSide === "rear" || wallSide === "front");
    if (wallSide === "rear") return alongFall ? at(0) : Math.min(at(0), at(fL));
    if (wallSide === "front") return alongFall ? at(fW) : Math.min(at(0), at(fL));
    if (wallSide === "left")
      return fall.axis === "x" ? at(0) : Math.min(at(0), at(fW));
    if (wallSide === "right")
      return fall.axis === "x" ? at(fL) : Math.min(at(0), at(fW));
    return base;
  }, [groundClear, floorSection, fDeckT, ceilingHeight, fall, wallSide, fL, fW]);

  /* Memoised so everything it carries is listed in one place. It was a plain
     object with its fields spelled out again in the roof's dependency list,
     and adding two of them there and not here meant the mode flip and the
     wall height reached the label and not the arithmetic. One list is one
     thing to keep right. */
  const wall = useMemo(
    () => ({ side: wallSide, gap: wallGap, from: wallGapFrom,
             height: boundaryH, roofUnderY }),
    [wallSide, wallGap, wallGapFrom, boundaryH, roofUnderY]
  );

  /* ---- floor ---- */
  const floor = useMemo(() => {
    const deck = planSheets({ length: fL, width: fW, board: floorBoard, gap,
      align: fAlign, stagger, goal, orient: fOrient });
    const frame = buildFrame(
      { length: fL, width: fW, t: floorSection.t, d: floorSection.d, spacing: fSpacing,
        joistAlign: fJoist, nogginRows: fNoggins, doubleRim: fDouble, spacingMode },
      deck
    );
    const pir = floorInsulation(frame,
      { length: fL, width: fW, thickness: fPirT, joistDepth: floorSection.d });
    return { deck, frame, pir, support: checkSupport(deck, frame), deckT: fDeckT,
             board: floorBoard };
  }, [fL, fW, floorSection, fSpacing, fJoist, fNoggins, fDouble, floorBoard, gap,
      fAlign, fOrient, stagger, goal, fDeckT, fPirT, spacingMode]);

  /* ---- walls ---- */
  const walls = useMemo(
    () =>
      buildWalls({
        buildingL: fL, buildingW: fW, section: wallSection, spacing: wSpacing,
        studAlign: wStudAlign, nogginRows: wNoggins, ceilingHeight,
        sheathT: wSheathT, sheathBoard, gap, goal, stagger,
        skirt: floorSection.d + fDeckT, minBay: wMinBay, spacingMode,
        layout: wLayout, lap: wLap, fall,
        sheathAlign: wSheathAlign, sheathOrient: wSheathOrient,
        window: winOn
          ? { side: winSide, w: winW, h: winH, sill: winSill, gap: winGap,
              centre: winCentred ? null : winCentre,
              headerH: headerOnEdge ? headerSection.d : headerSection.t,
              headerT: headerOnEdge ? headerSection.t : headerSection.d,
              headerSection, style: winStyle, heightMode: glazedFix }
          : null,
        door: doorOn
          ? { side: doorSide, w: doorW, h: doorH, gap: doorGap,
              from: doorFrom, offset: doorOffset,
              headerH: doorHeaderOnEdge ? headerSection.d : headerSection.t,
              headerT: doorHeaderOnEdge ? headerSection.t : headerSection.d,
              headerSpansWall: doorFullHeader }
          : null,
      }),
    [fL, fW, wallSection, wSpacing, wStudAlign, wNoggins, ceilingHeight, wSheathT,
     sheathBoard, gap, goal, stagger, floorSection, fDeckT, winOn, winSide, winW, winH,
     winSill, winGap, winCentred, winCentre, headerSection, wLayout, wLap, winStyle,
     headerOnEdge, fall, glazedFix, wSheathAlign, wSheathOrient,
     doorOn, doorSide, doorW, doorH, doorGap, doorFrom, doorOffset, doorFullHeader,
     doorHeaderOnEdge, wMinBay, spacingMode]
  );

  /* Insulation bays per wall, keyed by side so podBoxes and the drawings can
     each ask for the one they want. Derived from the walls, so it sits with
     everything else that reads them. */
  /* Cladding battens, off the walls that are already built. Off when not
     asked for, so an older design and a fresh one both come out unchanged. */
  const battens = useMemo(
    () => (battenOn
      ? buildBattens(walls, { section: battenSection, onEdge: battenOnEdge,
          cladding, spacing: battenSpacing, mode: battenMode,
          hSpacing: battenHSpacing, hMode: battenHMode })
      : null),
    [walls, battenOn, battenSection, battenOnEdge, cladding, battenSpacing,
     battenMode, battenHSpacing, battenHMode]
  );

  /* The boards on those battens. Off when not asked for, and the direction
     is the same setting the battens read — one choice, both layers. */
  const clad = useMemo(
    () => (cladOn
      ? buildCladding(walls, { face: cladFace, gap: cladGap, thickness: cladT,
          cladding, stock: Math.round(cladStock * 1000), sides: cladSides, fall })
      : null),
    [walls, cladOn, cladFace, cladGap, cladT, cladding, cladStock, cladSides, fall]
  );

  /* The breather membrane, outside the sheathing and under the battens.
     Courses are bands round the whole building, so this takes the walls
     rather than a wall. */
  const wrap = useMemo(
    () => (wrapOn
      ? planWrap(walls, { rollW: wrapRollW, overlap: wrapOverlap, drop: wrapDrop,
          rollLen: wrapRollLen * 1000, tapeW: wrapTapeW,
          tapeRoll: wrapTapeRoll * 1000 })
      : null),
    [walls, wrapOn, wrapRollW, wrapOverlap, wrapDrop, wrapRollLen, wrapTapeW,
     wrapTapeRoll]
  );

  /* Ledgers under the floor PIR. Off when not asked for, and pointless with
     no board to hold up. */
  const joistBat = useMemo(
    () => (fBattenOn && fPirT > 0
      ? joistBattens(floor.pir, { section: battenSection, onEdge: fBattenOnEdge,
          pct: fBattenPct })
      : null),
    [fBattenOn, fPirT, floor, battenSection, fBattenOnEdge, fBattenPct]
  );

  const wallPir = useMemo(() => {
    const out = {};
    walls.walls.forEach((w) => {
      out[w.side] = wallInsulation(w, { thickness: wPirT, studDepth: wallSection.d });
    });
    out.total = walls.walls.reduce((n, w) => n + out[w.side].bays, 0);
    out.area = walls.walls.reduce((n, w) => n + out[w.side].area, 0);
    out.slivers = walls.walls.reduce((n, w) => n + out[w.side].slivers, 0);
    out.deeper = wPirT > wallSection.d;
    out.pieces = walls.walls.flatMap((w) => out[w.side].pieces);
    /* The flattened list is what the allocator is handed, so its indices are
       what a demand tag names. Keep the side alongside or there is no way
       back from `wall:7` to the wall it is in. */
    out.sides = walls.walls.flatMap((w) => out[w.side].pieces.map(() => w.side));
    return out;
  }, [walls, wPirT, wallSection]);

  /* Derived straight from the walls, so it has to sit above everything that
     reads it — the order summary does. */
  const glazedWall = walls.opened && walls.opened.frame.glazed ? walls.opened : null;
  const winSizes = walls.opened ? windowSizes(walls.opened, frameWidth, winGap) : null;
  const doorSizes = walls.doored ? windowSizes(walls.doored, doorFrameWidth, doorGap) : null;
  const fitWallToWindow = (target) => {
    if (!glazedWall) return;
    const need = target + 2 * winGap + 4 * walls.t
      + (glazedWall.len < glazedWall.faceLen ? 2 * walls.wt : 0);
    if (glazedWall.axis === "x") setFL(Math.round(need));
    else setFW(Math.round(need));
  };

  /* ---- roof ---- */
  const roof = useMemo(
    () =>
      buildRoof({
        buildingL: fL, buildingW: fW, ohLeft, ohRight, ohFront, ohRear,
        section: roofSection, spacing: rSpacing, joistAlign: rJoist, nogginRows: rNoggins,
        spacingMode,
        deckT: rDeckT, deckBoard: roofBoard, deckAlign: rAlign, deckOrient: rOrient,
        stagger, gap, goal,
        upstandSection, upstandOnEdge, pirT: roofPirT, pirBoard,
        pirLayers: roofPirLayers,
        topT, topBoard, topAlign, topOrient,
        epdmMargin, gutterSide, wall,
      }),
    [fL, fW, ohLeft, ohRight, ohFront, ohRear, roofSection, rSpacing, rJoist, rNoggins,
     rDeckT, roofBoard, rAlign, rOrient, stagger, gap, goal, upstandSection, upstandOnEdge,
     roofPirT, roofPirLayers, pirBoard, topT, topBoard, topAlign, topOrient, epdmMargin,
     gutterSide, wall, spacingMode]
  );

  /* ---- what did I just click? ----
     The engine tags every solid with the part it belongs to; the section and
     thickness behind that part live up here in the design, so the naming is
     done on this side and the geometry stays free of labels. */
  const describeBox = (b) => {
    if (!b) return null;
    const osb = (t) => `${t} mm OSB3`;
    const byPart = {
      "floor frame": () => floorSection.label,
      "floor insulation": () => `${fPirT} mm PIR`,
      "wall insulation": () => `${wPirT} mm PIR`,
      "floor deck": () => osb(fDeckT),
      "wall frame": () => wallSection.label,
      header: () => headerSection.label,
      sheathing: () => osb(wSheathT),
      "roof frame": () => roofSection.label,
      "roof noggin": () => roofSection.label,
      "roof deck": () => osb(rDeckT),
      upstand: () => upstandSection.label,
      insulation: () => `${roof.pirLayerT} mm PIR${roof.pirLayers > 1 ? `, 1 of ${roof.pirLayers} layers` : ""}`,
      "top deck": () => osb(topT),
      furring: () => furrSection.label,
      batten: () => `${battenSection.label} batten, ${battens ? battens.width : battenSection.d} mm on the face`,
      "counter batten": () => `${battenSection.label} counter batten, over the verticals`,
      "joist batten": () => `${battenSection.label} ledger, ${joistBat ? fmt(joistBat.ledge) : ""} mm of it under the PIR`,
      "furring packer": () => furrSection.label,
      glazing: () => "Glazing unit",
      leaf: () => "Door leaf",
      gutter: () => "Gutter",
      /* One frame routine serves a window and a door and the box does not
         carry which — but a wall takes one opening at most, so the side it
         sits on says which it is. */
      frame: () => {
        const isDoor = doorOn && b.side === doorSide;
        return `${isDoor ? "Door" : "Window"} frame, ${isDoor ? doorFrameWidth : frameWidth} × ${frameDepth} mm`;
      },
      tape: () => `PIR foil tape, ${tapeW} mm wide`,
      wrap: () => `Breather membrane, ${wrap ? fmt(wrap.rollW) : ""} mm roll, ${wrap ? fmt(wrap.overlap) : ""} mm lap`,
      "roof wrap": () => `Breather membrane under the roof PIR, ${fmt(wrapRollW)} mm roll, over the upstand`,
      cladding: () => `Cladding board, ${fmt(cladFace)} × ${fmt(cladT)} mm face, ${fmt(cladGap)} mm shadow gap`,
      boundary: () => "Boundary wall (context, not built)",
      plinth: () => "Floor, as a block",
      walls: () => "Walls, as a block",
      roof: () => "Roof, as a block",
    };
    /* A part with no entry is still named rather than shown raw, so a new
       part added to the engine degrades to a readable label instead of a
       lowercase fragment. */
    const what = byPart[b.part]
      ? byPart[b.part]()
      : b.part
        ? b.part.charAt(0).toUpperCase() + b.part.slice(1)
        : "Unnamed solid";
    /* A stick is quoted by its cut length, a sheet by its face. */
    const dims = [b.dx, b.dy, b.dz].map((n) => r(n));
    const longest = Math.max(...dims);
    const stick = dims.filter((d) => d > 200).length === 1;
    return {
      what,
      part: b.part,
      side: b.side || null,
      size: stick
        ? `${fmt(longest)} mm long`
        : `${fmt(dims[0])} × ${fmt(dims[1])} × ${fmt(dims[2])} mm`,
    };
  };
  const pickInfo = describeBox(picked);
  /* A stale selection would name a solid that is no longer there, so it is
     dropped whenever the model is rebuilt. */
  useEffect(() => setPicked(null), [floor, walls, roof, show, explode, nav]);

  /* ---- energy ----
     Built on the same geometry the drawings use, so the insulated area is
     measured rather than assumed and the bridged fraction comes out of the
     bay list. Advisory: see the note the panel carries. */
  const site = LOCATIONS.find((l) => l.id === locationId) || LOCATIONS[0];
  const outdoorWinter = locationId === "custom" ? siteWinter : site.winter;
  const outdoorSummer = locationId === "custom" ? siteSummer : site.summer;

  const energy = useMemo(() => {
    const opened = walls.opened;
    const sizes = opened ? windowSizes(opened, frameWidth, winGap) : null;
    const openings = [];
    if (winOn && sizes)
      openings.push({ id: "glazing", label: "Glazing", side: winSide,
        area: (sizes.frameW * sizes.frameH) / 1e6, u: uGlazing, g: gGlazing });
    if (doorOn)
      openings.push({ id: "door", label: doorSolid ? "Door, solid" : "Door, glazed",
        side: doorSide, area: (doorW * doorH) / 1e6,
        u: doorSolid ? uDoorSolid : uGlazing, g: doorSolid ? 0 : gGlazing });

    const envelope = buildEnvelope({
      fL, fW, floorPir: floor.pir, floorSection, fDeckT,
      walls, wallPir, wallSection, wSheathT,
      roof, roofPir: roofPirT, rDeckT, topT,
      openings,
      lambda: { pir: lambdaPir },
    });
    /* Internal volume, for the ventilation term: inside the studs, floor to
       ceiling. */
    const inner = Math.max(0, fL - 2 * walls.wt) * Math.max(0, fW - 2 * walls.wt);
    const loads = heatLoads({
      envelope, volume: (inner * ceilingHeight) / 1e9, airChanges,
      indoorWinter, indoorSummer, outdoorWinter, outdoorSummer,
      frontFaces, shading: shadingFactor, internalGain,
      floorArea: inner / 1e6,
    });
    return { envelope, loads, innerArea: inner / 1e6 };
  }, [fL, fW, floor, floorSection, fDeckT, walls, wallPir, wallSection, wSheathT,
      roof, roofPirT, rDeckT, topT, winOn, winSide, doorOn, doorSide, doorW, doorH,
      doorSolid, uGlazing, gGlazing, uDoorSolid, lambdaPir, frameWidth, winGap,
      ceilingHeight, airChanges, indoorWinter, indoorSummer, outdoorWinter,
      outdoorSummer, frontFaces, shadingFactor, internalGain]);

  /* ---- fixings ----
     Counted off the framing and sheet layouts that are already drawn, so the
     tally cannot drift from them. Turning fixings off zeroes every count
     rather than hiding a number that is still being added up. */
  const fixings = useMemo(
    () => planFixings({
      floor, walls, roof,
      jointScrews: fixOn ? fixJointScrews : 0,
      nogginScrews: fixOn ? fixNogginScrews : 0,
      hangers: fixOn && fixHangers,
      pitch: fixOn ? fixSheetPitch : 0,
    }),
    [floor, walls, roof, fixOn, fixJointScrews, fixNogginScrews, fixHangers,
     fixSheetPitch]
  );

  const fixingsFor = (key) => fixings.panels.find((p) => p.key === key) || null;

  /* ---- one allocation pool per material, floor served first ---- */
  const alloc = useMemo(() => {
    const groups = new Map();
    const add = (board, thickness, module, pieces) => {
      const key = `${thickness}mm · ${board.label}`;
      if (!groups.has(key)) groups.set(key, { key, board, thickness, demands: [] });
      groups.get(key).demands.push(
        ...pieces.map((p) => ({ dx: p.dx, dz: p.dz, full: p.full, module }))
      );
    };
    add(floorBoard, fDeckT, "Floor deck", floor.deck.pieces);
    /* Over the opening and cut after, or pieced around it. Sheeting over is
       the easier way to work and hands back the cut-out; piecing around buys
       less board but every piece round an opening is one to cut and fit. */
    const sheetOver = wSheathCut === "over";
    walls.walls.forEach((w) => add(sheathBoard, wSheathT, "Wall sheathing",
      sheetOver ? w.plan.pieces : w.cutPieces));
    add(roofBoard, rDeckT, "Roof deck", roof.deck.pieces);
    add(topBoard, topT, "Roof top deck", roof.top.pieces);

    const sheathKey = `${wSheathT}mm · ${sheathBoard.label}`;
    const osb = new Map();
    groups.forEach((g) => osb.set(g.key, { ...g, ...allocateSheets(g.demands, g.board) }));

    /* What the other way round would have cost, so the choice is a measured
       one rather than a preference. Both figures are the **whole pile's**
       sheets, not the wall sheathing's pieces: the pile is shared with the
       roof's top deck at the same thickness, and a cut-out handed back can
       be taken by either. Comparing a pile total against a per-module piece
       count reported a saving in both directions at once. */
    const sheathAlt = (() => {
      const g = groups.get(sheathKey);
      if (!g) return null;
      const other = sheetOver
        ? walls.walls.flatMap((w) => w.cutPieces
          .map((p) => ({ dx: p.dx, dz: p.dz, full: p.full, module: "Wall sheathing" })))
        : walls.walls.flatMap((w) => w.plan.pieces
          .map((p) => ({ dx: p.dx, dz: p.dz, full: p.full, module: "Wall sheathing" })));
      const rest = g.demands.filter((d) => d.module !== "Wall sheathing");
      return allocateSheets([...rest, ...other], g.board).bought;
    })();

    /* PIR is pooled by thickness, so a floor and a roof on the same board
       share one pile and the roof can be cut from the floor's offcuts. Two
       different thicknesses are two different materials and two piles — which
       is the whole reason matching them is worth doing. Floor first, so the
       bays are cut from whole boards and the roof tray takes what is left. */
    const pirGroups = new Map();
    const addPir = (thickness, module, pieces, piece = false) => {
      if (!thickness || !pieces.length) return;
      const key = `PIR ${thickness}mm · ${pirBoard.label}`;
      if (!pirGroups.has(key)) pirGroups.set(key, { key, thickness, demands: [] });
      pirGroups.get(key).demands.push(
        ...pieces.map((p) => ({ dx: p.dx, dz: p.dz, full: !!p.full, module, piece,
          tray: p.tray }))
      );
    };
    /* Floor and roof insulation lies flat, so a bay or a tray made up from
       several butted offcuts stays where it is put. A wall's does not: a
       patchwork held by friction in a vertical cavity can slump and leave a
       void at the top, which is the one place you would not find it. Hence
       the middle setting, and hence it being the default — but it is a
       judgement, not a law, so the walls can be opted in. */
    const flat = pirPiece !== "none";
    /* Every demand is tagged with where it came from, so a made-up piece can
       be put back together as the parts you will actually lay — and so the
       tape can measure the joints between them. Untagged, a bay cut from
       four offcuts is taped as one bay and three butt joints go uncounted. */
    addPir(fPirT, "Floor insulation",
      floor.pir.pieces.map((p, i) => ({ ...p, tray: `floor:${i}` })), flat);
    addPir(wPirT, "Wall insulation",
      wallPir.pieces.map((p, i) => ({ dx: p.dx, dz: p.dy, tray: `wall:${i}` })),
      pirPiece === "all");
    addPir(roof.pirLayerT, "Roof insulation",
      roof.pirPlans.flatMap((pl, li) =>
        pl.pieces.map((pc, pi) => ({ ...pc, tray: `roof:${li}:${pi}` }))), flat);

    const pir = new Map();
    /* The tolerance is not part of the piecing: a piece a millimetre short
       is a millimetre short whether it stands alone or butts another, so it
       applies to the baseline too and `pirSaved` stays a measure of the
       piecing alone. */
    const base = { tolerance: pirTol };
    const opts = flat
      ? { ...base, minPiece: { l: pirPieceL, w: pirPieceW }, maxParts: pirPieceMax }
      : base;
    /* What the piecing is worth, in boards. Run both ways and subtract:
       a threshold that saves nothing is worth knowing about, and on a
       typical pod 400 mm is exactly that. */
    let pirSaved = 0;
    let plain = 0;
    pirGroups.forEach((g) => {
      const got = allocateSheets(g.demands, pirBoard, opts);
      pir.set(g.key, { ...g, board: pirBoard, ...got });
      if (flat) {
        const whole = allocateSheets(g.demands, pirBoard, base).bought;
        plain += whole;
        pirSaved += whole - got.bought;
      }
    });

    /* Telling somebody a setting is worth nothing is half an answer. Walk a
       ladder of smaller pieces and name the largest that actually buys fewer
       boards, so the figure to try is on the page rather than found by
       poking. Only when the current one is earning nothing, since that is
       the only time it is worth the work. */
    let pirSuggest = null;
    if (flat && pirSaved <= 0) {
      for (const l of [300, 250, 200, 150]) {
        if (l >= pirPieceL) continue;
        let n = 0;
        pirGroups.forEach((g) => {
          n += allocateSheets(g.demands, pirBoard,
            { ...base, minPiece: { l, w: pirPieceW }, maxParts: pirPieceMax }).bought;
        });
        if (plain - n > 0) { pirSuggest = { l, saves: plain - n }; break; }
      }
    }
    /* One pile means every insulated layer is on the same thickness, so
       every one of them can take from the others' offcuts. */
    return { osb, pir, pirSaved, pirSuggest, sheathAlt,
             sheathNow: osb.get(sheathKey) ? osb.get(sheathKey).bought : null,
             pirShared: pirGroups.size === 1 && pirGroups.size > 0 };
  }, [floor, walls, roof, wallPir, fDeckT, wSheathT, rDeckT, topT, floorBoard,
      sheathBoard, roofBoard, topBoard, pirBoard, roofPirT, fPirT, wPirT, wSheathCut,
      pirPiece, pirPieceL, pirPieceW, pirPieceMax, pirTol]);

  /* The cutting order, board by board. Every row says what was cut and what
     it was cut out of, which is the only way the plan can be checked: six
     pieces all reported as "from sheet 1" reads as impossible even when they
     tile it exactly, because the board they descend from is not the rectangle
     they came out of. */
  const cutOrder = (g) => {
    const boards = new Map();
    const get = (key) => {
      if (!boards.has(key)) boards.set(key, { board: key, rows: [], left: [] });
      return boards.get(key);
    };
    g.log.forEach((l) => get(l.outOf ? l.outOf.origin : l.source).rows.push(l));
    g.pool.forEach((x) => get(x.origin).left.push(x));
    return [...boards.values()];
  };

  /* Pieces that were made up from several offcuts, per module: how many, and
     out of how many parts each. The log carries one row per part, so the
     first part of each run is what counts them. */
  const madeUp = (g) => {
    const by = new Map();
    g.log.forEach((l) => {
      if (l.part !== 1) return;
      if (!by.has(l.module)) by.set(l.module, { module: l.module, n: 0, parts: 0 });
      const e = by.get(l.module);
      e.n += 1;
      e.parts = Math.max(e.parts, l.parts);
    });
    return [...by.values()];
  };

  /* Boards bought and offcuts reused, per module, across every PIR pile. */
  const pirFor = (module) => {
    let bought = 0;
    let reused = 0;
    alloc.pir.forEach((g) =>
      g.log.forEach((l) => {
        if (l.module !== module) return;
        l.reused ? (reused += 1) : (bought += 1);
      })
    );
    return { bought, reused };
  };


  const sheetsFor = (module) => {
    let bought = 0;
    let reused = 0;
    alloc.osb.forEach((g) =>
      g.log.forEach((l) => {
        if (l.module !== module) return;
        l.reused ? (reused += 1) : (bought += 1);
      })
    );
    return { bought, reused };
  };

  /* The furring strips, as pieces rather than as a plane. The cut list and
     the timber pile both read this, so what is drawn is what is bought —
     they were drawn, tabled and never ordered before, which is two strips a
     wall missing off the length count. */
  const furr = useMemo(
    () => furringPieces(walls, fall, furrSection),
    [walls, fall, furrSection]
  );
  /* What those boards come to against the plate, as information: whole
     boards never land on an arbitrary wall thickness, and the odd 12 mm is
     a packer on site rather than a piece on the cut list. */
  const furrCover = useMemo(
    () => furringCover(walls.wt, furrSection.t),
    [walls.wt, furrSection.t]
  );

  /* ---- timber packed per section across the whole build ---- */
  const timber = useMemo(() => {
    const bySection = new Map();
    const entry = (sec) => {
      if (!bySection.has(sec.id)) bySection.set(sec.id, { sec, rows: [], sources: [] });
      return bySection.get(sec.id);
    };
    const addMembers = (sec, members, label) => {
      const e = entry(sec);
      rollUp(members).forEach((row) => e.rows.push(row));
      if (!e.sources.includes(label)) e.sources.push(label);
    };
    addMembers(floorSection, floor.frame.members, "floor");
    {
      const e = entry(wallSection);
      walls.cuts.forEach((row) => e.rows.push(row));
      if (!e.sources.includes("walls")) e.sources.push("walls");
    }
    addMembers(roofSection, roof.frame.members, "roof");
    const up = entry(upstandSection);
    roof.upstand.pieces.forEach((p) => up.rows.push(p));
    if (!up.sources.includes("upstand")) up.sources.push("upstand");
    /* Battens come in their own length — 4.5 m for a 2 × 1, not the 4.8 m
       the framing is ordered in — so the pile carries the length it is cut
       from rather than taking the global one. */
    if (battens || joistBat) {
      const e = entry(battenSection);
      if (battens) {
        battens.cuts.forEach((row) => e.rows.push(row));
        if (!e.sources.includes("battens")) e.sources.push("battens");
      }
      if (joistBat) {
        joistBat.cuts.forEach((row) => e.rows.push(row));
        if (!e.sources.includes("joist battens")) e.sources.push("joist battens");
      }
      e.stock = battenStock;
    }
    /* The furring is ordinary timber on an ordinary length, so it goes in
       its section's pile with everything else cut from that section. */
    if (furr.length) {
      const e = entry(furrSection);
      furr.forEach((row) => e.rows.push({ len: row.len, qty: row.qty }));
      if (!e.sources.includes("furring")) e.sources.push("furring");
    }

    return [...bySection.values()].map((e) => {
      const merged = new Map();
      e.rows.forEach((row) => merged.set(row.len, (merged.get(row.len) || 0) + row.qty));
      const pieces = [...merged.entries()]
        .map(([len, qty]) => ({ len: Number(len), qty }))
        .sort((a, b) => b.len - a.len);
      const bars = packStock(pieces, e.stock || stock);
      return {
        ...e, pieces, bars, stock: e.stock || stock,
        linearM: pieces.reduce((s, p) => s + (p.len * p.qty) / 1000, 0),
        offcuts: bars.map((b) => b.offcut).filter((o) => o >= 250).sort((a, b) => b - a),
      };
    });
  }, [floorSection, roofSection, upstandSection, wallSection, floor, walls, roof, stock,
      battens, joistBat, battenSection, battenStock, furr, furrSection]);

  /* The full chain from ground to the top of the roof. Every figure here is
     already driving geometry elsewhere, so the total can't drift from it. */
  const heights = useMemo(() => {
    const rows = [
      { label: "Ground clearance", mm: groundClear },
      { label: `Floor joists (${floorSection.label})`, mm: floorSection.d },
      { label: `Floor deck (${fDeckT} mm OSB)`, mm: fDeckT },
      { label: "Internal ceiling height", mm: ceilingHeight, key: true },
      ...(fall.on ? [{ label: `Furring at the high end`, mm: fall.high }] : []),
      { label: `Roof joists (${roofSection.label})`, mm: roofSection.d },
      { label: `Roof deck (${rDeckT} mm OSB)`, mm: rDeckT },
      { label: `Upstand / PIR (${Math.max(roof.upstand.uh, roofPirT)} mm)`,
        mm: Math.max(roof.upstand.uh, roofPirT) },
      { label: `Top deck (${topT} mm OSB)`, mm: topT },
    ];
    const overall = rows.reduce((s, x) => s + x.mm, 0);
    const fixed = overall - ceilingHeight;
    const floorTop = groundClear + floorSection.d + fDeckT;
    return {
      rows, overall, fixed, floorTop,
      roofTop: roofSection.d + rDeckT + Math.max(roof.upstand.uh, roofPirT) + topT,
      maxCeiling: targetHeight - fixed,
      overTarget: overall - targetHeight,
    };
  }, [groundClear, floorSection, fDeckT, ceilingHeight, roofSection, rDeckT, roof, roofPirT,
      topT, targetHeight, fall]);

  /* ---- dimensions, in the shared global coordinates ---- */
  const geom = useMemo(() => {
    const floorTop = groundClear + floorSection.d + fDeckT;
    return {
      floorBottom: groundClear,
      floorTop,
      wallTop: floorTop + ceilingHeight,
      roofTop: floorTop + ceilingHeight + (fall.on ? fall.high : 0) + heights.roofTop,
      cxFloor: fL / 2, czFloor: fW / 2,
      cxRoof: (fL + ohRight - ohLeft) / 2, czRoof: (fW + ohFront - ohRear) / 2,
    };
  }, [groundClear, floorSection, fDeckT, ceilingHeight, heights, fL, fW,
      ohLeft, ohRight, ohFront, ohRear, fall]);

  const floorDims = useMemo(() => {
    const off = Math.max(300, Math.max(fL, fW) / 14);
    return [
      { from: [0, 0, -off], to: [fL, 0, -off], label: `${fmt(fL)} long`, strong: true },
      { from: [-off, 0, 0], to: [-off, 0, fW], label: `${fmt(fW)} wide`, strong: true },
      { from: [fL + off, geom.floorBottom, -off], to: [fL + off, geom.floorTop, -off],
        label: `${fmt(floorSection.d + fDeckT)} frame + deck` },
      { from: [-off, 0, -off], to: [-off, groundClear, -off],
        label: `${fmt(groundClear)} clearance` },
    ];
  }, [fL, fW, floorSection, fDeckT, groundClear, geom]);

  const wallDims = useMemo(() => {
    const off = Math.max(300, Math.max(fL, fW) / 14);
    return [
      { from: [0, 0, -off], to: [fL, 0, -off], label: `${fmt(fL)} long` },
      { from: [-off, 0, 0], to: [-off, 0, fW], label: `${fmt(fW)} wide` },
      { from: [-off, geom.floorTop, -off], to: [-off, geom.wallTop, -off],
        label: `${fmt(ceilingHeight)} internal ceiling`, strong: true },
      { from: [-off * 2, geom.wallTop - walls.sheathH, -off], to: [-off * 2, geom.wallTop, -off],
        label: `${fmt(walls.sheathH)} sheathed face` },
      { from: [fL + off, 0, fW + off], to: [fL + off, geom.wallTop, fW + off],
        label: `${fmt(geom.wallTop)} to wall top` },
    ];
  }, [fL, fW, ceilingHeight, walls, geom]);

  const roofDims = useMemo(() => {
    const off = Math.max(300, Math.max(roof.L, roof.W) / 14);
    return [
      { from: [-ohLeft, 0, -ohRear - off], to: [fL + ohRight, 0, -ohRear - off],
        label: `${fmt(roof.L)} roof`, strong: true },
      { from: [-ohLeft - off, 0, -ohRear], to: [-ohLeft - off, 0, fW + ohFront],
        label: `${fmt(roof.W)} roof`, strong: true },
      { from: [-ohLeft, geom.wallTop + roofSection.d + 60, -ohRear - off],
        to: [0, geom.wallTop + roofSection.d + 60, -ohRear - off],
        label: `${fmt(ohLeft)} overhang` },
      ...(explode ? [] : [{ from: [-ohLeft - off * 2, 0, -ohRear - off],
        to: [-ohLeft - off * 2, geom.roofTop, -ohRear - off],
        label: `${fmt(heights.overall)} overall`, strong: true }]),
      { from: [-ohLeft - off, geom.floorTop, fW + ohFront + off],
        to: [-ohLeft - off, geom.wallTop, fW + ohFront + off],
        label: `${fmt(ceilingHeight)} internal` },
    ];
  }, [roof, ohLeft, ohRight, ohFront, ohRear, fL, fW, ceilingHeight, geom, heights,
      roofSection, explode]);

  /* ---- one builder, three cumulative views ---- */
  /* The roof tray as it will actually be laid. A made-up tray board is
     replaced by its parts, put back in place from the position each one was
     given inside the piece — so the drawing and the 3D model show the seven
     boards you will carry up rather than the four that were planned. The
     plan's own shape is kept, so nothing downstream needs to know. */
  /* Where each made-up piece's parts ended up, keyed by the demand tag the
     allocation was handed. One map for the whole pile; each module reads its
     own prefix out of it. */
  const pirParts = useMemo(() => {
    const parts = new Map();
    alloc.pir.forEach((g) => g.log.forEach((l) => {
      if (!l.tray || !l.within) return;
      if (!parts.has(l.tray)) parts.set(l.tray, []);
      parts.get(l.tray).push(l);
    }));
    return parts;
  }, [alloc]);

  /* A planned piece, redrawn as the parts it was actually cut from. `within`
     runs along the piece's long side, so a tall piece has its parts turned
     back the other way about. `b` names the second axis because a deck is
     laid in x/z and a wall in x/y. */
  const asCut = useMemo(() => (pc, tag, b = "z") => {
    const got = pirParts.get(tag);
    if (!got || got.length < 2) return [pc];
    const db = b === "z" ? "dz" : "dy";
    const swap = pc[db] > pc.dx;
    return got.map((l) => {
      const [lo, hi] = [Math.max(l.dx, l.dz), Math.min(l.dx, l.dz)];
      return swap
        ? { x: pc.x + l.within.y, [b]: pc[b] + l.within.x, dx: hi, [db]: lo, full: false }
        : { x: pc.x + l.within.x, [b]: pc[b] + l.within.y, dx: lo, [db]: hi, full: false };
    });
  }, [pirParts]);

  const pirAsCut = useMemo(
    () => (pirParts.size
      ? roof.pirPlans.map((pl, li) => ({
          ...pl,
          pieces: pl.pieces.flatMap((pc, pi) => asCut(pc, `roof:${li}:${pi}`)),
        }))
      : roof.pirPlans),
    [pirParts, asCut, roof]
  );

  /* The same, for the bays the tape has to seal: a bay cut from four offcuts
     is four boards with three butt joints between them, and those joints are
     taped like any other. */
  const floorPirAsCut = useMemo(
    () => (pirParts.size
      ? { ...floor.pir,
          pieces: floor.pir.pieces.flatMap((pc, i) => asCut(pc, `floor:${i}`)) }
      : floor.pir),
    [pirParts, asCut, floor]
  );

  const wallPirAsCut = useMemo(() => {
    if (!pirParts.size) return wallPir;
    const out = { ...wallPir };
    const bySide = {};
    wallPir.pieces.forEach((pc, i) => {
      const side = wallPir.sides[i];
      (bySide[side] || (bySide[side] = [])).push(
        ...asCut({ x: pc.x, y: pc.y, dx: pc.dx, dy: pc.dy }, `wall:${i}`, "y"));
    });
    Object.keys(bySide).forEach((side) => {
      out[side] = { ...wallPir[side], pieces: bySide[side] };
    });
    return out;
  }, [pirParts, asCut, wallPir]);

  /* The same membrane lining the roof tray: on the deck under the PIR, up
     the inside of the upstand and over its top. Same roll and same tape as
     the walls, so it takes the same settings. */
  const roofWrap = useMemo(
    () => (rWrapOn
      ? planRoofWrap(roof, { rollW: wrapRollW, overlap: wrapOverlap,
          rollLen: wrapRollLen * 1000, tapeW: wrapTapeW,
          tapeRoll: wrapTapeRoll * 1000, orient: rWrapOrient })
      : null),
    [roof, rWrapOn, rWrapOrient, wrapRollW, wrapOverlap, wrapRollLen, wrapTapeW,
     wrapTapeRoll]
  );

  /* Foil tape over every PIR joint. Measured from the tray **as cut**, not
     as planned — a board made up from four offcuts has three more butt
     joints than the one board that was planned there, and those joints are
     the ones you tape. The planned tray is passed alongside so the panel can
     say what the piecing costs in tape. */
  /* One list again, for the same reason: it feeds two allocations and its
     fields were spelled out in both. */
  const tapeOpts = useMemo(
    () => ({ width: tapeW, roll: tapeRoll * 1000, waste: tapeWaste,
             layers: tapeLayers }),
    [tapeW, tapeRoll, tapeWaste, tapeLayers]
  );
  const tape = useMemo(
    () => (tapeOn
      ? planTape({
          floorPir: fPirT > 0 ? floorPirAsCut : null,
          wallPir: wPirT > 0 ? wallPirAsCut : null,
          roofPlans: pirAsCut, ...tapeOpts,
        })
      : null),
    [tapeOn, floorPirAsCut, wallPirAsCut, pirAsCut, fPirT, wPirT, tapeOpts]
  );

  /* The same pod in whole planned pieces, so the panel can say what the
     piecing costs in tape against what it saved in boards. Both figures then
     cover the same ground — floor, walls and roof — rather than setting the
     roof's extra joints against a pile-wide saving. */
  const tapeWholeWay = useMemo(
    () => (tapeOn
      ? planTape({
          floorPir: fPirT > 0 ? floor.pir : null,
          wallPir: wPirT > 0 ? wallPir : null,
          roofPlans: roof.pirPlans, ...tapeOpts,
        })
      : null),
    [tapeOn, floor, wallPir, roof, fPirT, wPirT, tapeOpts]
  );

  const tapeCost = useMemo(() => tapeExtra(tape, tapeWholeWay), [tape, tapeWholeWay]);

  const podArgs = useMemo(
    () => ({
      fL, fW, floorSection, fDeckT, groundClear,
      floorFrame: floor.frame, floorDeckPlan: floor.deck, floorPir: floor.pir,
      walls, wallPir, ceilingHeight, roof, roofSection, rDeckT, pirT: roofPirT, topT,
      pirAsCut,
      ohLeft, ohRear, windowGap: winGap, frameDepth, frameWidth, doorFrameWidth, fall,
      battens, joistBat, tape, wrap, roofWrap, clad, furrSection,
      solidLeaf: doorSolid,
    }),
    [fL, fW, floorSection, fDeckT, groundClear, floor, walls, wallPir, ceilingHeight, roof,
     pirAsCut, tape,
     roofSection, rDeckT, roofPirT, topT, ohLeft, ohRear, winGap, frameDepth, frameWidth,
     doorFrameWidth, fall, battens, joistBat, wrap, roofWrap, clad, furrSection,
     doorSolid]
  );

  const PART_COLOUR = useMemo(() => ({
    "floor frame": C.timber, "floor deck": C.osb, "wall frame": C.timber,
    header: C.header, sheathing: C.osb, frame: FRAME_COLOUR, glazing: C.glass,
    "roof frame": C.timber, "roof noggin": C.noggin, "roof deck": C.osb,
    upstand: C.upstand, batten: C.batten, "counter batten": C.batten,
    "joist batten": C.batten,
    insulation: C.pir, "floor insulation": C.pir,
    "wall insulation": C.pir, tape: C.tape, wrap: C.wrap, "roof wrap": C.wrap, cladding: C.cladding,
    "top deck": C.osbCut, gutter: C.gutter,
    plinth: C.floorblock, walls: C.wallblock, roof: C.osb,
  }), [C]);

  /* Cut pieces read a shade darker than whole sheets, as they did before. */
  const CUT_COLOUR = useMemo(() => ({
    "floor deck": C.osbCut,
    "roof deck": C.osbCut,
    "top deck": darken(C.osbCut, 0.14),
    insulation: darken(C.pir, 0.13),
    "floor insulation": darken(C.pir, 0.13),
    "wall insulation": darken(C.pir, 0.13),
    sheathing: C.osbCut,
  }), [C]);

  /* Three ways of colouring the same geometry: as materials, one hue per
     wall, or one hue per construction layer when legibility beats realism. */
  const colourFor = (b) => {
    if (colourBy === "layer") {
      const key = PART_LAYER[b.part];
      if (b.part === "frame") return FRAME_COLOUR;
      if (key) return b.full === false ? darken(hexInt(LAYER_COLOURS[key]), 0.16)
                                       : hexInt(LAYER_COLOURS[key]);
      return C.timber;
    }
    if (C.byWall && b.side && (b.part === "wall frame" || b.part === "sheathing"))
      return b.part === "sheathing" ? C.sideLight[b.side] : C.sides[b.side];
    if (b.full === false && CUT_COLOUR[b.part] != null) return CUT_COLOUR[b.part];
    return PART_COLOUR[b.part] ?? C.timber;
  };

  const buildView = (opts, ghostable) => {
    const out = podBoxes({ ...podArgs, opts }).map((b) => ({
      ...b,
      color: colourFor(b),
      ghostable: ghostable(b),
      ...(b.part === "glazing"
        ? { translucent: true, opacity: 0.26, edgeHex: 0x3c5c6b }
        : null),
    }));
    if (shown("boundary")) {
      /* Positioned from the building face, so it takes the setting-out
         figure rather than the one that was typed — those are the same
         number only when the gap was measured from the frame. */
      const wb = wallBox(
        { ...wall, gap: roof.clearance ? roof.clearance.frameToWall : wallGap },
        { L: fL, W: fW, groundY: 0, height: boundaryH });
      if (wb) out.push({ ...wb, part: "boundary", color: C.wall, ghostable: false, plain: true });
    }
    return out;
  };

  /* One ghostable predicate for all three views: sheet goods and glazing go
     see-through, framing never does. */
  /* Glass is never solid, whatever the view mode says. */
  const isGhostable = (b) =>
    b.part === "floor deck" || b.part === "sheathing" ||
    b.part === "roof deck" || b.part === "top deck" || b.part === "insulation" ||
    b.part === "floor insulation" || b.part === "wall insulation";

  const roofLayers = [
    shown("roofDeck") && "deck", shown("upstand") && "upstand",
    shown("pir") && "pir", shown("topDeck") && "top",
  ].filter(Boolean);

  const floorBoxes = useMemo(
    () => buildView(
      { pieces: true, floorDeck: shown("floorDeck"), floorPir: shown("floorPir"),
        ledgers: shown("ledgers"), tape: shown("tape"), explodeFloor: explode },
      isGhostable
    ),
    [podArgs, show, C, PART_COLOUR, wallSide, wallGap, boundaryH, colourBy, explode]
  );

  const wallBoxes = useMemo(
    () => buildView(
      { pieces: true, floorDeck: shown("floorDeck"), floorPir: shown("floorPir"),
        walls: true, wallPir: shown("wallPir"),
        sheathing: shown("sheathing"), glazing: shown("glazing"),
        battens: shown("battens"), ledgers: shown("ledgers"), tape: shown("tape"),
        wrap: shown("wrap"), cladding: shown("cladding"),
        explodeWalls: explode, furring: true, explodeFloor: 0 },
      isGhostable
    ),
    [podArgs, show, C, PART_COLOUR, wallSide, wallGap, boundaryH, colourBy, explode]
  );

  const roofBoxes = useMemo(
    () => buildView(
      { pieces: true, floorDeck: shown("floorDeck"), floorPir: shown("floorPir"),
        walls: true, wallPir: shown("wallPir"),
        sheathing: shown("sheathing"), glazing: shown("glazing"),
        battens: shown("battens"), ledgers: shown("ledgers"), tape: shown("tape"),
        wrap: shown("wrap"), roofWrap: shown("wrap"), cladding: shown("cladding"),
        roof: true, roofLayers, gutter: shown("gutter"), explodeRoof: explode,
        furring: true },
      isGhostable
    ),
    [podArgs, show, C, PART_COLOUR, wallSide, wallGap, boundaryH, colourBy, explode]
  );

  /* Export runs the same builder with everything switched on. */
  const assembly = useMemo(() => {
    const boxes = asmMode === "massing"
      ? massingBoxes(podArgs)
      : podBoxes({
          ...podArgs,
          opts: {
            floorDeck: asmInclude.includes("floor"), walls: asmInclude.includes("walls"),
            sheathing: asmInclude.includes("sheathing"), glazing: asmInclude.includes("glazing"),
            /* Battens go on with the sheathing: they are the same skin, and
               a print of the walls without them is missing 22 mm all round. */
            battens: asmInclude.includes("sheathing"),
            wrap: asmInclude.includes("sheathing"),
            cladding: asmInclude.includes("sheathing"),
            roofWrap: asmInclude.includes("roof"),
            ledgers: asmInclude.includes("floor"),
            roof: asmInclude.includes("roof"),
            roofLayers: asmInclude.includes("insulation")
              ? ["deck", "upstand", "pir", "top"] : ["deck", "upstand", "top"],
            gutter: false,
          },
        }).filter((b) => asmInclude.includes("floor") || b.stage !== "floor");
    return { boxes, ext: extents(boxes) };
  }, [podArgs, asmInclude, asmMode]);

  const printSize = useMemo(() => {
    const s = 1 / asmScale;
    const e = assembly.ext;
    const raw = e.minFeature * s;
    return {
      x: e.dx * s, y: e.dz * s, z: e.dy * s,
      minFeature: raw, thinnest: e.thinnest,
      effective: asmMinFeature ? Math.max(raw, asmMinFeature) : raw,
      inflated: asmMinFeature
        ? assembly.boxes.filter((b) => Math.min(b.dx, b.dy, b.dz) * s < asmMinFeature).length
        : 0,
      biggest: Math.max(e.dx, e.dy, e.dz) * s,
    };
  }, [assembly, asmScale, asmMinFeature]);

  const exportSTL = () => {
    const prepped = inflate(assembly.boxes, 1 / asmScale, asmMinFeature);
    const { buffer, triangles } = boxesToSTL(prepped, 1 / asmScale, `pod-1-${asmScale}`);
    const blob = new Blob([buffer], { type: "model/stl" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `garden-pod-${asmMode}-1-${asmScale}.stl`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
    return triangles;
  };

  /* ---- cameras ----
     Defaults are opinionated per section and swing round to face whichever
     side matters; a saved view overrides them and travels in the JSON. */
  const camFloor = useRef(null);
  const camWalls = useRef(null);
  const camRoof = useRef(null);

  const viewFor = (which) => {
    const saved = views && views[which];
    if (saved) return saved;
    const base = DEFAULT_VIEWS[which];
    if (which === "walls") return winOn ? viewForSide(base, "walls", winSide) : base;
    if (which === "roof") return viewForSide(base, "roof", gutterSide);
    return base;
  };

  const saveView = (which, ref) => {
    if (!ref.current) return;
    setViews((cur) => ({ ...(cur || {}), [which]: ref.current.capture() }));
  };
  const clearView = (which, ref) => {
    setViews((cur) => {
      if (!cur || !cur[which]) return cur;
      const next = { ...cur };
      delete next[which];
      return Object.keys(next).length ? next : null;
    });
    setTimeout(() => ref.current && ref.current.reset(), 0);
  };

  /* ---- what to buy, and what it costs ----
     Lines are derived from the same allocations the cut lists use, so the
     tally can't drift from the drawings. Prices are per unit and live in the
     saved design. */
  const orderLines = useMemo(() => {
    const lines = [];
    alloc.osb.forEach((g) =>
      lines.push({ id: `osb-${g.thickness}-${g.board.id}`,
        label: `OSB3 ${g.thickness} mm, ${g.board.label}`, qty: g.bought, unit: "sheet" })
    );
    alloc.pir.forEach((g) =>
      lines.push({ id: `pir-${g.thickness}-${pirBoard.id}`,
        label: `PIR ${g.thickness} mm, ${pirBoard.label}`, qty: g.bought, unit: "board" }));
    timber.forEach((t) =>
      lines.push({ id: `timber-${t.sec.id}-${t.stock}`,
        label: `${t.sec.label} mm, ${(t.stock / 1000).toFixed(1)} m lengths`,
        qty: t.bars.length, unit: "length" })
    );
    lines.push({ id: "epdm",
      label: `EPDM, ${fmt(roof.epdm.l)} × ${fmt(roof.epdm.w)} mm`,
      qty: Number(m2(roof.epdm.area)), unit: "m²" });
    if (winOn)
      lines.push({ id: "window",
        label: winSizes
          ? `Window, ${fmt(winSizes.frameW)} × ${fmt(winSizes.frameH)} mm`
          : "Window",
        qty: 1, unit: "unit" });
    fixings.lines.forEach((f) =>
      lines.push({ id: `fix-${f.id}`, label: f.label, qty: f.qty, unit: f.unit }));
    /* Bought by the roll, but the metres are what you check it against, so
       the label carries them. */
    if (tape && tape.rolls > 0)
      lines.push({ id: `tape-${tape.width}-${tapeRoll}`,
        label: `PIR foil tape, ${tape.width} mm × ${tapeRoll} m — ${(tape.ordered / 1000).toFixed(1)} m needed`,
        qty: tape.rolls, unit: "roll" });
    /* Walls and roof come off the same roll and are taped with the same
       tape, so they are one order line each rather than four — the whole
       point of it being the same membrane. */
    const runM = (wrap ? wrap.runM : 0) + (roofWrap ? roofWrap.runM : 0);
    const tapeM = (wrap ? wrap.tape.total : 0) + (roofWrap ? roofWrap.tape.total : 0);
    if (runM > 0) {
      lines.push({ id: `wrap-${wrapRollW}-${wrapRollLen}`,
        label: `Breather membrane, ${fmt(wrapRollW)} mm × ${wrapRollLen} m — ${(runM / 1000).toFixed(1)} m needed`,
        qty: Math.ceil(runM / (wrapRollLen * 1000)), unit: "roll" });
      if (tapeM > 0)
        lines.push({ id: `wraptape-${wrapTapeW}-${wrapTapeRoll}`,
          label: `Membrane lap tape, ${wrapTapeW} mm × ${wrapTapeRoll} m — ${(tapeM / 1000).toFixed(1)} m needed`,
          qty: Math.ceil(tapeM / (wrapTapeRoll * 1000)), unit: "roll" });
    }
    if (clad && clad.lengths > 0)
      lines.push({ id: `clad-${cladFace}x${cladT}-${cladStock}`,
        label: `Cladding, ${fmt(cladFace)} × ${fmt(cladT)} mm shadow gap, ${cladStock} m lengths — ${(clad.linear / 1000).toFixed(1)} m cut`,
        qty: clad.lengths, unit: "length" });
    lines.push({ id: "trussclips", label: "Tie truss clips",
      qty: (roof.frame.n + 2) * 2, unit: "each" });
    lines.push({ id: "tbfixings", label: "Thermally broken fixings",
      qty: Math.ceil(((roof.L * roof.W) / 1e6) * 4), unit: "each" });
    lines.push({ id: "guttering", label: `Gutter and downpipe, ${sideLabel(gutterSide).toLowerCase()}`,
      qty: Number((roof.gutter.run / 1000).toFixed(2)), unit: "m" });
    lines.push({ id: "trim", label: "Edge and drip trim",
      qty: Number(((roof.gutter.run + roof.gutter.edgeTrim) / 1000).toFixed(2)), unit: "m" });
    return lines.filter((l) => l.qty > 0);
  }, [alloc, timber, roof, roofPirT, pirBoard, stock, winOn, winSizes, fixings, gutterSide,
      tape, tapeRoll, wrap, roofWrap, wrapRollW, wrapRollLen, wrapTapeW,
      wrapTapeRoll, clad, cladFace, cladT, cladStock]);

  const cost = useMemo(() => priceTotal(orderLines, prices), [orderLines, prices]);
  const money = (n) =>
    `${currency}${n.toLocaleString("en-IE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  /* ---- save / load ----
     One map of every design field, so export and import stay in step with
     the state without hand-maintaining two lists. */
  const fields = {
    sheetFor: [sheetFor, setSheetFor], enabled: [enabled, setEnabled],
    pirBoardId: [pirBoardId, setPirBoardId], stock: [stock, setStock],
    gap: [gap, setGap], stagger: [stagger, setStagger], goal: [goal, setGoal],
    fPirT: [fPirT, setFPirT], wPirT: [wPirT, setWPirT],
    fBattenOn: [fBattenOn, setFBattenOn], fBattenPct: [fBattenPct, setFBattenPct],
    fBattenOnEdge: [fBattenOnEdge, setFBattenOnEdge],
    tapeOn: [tapeOn, setTapeOn], tapeW: [tapeW, setTapeW],
    tapeRoll: [tapeRoll, setTapeRoll], tapeWaste: [tapeWaste, setTapeWaste],
    tapeLayers: [tapeLayers, setTapeLayers],
    wrapOn: [wrapOn, setWrapOn], wrapRollW: [wrapRollW, setWrapRollW],
    wrapOverlap: [wrapOverlap, setWrapOverlap], wrapDrop: [wrapDrop, setWrapDrop],
    wrapRollLen: [wrapRollLen, setWrapRollLen],
    wrapTapeW: [wrapTapeW, setWrapTapeW], wrapTapeRoll: [wrapTapeRoll, setWrapTapeRoll],
    rWrapOn: [rWrapOn, setRWrapOn], rWrapOrient: [rWrapOrient, setRWrapOrient],
    pirPiece: [pirPiece, setPirPiece], pirPieceL: [pirPieceL, setPirPieceL],
    pirPieceW: [pirPieceW, setPirPieceW],
    pirPieceMax: [pirPieceMax, setPirPieceMax],
    pirTol: [pirTol, setPirTol],
    locationId: [locationId, setLocationId],
    siteWinter: [siteWinter, setSiteWinter], siteSummer: [siteSummer, setSiteSummer],
    frontFaces: [frontFaces, setFrontFaces],
    indoorWinter: [indoorWinter, setIndoorWinter],
    indoorSummer: [indoorSummer, setIndoorSummer],
    airChanges: [airChanges, setAirChanges], internalGain: [internalGain, setInternalGain],
    shadingFactor: [shadingFactor, setShadingFactor],
    uGlazing: [uGlazing, setUGlazing], gGlazing: [gGlazing, setGGlazing],
    uDoorSolid: [uDoorSolid, setUDoorSolid], lambdaPir: [lambdaPir, setLambdaPir],
    fixOn: [fixOn, setFixOn], fixJointScrews: [fixJointScrews, setFixJointScrews],
    fixNogginScrews: [fixNogginScrews, setFixNogginScrews],
    fixHangers: [fixHangers, setFixHangers],
    fixSheetPitch: [fixSheetPitch, setFixSheetPitch],
    wallSide: [wallSide, setWallSide], wallGap: [wallGap, setWallGap],
    wallGapFrom: [wallGapFrom, setWallGapFrom],
    groundClear: [groundClear, setGroundClear], targetHeight: [targetHeight, setTargetHeight],
    show: [show, setShow], renderMode: [renderMode, setRenderMode],
    boundaryH: [boundaryH, setBoundaryH],
    views: [views, setViews],
    paletteId: [paletteId, setPaletteId],
    colourBy: [colourBy, setColourBy], explode: [explode, setExplode],
    customSections: [customSections, setCustomSections],
    prices: [prices, setPrices], currency: [currency, setCurrency],
    furrOn: [furrOn, setFurrOn], furrLow: [furrLow, setFurrLow],
    furrHigh: [furrHigh, setFurrHigh], furrId: [furrId, setFurrId],
    fL: [fL, setFL], fW: [fW, setFW], fSectionId: [fSectionId, setFSectionId],
    fSpacing: [fSpacing, setFSpacing], fDeckT: [fDeckT, setFDeckT],
    fAlign: [fAlign, setFAlign], fOrient: [fOrient, setFOrient],
    fJoist: [fJoist, setFJoist], fDouble: [fDouble, setFDouble],
    fNoggins: [fNoggins, setFNoggins],
    wSectionId: [wSectionId, setWSectionId], wLayout: [wLayout, setWLayout], wLap: [wLap, setWLap],
    battenOn: [battenOn, setBattenOn], battenId: [battenId, setBattenId],
    battenOnEdge: [battenOnEdge, setBattenOnEdge], battenStock: [battenStock, setBattenStock],
    cladding: [cladding, setCladding],
    cladOn: [cladOn, setCladOn], cladFace: [cladFace, setCladFace],
    cladGap: [cladGap, setCladGap], cladT: [cladT, setCladT],
    cladStock: [cladStock, setCladStock], cladSides: [cladSides, setCladSides],
    battenSpacing: [battenSpacing, setBattenSpacing],
    battenMode: [battenMode, setBattenMode],
    battenHSpacing: [battenHSpacing, setBattenHSpacing],
    battenHMode: [battenHMode, setBattenHMode],
    wSheathAlign: [wSheathAlign, setWSheathAlign], wSheathOrient: [wSheathOrient, setWSheathOrient], wSheathCut: [wSheathCut, setWSheathCut], ceilingHeight: [ceilingHeight, setCeilingHeight],
    wSpacing: [wSpacing, setWSpacing], wStudAlign: [wStudAlign, setWStudAlign],
    wNoggins: [wNoggins, setWNoggins], wSheathT: [wSheathT, setWSheathT],
    winOn: [winOn, setWinOn], winSide: [winSide, setWinSide], winW: [winW, setWinW],
    winH: [winH, setWinH], winSill: [winSill, setWinSill], winGap: [winGap, setWinGap],
    winCentred: [winCentred, setWinCentred], winCentre: [winCentre, setWinCentre],
    headerId: [headerId, setHeaderId], winStyle: [winStyle, setWinStyle],
    headerOnEdge: [headerOnEdge, setHeaderOnEdge], glazedFix: [glazedFix, setGlazedFix],
    doorOn: [doorOn, setDoorOn], doorSide: [doorSide, setDoorSide],
    doorW: [doorW, setDoorW], doorH: [doorH, setDoorH], doorGap: [doorGap, setDoorGap],
    doorFrom: [doorFrom, setDoorFrom], doorOffset: [doorOffset, setDoorOffset],
    doorSolid: [doorSolid, setDoorSolid],
    doorFullHeader: [doorFullHeader, setDoorFullHeader],
    doorHeaderOnEdge: [doorHeaderOnEdge, setDoorHeaderOnEdge],
    wMinBay: [wMinBay, setWMinBay], spacingMode: [spacingMode, setSpacingMode],
    frameDepth: [frameDepth, setFrameDepth], frameWidth: [frameWidth, setFrameWidth],
    doorFrameWidth: [doorFrameWidth, setDoorFrameWidth],
    ohLeft: [ohLeft, setOhLeft], ohRight: [ohRight, setOhRight],
    ohFront: [ohFront, setOhFront], ohRear: [ohRear, setOhRear],
    rSectionId: [rSectionId, setRSectionId], rSpacing: [rSpacing, setRSpacing],
    rJoist: [rJoist, setRJoist], rNoggins: [rNoggins, setRNoggins],
    rDeckT: [rDeckT, setRDeckT], rAlign: [rAlign, setRAlign], rOrient: [rOrient, setROrient],
    rPirMode: [rPirMode, setRPirMode],
    upstandId: [upstandId, setUpstandId], upstandOnEdge: [upstandOnEdge, setUpstandOnEdge],
    pirT: [pirT, setPirT], topT: [topT, setTopT], topAlign: [topAlign, setTopAlign],
    topOrient: [topOrient, setTopOrient], epdmMargin: [epdmMargin, setEpdmMargin],
    gutterSide: [gutterSide, setGutterSide],
    asmMode: [asmMode, setAsmMode], asmScale: [asmScale, setAsmScale],
    asmInclude: [asmInclude, setAsmInclude], asmGhost: [asmGhost, setAsmGhost],
    asmMinFeature: [asmMinFeature, setAsmMinFeature],
  };

  const heightsRef = useRef(0);
  /* In glazed-wall mode the window is whatever the wall leaves, so the useful
     direction is the other way: size the building to suit a target width. */

  const [ioMsg, setIoMsg] = useState(null);
  const [pasted, setPasted] = useState("");
  const fileRef = useRef(null);

  const configJSON = () =>
    JSON.stringify(
      {
        kind: "garden-pod-studio",
        version: DESIGN_VERSION,
        saved: new Date().toISOString(),
        summary: {
          footprint: `${fL} × ${fW}`,
          overall: heightsRef.current,
        },
        design: Object.fromEntries(Object.entries(fields).map(([k, [v]]) => [k, v])),
      },
      null,
      2
    );

  const applyConfig = (text) => {
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch (e) {
      return setIoMsg({ ok: false, text: "That isn't valid JSON." });
    }
    if (!parsed || parsed.kind !== "garden-pod-studio")
      return setIoMsg({ ok: false, text: "Not a garden pod file — no matching header." });
    const raw = parsed.design || parsed.config;
    if (!raw || typeof raw !== "object")
      return setIoMsg({ ok: false, text: "File has no design section." });
    const design = migrateDesign(raw, Number(parsed.version) || 1);

    let applied = 0;
    const skipped = [];
    Object.entries(design).forEach(([k, v]) => {
      const entry = fields[k];
      if (!entry) return void skipped.push(k);
      const [current, set] = entry;
      if (Array.isArray(current) !== Array.isArray(v) || typeof current !== typeof v)
        return void skipped.push(k);
      set(v);
      applied += 1;
    });
    setIoMsg({
      ok: applied > 0,
      text: applied
        ? `Loaded ${applied} settings${skipped.length ? `, ignored ${skipped.length} unrecognised` : ""}.`
        : "Nothing in that file matched.",
    });
  };

  const applyDesign = (design) => {
    Object.entries(design).forEach(([k, v]) => {
      const entry = fields[k];
      if (entry) entry[1](v);
    });
  };

  const loadExample = (ex) => {
    applyDesign(migrateDesign({ ...DEFAULT_DESIGN, ...ex.design }, DESIGN_VERSION));
    setIoMsg({ ok: true, text: `Loaded “${ex.name}”.` });
    setNav("walls");
  };

  const downloadConfig = () => {
    const blob = new Blob([configJSON()], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `garden-pod-${fL}x${fW}.json`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
    setIoMsg({ ok: true, text: "Saved." });
  };

  const readFile = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => applyConfig(String(reader.result));
    reader.onerror = () => setIoMsg({ ok: false, text: "Could not read that file." });
    reader.readAsText(file);
    e.target.value = "";
  };

  heightsRef.current = heights.overall;

  const floorCuts = useMemo(() => rollUp(floor.frame.members), [floor]);
  /* Bays repeat, so quote each size once with a quantity rather than listing
     every one of them. */
  const wallPirBays = useMemo(() => {
    const map = new Map();
    wallPir.pieces.forEach((p) => {
      const k = `${p.dx}x${p.dy}`;
      if (!map.has(k)) map.set(k, { dx: p.dx, dy: p.dy, qty: 0 });
      map.get(k).qty += 1;
    });
    return [...map.values()].sort((a, b) => b.dx * b.dy - a.dx * a.dy);
  }, [wallPir]);

  const pirBays = useMemo(() => {
    const map = new Map();
    floor.pir.pieces.forEach((p) => {
      const k = `${p.dx}x${p.dz}`;
      if (!map.has(k)) map.set(k, { dx: p.dx, dz: p.dz, qty: 0 });
      map.get(k).qty += 1;
    });
    return [...map.values()].sort((a, b) => b.dx * b.dz - a.dx * a.dz);
  }, [floor]);
  const roofCuts = useMemo(() => rollUp(roof.frame.members), [roof]);
  const fSheets = sheetsFor("Floor deck");
  const rSheets = sheetsFor("Roof deck");
  const tSheets = sheetsFor("Roof top deck");
  const wSheets = sheetsFor("Wall sheathing");
  const usedThicknesses = [...new Set([fDeckT, wSheathT, rDeckT, topT])].sort((a, b) => a - b);
  const orientOpts = [
    { value: "auto", label: "Auto" },
    { value: "along", label: "Long side ↔ length" },
    { value: "across", label: "Long side ↕ width" },
  ];

  return (
    <div className="app">
      <style>{`
        .app {
          --paper:#E6E7E0; --panel:#DCDED5; --ink:#1B2420; --soft:#5B645C;
          --rule:#A9B0A7; --timber:#C5877C; --osb:#C6A268; --pir:#E3DBA4;
          --pencil:#9E3427; --ok:#41704A;
          background:var(--paper); color:var(--ink); min-height:100vh;
          font-family:"Inter",-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif;
          font-variant-numeric:tabular-nums; -webkit-font-smoothing:antialiased;
          display:flex; flex-direction:column;
        }
        .app * { box-sizing:border-box; }
        @media (min-width:900px) {
          .app { height:100vh; height:100dvh; overflow:hidden; }
          .masthead { flex:0 0 auto; }
          .stage { overflow:hidden; }
        }
        .masthead { padding:18px 22px 12px; border-bottom:1px solid var(--rule); }
        .topline { display:flex; flex-wrap:wrap; align-items:baseline; gap:14px 20px; }
        .masthead h1 { margin:0; font-size:20px; font-weight:600; letter-spacing:-0.015em; }
        .nav { display:flex; gap:4px; }
        .costtag { margin-left:auto; display:flex; flex-direction:column; align-items:flex-end;
          gap:1px; font:inherit; cursor:pointer; padding:4px 12px; border-radius:2px;
          background:transparent; border:1px solid var(--rule); color:var(--soft); }
        .costtag.has { border-color:var(--ink); color:var(--ink); }
        .costtag:hover { border-color:var(--ink); }
        .costtag:focus-visible { outline:2px solid var(--pencil); outline-offset:1px; }
        .costnum { font-size:17px; font-weight:600; letter-spacing:-0.02em; line-height:1.05; }
        .costsub { font-size:10.5px; text-transform:uppercase; letter-spacing:.07em; }
        .pricetable input { max-width:110px; text-align:right; }
        .pricetable td:last-child { font-variant-numeric:tabular-nums; }
        .navbtn { font:inherit; font-size:13.5px; padding:6px 14px; cursor:pointer;
          background:transparent; color:var(--soft); border:1px solid var(--rule); border-radius:2px; }
        .navbtn.on { background:var(--ink); color:var(--paper); border-color:var(--ink); font-weight:600; }
        .visbar { display:flex; flex-wrap:wrap; align-items:center; gap:5px;
          margin-top:12px; padding-top:11px; border-top:1px solid var(--rule); }
        .visbar-lab { font-size:11.5px; color:var(--soft); margin-right:3px;
          text-transform:uppercase; letter-spacing:.06em; }
        .visbar-sep { width:1px; height:18px; background:var(--rule); margin:0 6px; }
        .exslider { width:120px; height:18px; accent-color:var(--ink); cursor:pointer; }
        .exslider:focus-visible { outline:2px solid var(--pencil); outline-offset:2px; }
        .exval { font-size:11.5px; color:var(--soft); min-width:52px;
          font-variant-numeric:tabular-nums; }
        .vis { font:inherit; font-size:12px; padding:4px 9px; cursor:pointer;
          background:transparent; color:var(--soft); border:1px solid var(--rule);
          border-radius:2px; }
        .vis.on { background:var(--ok); color:#fff; border-color:var(--ok); }
        .vis.mode.on { background:var(--ink); border-color:var(--ink); color:var(--paper); }
        .vis:focus-visible { outline:2px solid var(--pencil); outline-offset:1px; }
        .readout { display:flex; flex-wrap:wrap; gap:6px 24px; margin-top:12px; font-size:13px; }
        .readout b { font-weight:600; }
        .readout span { color:var(--soft); }
        .body { display:flex; flex:1; min-height:0; flex-direction:column; }
        /* On a wide screen the page itself doesn't scroll: the rail and the
           panel each scroll on their own so the model never moves. */
        @media (min-width:900px) {
          .body { flex-direction:row; overflow:hidden; }
        }
        .rail { padding:18px 22px 28px; border-bottom:1px solid var(--rule); background:var(--panel); }
        @media (min-width:900px) {
          .rail { width:322px; flex:0 0 322px; border-bottom:none;
            border-right:1px solid var(--rule); overflow-y:auto; overscroll-behavior:contain; }
        }
        .rail h3 { font-size:12px; font-weight:600; color:var(--soft); margin:22px 0 12px;
          padding-bottom:6px; border-bottom:1px solid var(--rule); }
        .rail h3:first-child { margin-top:0; }
        .field { margin-bottom:16px; }
        .field > label { display:block; font-size:12px; color:var(--soft); margin-bottom:6px; }
        .pair { display:flex; gap:10px; }
        .quad { display:grid; grid-template-columns:1fr 1fr; gap:8px; }
        .quad label { font-size:11px; color:var(--soft); display:block; margin-bottom:3px; }
        input[type=number], select {
          width:100%; padding:8px 10px; font:inherit; font-size:14px; background:var(--paper);
          color:var(--ink); border:1px solid var(--rule); border-radius:2px;
        }
        input:focus-visible, select:focus-visible, .chip:focus-visible,
        .tab:focus-visible, .navbtn:focus-visible { outline:2px solid var(--pencil); outline-offset:1px; }
        input.pending { border-color:var(--pencil); }
        .chips { display:flex; flex-wrap:wrap; gap:5px; }
        .chip { font:inherit; font-size:13px; padding:6px 11px; cursor:pointer; background:transparent;
          color:var(--soft); border:1px solid var(--rule); border-radius:2px; }
        .chip.on { background:var(--ink); color:var(--paper); border-color:var(--ink); }
        .hint { font-size:11.5px; color:var(--soft); margin-top:6px; line-height:1.45; }
        .tapesum { display:inline-block; margin-right:14px; white-space:nowrap; }
        .tapesum b { color:var(--ink); }
        .stage { flex:1; min-width:0; display:flex; flex-direction:column; }
        .tabs { display:flex; gap:2px; padding:10px 22px 0; border-bottom:1px solid var(--rule); flex-wrap:wrap; }
        .tab { font:inherit; font-size:13px; padding:8px 13px; cursor:pointer; background:transparent;
          border:1px solid transparent; border-bottom:none; color:var(--soft);
          border-radius:2px 2px 0 0; margin-bottom:-1px; }
        .tab.on { color:var(--ink); background:var(--paper); border-color:var(--rule);
          border-bottom:1px solid var(--paper); font-weight:600; }
        .panelbody { flex:1; min-height:440px; display:flex; flex-direction:column; }
        .viewport { flex:1; min-height:420px; position:relative; overflow:hidden; cursor:grab; }
        .viewport canvas:focus { outline:none; }
        .viewport canvas:focus-visible { outline:2px solid var(--pencil); outline-offset:-2px; }
        .setup { display:grid; gap:12px 16px; margin:14px 0 4px; max-width:820px;
          grid-template-columns:repeat(auto-fit,minmax(180px,1fr)); }
        .setup label { display:block; font-size:11.5px; color:var(--soft); margin-bottom:4px; }
        .elevgrid { display:grid; gap:20px; grid-template-columns:1fr; }
        @media (min-width:1100px) { .elevgrid { grid-template-columns:1fr 1fr; } }
        .elevcell { display:flex; flex-direction:column; min-height:340px; }
        .elevcell h4 { margin:0 0 4px; font-size:13px; font-weight:600; }
        .elevcell h4 span { color:var(--soft); font-weight:400; }
        .elevcell .draw { max-height:330px; }
        .customrow { display:flex; align-items:center; gap:8px; font-size:12.5px;
          padding:5px 0; border-bottom:1px solid var(--rule); }
        .customrow b { font-weight:600; }
        .customrow .linkbtn { margin-left:auto; }
        input[type=text] { width:100%; padding:8px 10px; font:inherit; font-size:14px;
          background:var(--paper); color:var(--ink); border:1px solid var(--rule);
          border-radius:2px; }
        .exgrid { display:grid; gap:12px; margin:14px 0 6px;
          grid-template-columns:repeat(auto-fit,minmax(280px,1fr)); max-width:1000px; }
        .excard { text-align:left; font:inherit; cursor:pointer; padding:14px 16px;
          background:var(--panel); border:1px solid var(--rule); border-radius:2px; color:var(--ink); }
        .excard:hover { border-color:var(--ink); }
        .excard h4 { margin:0 0 6px; font-size:13.5px; font-weight:600; }
        .excard p { margin:0; font-size:12.5px; line-height:1.5; color:var(--soft); }
        .excard .exnote { margin-top:8px; font-size:11.5px; color:var(--soft); font-style:italic; }
        .docgrid { display:grid; gap:16px; grid-template-columns:repeat(auto-fit,minmax(260px,1fr));
          max-width:1000px; margin:10px 0; }
        .doc h4 { margin:0 0 5px; font-size:13px; font-weight:600; }
        .doc p { margin:0; font-size:12.5px; line-height:1.55; color:var(--soft); }
        .camctl { display:inline-flex; gap:10px; margin-left:auto; }
        .linkbtn { font:inherit; font-size:12px; padding:0; background:none; border:none;
          color:var(--soft); cursor:pointer; text-decoration:underline;
          text-underline-offset:2px; }
        .linkbtn:hover { color:var(--ink); }
        .linkbtn:focus-visible { outline:2px solid var(--pencil); outline-offset:2px; }
        .legend { align-items:center; }
        .keys { font-size:11.5px; color:var(--soft); }
        .keys kbd { font:inherit; font-size:11px; padding:1px 5px; margin:0 1px;
          border:1px solid var(--rule); border-radius:2px; background:var(--panel); }
        .dimlayer { position:absolute; inset:0; pointer-events:none; overflow:hidden; }
        .dimlabel { position:absolute; top:0; left:0; white-space:nowrap; font-size:11.5px;
          font-weight:500; color:var(--ink); background:rgba(230,231,224,.88);
          padding:1px 5px; border:1px solid var(--rule); border-radius:2px;
          font-variant-numeric:tabular-nums; }
        .dimlabel.strong { background:var(--ink); color:var(--paper); border-color:var(--ink); font-weight:600; }
        .stack .layer.head { background:var(--panel); font-weight:600; }
        .viewport:active { cursor:grabbing; }
        .drawwrap { flex:1; padding:18px; min-height:0; display:flex; }
        .draw { width:100%; height:100%; max-height:600px; }
        .mem { fill:var(--timber); stroke:#7E4E47; stroke-width:5; }
        .mem.noggin { fill:#B0716A; }
        .dim line { stroke:var(--ink); stroke-width:4; }
        .dim line.anchor { stroke:var(--ok); stroke-width:9; }
        .dimtext { fill:var(--ink); font-size:74px; }
        .dimtext.small { fill:var(--soft); font-size:58px; }
        .dimtext.tiny { fill:var(--soft); font-size:44px; }
        .deckoutline { fill:none; stroke:var(--ink); stroke-width:5; stroke-dasharray:34 22; }
        .sheet { fill:var(--osb); stroke:#7C6031; stroke-width:5; }
        .sheet.iscut { fill:#A87F42; }
        .pir { fill:var(--pir); stroke:#9A9058; stroke-width:5; }
        .pir.iscut { fill:#D2C77F; }
        .sheetlabel { fill:#3B2E0D; font-size:100px; font-weight:600; }
        .sheetdim { fill:#4E3E17; font-size:54px; }
        .joint { stroke-width:5; stroke-dasharray:20 16; }
        .joint.ok { stroke:var(--ok); }
        .joint.bad { stroke:var(--pencil); stroke-width:9; stroke-dasharray:none; }
        .joint.faint { stroke:var(--soft); stroke-width:3; stroke-dasharray:14 14; opacity:.5; }
        .gutterline { stroke:#506B7D; stroke-width:26; }
        .wallband { fill:#9AA39C; stroke:#6E766F; stroke-width:5; }
        .sheathband { fill:none; stroke:#7C6031; stroke-width:6; stroke-dasharray:30 18; }
        .mem.header { fill:#8A5340; stroke:#2A2A2A; stroke-width:5; }
        .plyseam { stroke:#F2E9E2; stroke-width:4; stroke-dasharray:26 16; opacity:.75; }
        .rough { fill:var(--paper); stroke:var(--pencil); stroke-width:6; }
        .winframe { fill:#383E42; stroke:#22262A; stroke-width:5; }
        .glazing { fill:#8FB2C4; opacity:.55; stroke:#5B7F92; stroke-width:5; }
        .waste { fill:url(#hatch); stroke:var(--pencil); stroke-width:3; stroke-dasharray:14 12; }
        .furring { fill:#9D6A60; stroke:#5E3F39; stroke-width:5; }
        .skirtzone { fill:#BFAE9C; opacity:.55; stroke:#8C7A66; stroke-width:4; }
        /* Fixings sit under the dimensions and read as pencil ticks. Quiet by
           design: legible when you look for them, invisible when you aren't. */
        .fixings .fix { vector-effect:non-scaling-stroke; }
        .fix.sheet { fill:none; stroke:#4A5C6B; stroke-width:1; opacity:.4; }
        .fix.framing { stroke:#8A4634; stroke-width:1.25; opacity:.62; }
        .fix.noggin { stroke:#2F6B4F; stroke-width:1.25; opacity:.62; }
        .fix.hanger { fill:none; stroke:#3D4F8A; stroke-width:1.25; opacity:.7; }
        .sheet.ghosted { opacity:.45; }
        .toggles { display:flex; flex-wrap:wrap; gap:5px; }
        .toggle { font:inherit; font-size:13px; padding:6px 11px; cursor:pointer; background:transparent;
          color:var(--soft); border:1px solid var(--rule); border-radius:2px; }
        .toggle.on { background:var(--ok); color:#fff; border-color:var(--ok); }
        .assign { display:grid; grid-template-columns:auto 1fr; gap:8px 12px; align-items:center;
          font-size:13px; margin:6px 0 2px; }
        .assign span { color:var(--soft); font-size:12px; }
        .scroll { flex:1; overflow-y:auto; padding:20px 22px 44px; }
        table { width:100%; border-collapse:collapse; font-size:13.5px; margin-bottom:4px; }
        th, td { text-align:right; padding:7px 8px; border-bottom:1px solid var(--rule); }
        th:first-child, td:first-child { text-align:left; }
        th { font-weight:600; font-size:12px; color:var(--soft); }
        tfoot td { font-weight:600; border-bottom:none; }
        h2 { font-size:15px; font-weight:600; margin:28px 0 8px; }
        h2:first-child { margin-top:0; }
        .note { font-size:13px; color:var(--soft); line-height:1.55; max-width:70ch; margin:8px 0 0; }
        .flag { border-left:3px solid var(--pencil); padding:8px 0 8px 12px; margin:12px 0;
          font-size:13px; line-height:1.5; max-width:72ch; }
        .flag.good { border-left-color:var(--ok); }
        .bars { display:flex; flex-direction:column; gap:6px; margin:10px 0 4px; }
        .bar { display:flex; height:26px; border:1px solid var(--rule); background:var(--paper); }
        .barseg { border-right:1px solid #7E4E47; background:var(--timber); font-size:11px;
          display:flex; align-items:center; justify-content:center; color:#3A211D; overflow:hidden; }
        .barwaste { background:repeating-linear-gradient(45deg,transparent,transparent 4px,var(--rule) 4px,var(--rule) 5px);
          font-size:11px; display:flex; align-items:center; justify-content:center; color:var(--soft); }
        .legend { display:flex; gap:16px; flex-wrap:wrap; font-size:12px; color:var(--soft); padding:0 22px 14px; }
        .picked { color:var(--ink); font-weight:600; }
        .draw .batten { fill:#E08A00; fill-opacity:.55; stroke:#7A4A05; stroke-width:7; }
        .draw .batten.rail { fill:#B86A00; fill-opacity:.72; }
        .draw .offcutrect { fill:var(--paper); stroke:var(--line); stroke-width:6;
          stroke-dasharray:22 18; opacity:.85; }
        .swatch { display:inline-block; width:11px; height:11px; margin-right:6px; vertical-align:-1px; }
        .stack { display:flex; flex-direction:column; gap:3px; margin:10px 0 6px; max-width:420px; }
        .layer { display:flex; justify-content:space-between; align-items:center; padding:7px 11px;
          font-size:12.5px; border:1px solid var(--rule); }
        .layer b { font-weight:600; }
        .disclose { margin:22px 0 0; border-top:1px solid var(--rule); padding-top:12px; max-width:760px; }
        .disclose > summary { cursor:pointer; font-size:13px; font-weight:600; color:var(--soft);
          list-style:none; padding:4px 0; }
        .disclose > summary::-webkit-details-marker { display:none; }
        .disclose > summary::before { content:"+ "; }
        .disclose[open] > summary::before { content:"– "; }
        .disclose > summary:hover { color:var(--ink); }
        .iorow { display:flex; flex-wrap:wrap; gap:8px; margin:12px 0; }
        .iorow .navbtn { padding:8px 16px; }
        .iorow .navbtn:disabled { opacity:.4; cursor:default; }
        .iobox { width:100%; max-width:760px; min-height:120px; font-family:ui-monospace,SFMono-Regular,
          Menlo,monospace; font-size:11.5px; line-height:1.45; padding:10px; resize:vertical;
          background:var(--paper); color:var(--ink); border:1px solid var(--rule); border-radius:2px; }
        .pills { display:flex; flex-wrap:wrap; gap:5px; margin-top:8px; }
        .pill { font-size:12px; padding:4px 9px; border:1px solid var(--rule); color:var(--soft); }
        @media (prefers-reduced-motion:reduce) { * { animation:none !important; transition:none !important; } }
      `}</style>

      <header className="masthead">
        <div className="topline">
          <h1>Garden pod — floor &amp; warm roof</h1>
          <button className={cost.total > 0 ? "costtag has" : "costtag"}
            onClick={() => setNav("materials")}
            title={cost.unpriced.length
              ? `${cost.unpriced.length} item(s) with no price yet`
              : "Every item priced"}>
            <span className="costnum">{money(cost.total)}</span>
            <span className="costsub">
              {cost.unpriced.length ? `${cost.unpriced.length} unpriced` : "materials"}
            </span>
          </button>
          <div className="nav">
            {[["start", "Start"], ["floor", "Floor"], ["walls", "Walls"], ["roof", "Roof"], ["energy", "Energy"], ["materials", "Settings"]].map(([k, l]) => (
              <button key={k} className={nav === k ? "navbtn on" : "navbtn"} onClick={() => setNav(k)}>
                {l}
              </button>
            ))}
          </div>
        </div>

        {["floor", "walls", "roof"].includes(nav) && (
          <div className="visbar">
            <span className="visbar-lab">Show</span>
            {LAYERS.filter((l) => l.depth <= VIEW_DEPTH[nav]).map((l) => (
              <button key={l.id} className={shown(l.id) ? "vis on" : "vis"}
                aria-pressed={shown(l.id)} onClick={() => toggleLayer(l.id)}>
                {l.label}
              </button>
            ))}
            <span className="visbar-sep" />
            {[["solid", "Solid"], ["ghost", "See-through"]].map(([k, lab]) => (
              <button key={k} className={renderMode === k ? "vis mode on" : "vis mode"}
                aria-pressed={renderMode === k} onClick={() => setRenderMode(k)}>
                {lab}
              </button>
            ))}
            <span className="visbar-sep" />
            <span className="visbar-lab">Colour</span>
            {[["material", "Material"], ["wall", "Per wall"], ["layer", "Per layer"]].map(([k, lab]) => (
              <button key={k} className={colourBy === k ? "vis mode on" : "vis mode"}
                aria-pressed={colourBy === k} onClick={() => setColourBy(k)}>
                {lab}
              </button>
            ))}
            <span className="visbar-sep" />
            <span className="visbar-lab" title={nav === "walls"
              ? "Pushes the walls apart and spreads each wall's own studs, plates and noggins"
              : nav === "roof" ? "Lifts the roof layers apart"
                : "Lifts the deck off the joists"}>Explode</span>
            <input className="exslider" type="range" min="0" max="600" step="10"
              value={explode} aria-label="Explode"
              onChange={(e) => setExplode(Number(e.target.value))} />
            <span className="exval">{explode ? `${fmt(explode)} mm` : "off"}</span>
            {explode > 0 && (
              <button className="linkbtn" onClick={() => setExplode(0)}>Reset</button>
            )}
            <span className="visbar-sep" />
            <button className="linkbtn" onClick={() => setShow(ALL_LAYERS)}>All</button>
            <button className="linkbtn" onClick={() => setShow(FRAMING_ONLY)}>Framing only</button>
          </div>
        )}

        {nav === "floor" && (
          <div className="readout">
            <div><b>{fmt(fL)} × {fmt(fW)}</b> <span>footprint</span></div>
            <div><b>{floor.frame.n}</b> <span>joists</span></div>
            <div><b>{fSheets.bought}</b> <span>sheets</span></div>
            <div><b>{fmt(floor.frame.maxBay)}</b> <span>widest bay</span></div>
            <div title="Sheet joints with no joist or rim underneath them">
              <b>{floor.support.unsupported.length}</b>{" "}
              <span>{floor.support.unsupported.length === 1
                ? "joint off a joist" : "joints off a joist"}</span>
            </div>
          </div>
        )}
        {nav === "walls" && (
          <div className="readout">
            <div><b>{fmt(ceilingHeight)}</b> <span>internal height</span></div>
            <div><b>{walls.walls.reduce((s, w) => s + w.frame.n + 2, 0)}</b> <span>studs</span></div>
            <div><b>{wSheets.bought}</b> <span>sheets</span></div>
            <div><b>{m2(walls.sheathArea)} m²</b> <span>sheathing</span></div>
            <div><b>{fmt(groundClear)}</b> <span>ground gap</span></div>
            <div><b>{fmt(heights.overall)}</b> <span>overall height</span></div>
            <div title="Vertical sheathing joints with no stud behind them">
              <b>{walls.unsupported}</b>{" "}
              <span>{walls.unsupported === 1 ? "joint off a stud" : "joints off a stud"}</span>
            </div>
            <div title="Horizontal sheathing joints with no noggin behind them">
              <b>{walls.rowUnsupported}</b>{" "}
              <span>
                {walls.rowUnsupported === 1 ? "joint off a noggin" : "joints off a noggin"}
              </span>
            </div>
          </div>
        )}
        {nav === "roof" && (
          <div className="readout">
            <div><b>{fmt(roof.L)} × {fmt(roof.W)}</b> <span>roof footprint</span></div>
            <div><b>{roof.frame.n}</b> <span>joists</span></div>
            <div><b>{rSheets.bought + tSheets.bought}</b> <span>OSB sheets</span></div>
            <div><b>{pirFor("Roof insulation").bought}</b> <span>PIR boards</span></div>
            <div><b>{fmt(roof.epdm.l)} × {fmt(roof.epdm.w)}</b> <span>EPDM</span></div>
            <div><b>{fmt(heights.overall)}</b> <span>overall height</span></div>
          </div>
        )}
        {nav === "energy" && (
          <div className="readout">
            <div><b>{energy.envelope.u.toFixed(2)}</b> <span>W/m²K envelope</span></div>
            <div><b>{energy.envelope.area.toFixed(1)} m²</b> <span>envelope area</span></div>
            <div><b>{r(energy.loads.heating.total)} W</b> <span>peak heating</span></div>
            <div><b>{r(Math.max(0, energy.loads.cooling.total))} W</b> <span>peak cooling</span></div>
            <div><b>{r(energy.loads.heating.perM2)} W/m²</b> <span>of floor</span></div>
          </div>
        )}
        {nav === "materials" && (
          <div className="readout">
            <div>
              <b>{[...alloc.osb.values()].reduce((s, g) => s + g.bought, 0)}</b> <span>OSB sheets</span>
            </div>
            <div>
              <b>{[...alloc.osb.values()].reduce((s, g) => s + g.reused, 0)}</b> <span>pieces from offcuts</span>
            </div>
            <div><b>{timber.reduce((s, t) => s + t.bars.length, 0)}</b> <span>timber lengths</span></div>
            <div>
              <b>{timber.reduce((s, t) => s + t.linearM, 0).toFixed(1)} m</b> <span>timber</span>
            </div>
          </div>
        )}
      </header>

      <div className="body">
        {nav !== "start" && (
        <aside className="rail">
          {nav === "floor" && (
            <>
              <h3>Floor</h3>
              <Field label="Footprint, length × width (mm)"
                hint="Outside face to outside face. Press Enter or click away to apply.">
                <div className="pair">
                  <NumberField value={fL} onCommit={setFL} min={500} max={12000} ariaLabel="Floor length" />
                  <NumberField value={fW} onCommit={setFW} min={500} max={8000} ariaLabel="Floor width" />
                </div>
              </Field>
              <Field label="Joists" hint="Sections come from the Materials list.">
                <select value={fSectionId} onChange={(e) => setFSectionId(e.target.value)}>
                  {enabled.map((id) => {
                    const s = allSections.find((x) => x.id === id);
                    return <option key={id} value={id}>{s.label} · nominal {s.nominal}</option>;
                  })}
                </select>
              </Field>
              <Field label={spacingMode === "clear" ? "Clear bay between joists" : "Maximum joist centres"}
                hint={`Widest bay ${fmt(floor.frame.maxBay)} mm centre to centre, narrowest ${fmt(floor.frame.narrowestBay)} mm clear.`}>
                <Chips value={fSpacing} set={setFSpacing}
                  opts={[300, 400, 600].map((v) => ({ value: v, label: String(v) }))} />
              </Field>
              {floor.frame.narrowestBay < 100 && (
                <div className="flag">
                  The narrowest bay is {fmt(floor.frame.narrowestBay)} mm. That is the sheet
                  layout, not the framing: a joint has landed close to the end, and a
                  joint-aligned joist follows it. Nothing cuts to fill a strip that
                  narrow. Try Equal pieces under Sheet alignment, or Even centres under
                  Joist layout if you would rather the bays were uniform than under
                  every joint.
                </div>
              )}
              <Field label="Joist layout"
                hint="Joint-aligned puts a joist on every sheet edge, then infills so no bay exceeds the maximum.">
                <Chips value={fJoist} set={setFJoist}
                  opts={[{ value: "deck", label: "Under sheet joints" }, { value: "even", label: "Even centres" }]} />
              </Field>
              <Field label="Perimeter" hint="Double rim adds a full-length outer skin to each long side.">
                <Chips value={fDouble} set={setFDouble}
                  opts={[{ value: true, label: "Double rim" }, { value: false, label: "Single rim" }]} />
              </Field>
              <Field label="Noggin rows">
                <Chips value={fNoggins} set={setFNoggins}
                  opts={[{ value: 0, label: "None" }, { value: 1, label: "One" }, { value: 2, label: "Two" }]} />
              </Field>

              <h3>Floor deck</h3>
              <Field label="Thickness (mm)" hint={`${floorBoard.label} sheets, set in Materials.`}>
                <Chips value={fDeckT} set={setFDeckT} opts={OSB_T.map((v) => ({ value: v, label: String(v) }))} />
              </Field>
              <Field label="Sheet direction"
                hint={floor.deck.alternative
                  ? `Auto picked ${floor.deck.orient === "along" ? "long side along the length" : "long side across the width"} — ${floor.deck.cuts} cuts against ${floor.deck.alternative.cuts} the other way.`
                  : "Locked to one direction."}>
                <Chips value={fOrient} set={setFOrient} opts={orientOpts} />
              </Field>
              <Field label="Sheet alignment"
                hint="One end runs full sheets from the start and leaves a single remainder.">
                <Chips value={fAlign} set={setFAlign}
                  opts={[{ value: "auto", label: "Auto" }, { value: "centre", label: "Centre out" },
                         { value: "edge", label: "One end" }, { value: "equal", label: "Equal pieces" }]} />
              </Field>

            </>
          )}

          {nav === "walls" && (
            <>
              <h3>Height</h3>
              <Field label="Internal ceiling height (mm)"
                hint={`Top of the floor OSB up to the underside of the roof joists. Sheathing runs ${fmt(walls.skirt)} mm lower to cover the floor frame, so the sheathed face is ${fmt(walls.sheathH)} mm.`}>
                <NumberField value={ceilingHeight} onCommit={setCeilingHeight} min={1500} max={4000}
                  step={50} ariaLabel="Internal ceiling height" />
              </Field>
              <h3>Framing</h3>
              <Field label="Which walls run through"
                hint={`Front and rear are ${fmt(walls.walls[0].len)} mm, sides ${fmt(walls.walls[2].len)} mm.`}>
                <Chips value={wLayout} set={setWLayout}
                  opts={[{ value: "lengthThrough", label: "Length walls" },
                         { value: "widthThrough", label: "Width walls" }]} />
              </Field>
              <Field label="Sheathing laps over"
                hint="Whichever pair goes on second runs long at each end to cover the other's edge and close the corner.">
                <Chips value={wLap} set={setWLap}
                  opts={[{ value: "lengthOver", label: "Front & rear" },
                         { value: "widthOver", label: "Sides" }]} />
              </Field>
              <Field label="Studs" hint={`Wall is ${walls.wt} mm thick. Sections come from Materials.`}>
                <select value={wSectionId} onChange={(e) => setWSectionId(e.target.value)}>
                  {enabled.map((id) => {
                    const s = allSections.find((x) => x.id === id);
                    return <option key={id} value={id}>{s.label} · nominal {s.nominal}</option>;
                  })}
                </select>
              </Field>
              <Field label="Maximum stud centres">
                <Chips value={wSpacing} set={setWSpacing}
                  opts={[300, 400, 600].map((v) => ({ value: v, label: String(v) }))} />
              </Field>
              <Field label="Narrowest bay (mm)"
                hint={(() => {
                  const dropped = walls.walls.reduce((n, w) => n + w.frame.droppedStuds.length, 0);
                  return dropped
                    ? `${dropped} stud${dropped === 1 ? "" : "s"} dropped for landing closer than this to a king stud or a wall end — a king stud is a full-height stud, so doubling up there buys a sliver you cannot insulate. Set it to 0 to keep every stud.`
                    : "A common stud landing closer than this to a king stud or a wall end is dropped, rather than leaving a bay too narrow to cut insulation for. 0 keeps every stud.";
                })()}>
                <NumberField value={wMinBay} onCommit={setWMinBay} min={0} max={600}
                  step={25} ariaLabel="Narrowest bay" />
              </Field>
              <Field label="Stud layout"
                hint="Joint-aligned puts a stud behind every vertical sheathing joint.">
                <Chips value={wStudAlign} set={setWStudAlign}
                  opts={[{ value: "deck", label: "Under sheet joints" }, { value: "even", label: "Even centres" }]} />
              </Field>
              <Field label="Noggin rows"
                hint={(() => {
                  /* A glazed wall reports no rows at all, so read the first
                     wall that has some rather than whichever comes first. */
                  const rows = (walls.walls.find((w) => (w.frame.nogginRowY || []).length)
                    || walls.walls[0]).frame.nogginRowY || [];
                  if (wStudAlign === "deck" && rows.length)
                    return `Sitting under the horizontal sheathing joints rather than at even heights — ${rows.length} row${rows.length === 1 ? "" : "s"} at ${rows.map((y) => fmt(y)).join(", ")} mm, whatever this is set to. A joint with no noggin behind it is a sheet edge you cannot fix.`;
                  return wStudAlign === "deck"
                    ? "The sheathing runs in one piece here, so there is no joint to sit under and the rows divide the wall evenly."
                    : "Even heights. Switch Stud layout to Under sheet joints and the rows move to back the horizontal joints instead.";
                })()}>
                <Chips value={wNoggins} set={setWNoggins}
                  opts={[{ value: 0, label: "None" }, { value: 1, label: "One" },
                         { value: 2, label: "Two" }, { value: 3, label: "Three" }]} />
              </Field>
              {walls.rowUnsupported > 0 && (
                <div className="flag">
                  {walls.rowUnsupported} horizontal sheathing joint
                  {walls.rowUnsupported === 1 ? " has" : "s have"} no noggin behind
                  {walls.rowUnsupported === 1 ? " it" : " them"}.{" "}
                  {wNoggins === 0
                    ? "Noggin rows are off, so nothing is backing them."
                    : wStudAlign === "deck"
                      ? "The rows are already following the joints, so these are ones no row can reach — too near a plate, or pushed out by the opening."
                      : "Set Stud layout to Under sheet joints and the rows will move to meet them."}
                </div>
              )}

              <h3>Window</h3>
              <Field label="Opening">
                <Chips value={winOn} set={setWinOn}
                  opts={[{ value: true, label: "One window" }, { value: false, label: "None" }]} />
              </Field>
              {winOn && (
                <>
                  <Field label="Style"
                    hint={winStyle === "glazedWall"
                      ? "Header runs the full wall and replaces the top plate. Two jack studs at the ends, cripples below, nothing above."
                      : "Trimmers, king studs, header and cripples above and below."}>
                    <Chips value={winStyle} set={setWinStyle}
                      opts={[{ value: "standard", label: "Standard" },
                             { value: "glazedWall", label: "Glazed wall" }]} />
                  </Field>
                  <Field label="Wall">
                    <Chips value={winSide} set={setWinSide}
                      opts={SIDES.map((s) => ({ value: s.id, label: s.label }))} />
                  </Field>
                  {winStyle === "glazedWall" ? (
                    <>
                      <Field label="Set the opening by"
                        hint={glazedFix === "max"
                          ? "Head tight under the header, sill just clear of the plate — glass as tall as the wall allows."
                          : glazedFix === "window"
                            ? "Window height is yours; the sill follows the header. A shallower header lifts the opening and lengthens the studs below."
                            : "Sill height is yours; the window height is whatever is left under the header."}>
                        <Chips value={glazedFix} set={setGlazedFix}
                          opts={[{ value: "max", label: "Tallest" },
                                 { value: "window", label: "Window height" },
                                 { value: "sill", label: "Sill height" }]} />
                      </Field>
                      <Field label="Window width"
                        hint="Set by the wall between the jack studs. Size the building to suit it below.">
                        <div className="assign">
                          <span>Width</span>
                          <b>{glazedWall ? `${fmt(glazedWall.frame.opening.winW)} mm` : "—"}</b>
                        </div>
                      </Field>
                      {glazedFix === "window" ? (
                        <>
                          <Field label="Window height (mm)"
                            hint={glazedWall
                              ? `Sill works out at ${fmt(glazedWall.frame.opening.sill)} mm. Maximum height here is ${fmt(glazedWall.frame.opening.maxHeight)} mm.`
                              : ""}>
                            <NumberField value={winH} onCommit={setWinH} min={200} max={3000}
                              ariaLabel="Window height" />
                          </Field>
                        </>
                      ) : (
                        <Field label="Window height"
                          hint={glazedFix === "max"
                            ? "Everything between the header and the lowest sill the wall allows."
                            : "Whatever the header leaves above the sill."}>
                          <div className="assign">
                            <span>Height</span>
                            <b>{glazedWall ? `${fmt(glazedWall.frame.opening.winH)} mm` : "—"}</b>
                          </div>
                        </Field>
                      )}
                    </>
                  ) : (
                    <Field label="Window size, width × height (mm)">
                      <div className="pair">
                        <NumberField value={winW} onCommit={setWinW} min={200} max={4000} ariaLabel="Window width" />
                        <NumberField value={winH} onCommit={setWinH} min={200} max={3000} ariaLabel="Window height" />
                      </div>
                    </Field>
                  )}
                  {winStyle === "glazedWall" && glazedWall && (
                    <Field label="Size the building to a window width (mm)"
                      hint={`Sets the ${glazedWall.axis === "x" ? "length" : "width"} so the glazing comes out at exactly this.`}>
                      <div className="pair">
                        <NumberField value={winW} onCommit={setWinW} min={200} max={6000} ariaLabel="Target window width" />
                        <button className="chip" style={{ whiteSpace: "nowrap" }}
                          onClick={() => fitWallToWindow(winW)}>
                          Fit wall
                        </button>
                      </div>
                    </Field>
                  )}
                  {!(winStyle === "glazedWall" && glazedFix !== "sill") && (
                  <Field label="Sill height above floor (mm)"
                    hint={walls.opened && walls.opened.frame.opening
                      ? `Header needs the sill at ${fmt(walls.opened.frame.opening.maxSill)} mm or lower here.`
                      : ""}>
                    <NumberField value={winSill} onCommit={setWinSill} min={0} max={2500}
                      step={25} ariaLabel="Sill height" />
                  </Field>
                  )}
                  <Field label="Gap around window (mm)"
                    hint="Rough opening is the window plus this all round, for packers and adjustment.">
                    <NumberField value={winGap} onCommit={setWinGap} min={0} max={30} step={1}
                      ariaLabel="Gap around window" />
                  </Field>
                  {winStyle === "standard" && (
                  <Field label="Position along wall">
                    <Chips value={winCentred} set={setWinCentred}
                      opts={[{ value: true, label: "Centred" }, { value: false, label: "Set position" }]} />
                  </Field>
                  )}
                  {winStyle === "standard" && !winCentred && (
                    <Field label="Centre from left end (mm)">
                      <NumberField value={winCentre} onCommit={setWinCentre} min={0} max={12000}
                        step={25} ariaLabel="Window centre" />
                    </Field>
                  )}
                  <Field label="Header section"
                    hint={glazedWall
                      ? `${glazedWall.frame.opening.plies} plies of ${headerSection.label} = ${fmt(glazedWall.frame.opening.plyTotal)} mm in an ${walls.wt} mm wall${glazedWall.frame.opening.plyShort ? `, ${fmt(glazedWall.frame.opening.plyShort)} mm to pack out` : ""}.`
                      : "Laminated to fill the wall depth."}>
                    <select value={headerId} onChange={(e) => setHeaderId(e.target.value)}>
                      {enabled.map((id) => {
                        const s = allSections.find((x) => x.id === id);
                        return <option key={id} value={id}>{s.label}</option>;
                      })}
                    </select>
                  </Field>
                  <Field label="Frame depth × face width (mm)"
                    hint={`${winSizes
                      ? `${fmt(winSizes.frameW)} × ${fmt(winSizes.frameH)} frame gives ${fmt(winSizes.glassW)} × ${fmt(winSizes.glassH)} of glass. Frame finishes flush with the outside of the sheathing. `
                      : "Depth is what sits in the wall; face width is the visible frame. "}Depth is shared with the door; the face width here is the window's, and the door has its own.`}>
                    <div className="pair">
                      <NumberField value={frameDepth} onCommit={setFrameDepth} min={20} max={200}
                        step={5} ariaLabel="Frame depth" />
                      <NumberField value={frameWidth} onCommit={setFrameWidth} min={10} max={150}
                        step={5} ariaLabel="Frame face width" />
                    </div>
                  </Field>
                  <Field label="Header laid"
                    hint={`The window's; the door has its own. On edge gives a ${fmt(headerSection.d)} mm deep header off a ${fmt(headerSection.t)} mm face; flat gives ${fmt(headerSection.t)} mm deep off ${fmt(headerSection.d)} mm. Whichever face runs across the wall gets laminated to fill its ${fmt(walls.wt)} mm.`}>
                    <Chips value={headerOnEdge} set={setHeaderOnEdge}
                      opts={[{ value: true, label: "On edge" }, { value: false, label: "Flat" }]} />
                  </Field>
                </>
              )}

              <h3>Door</h3>
              <Field label="Opening">
                <Chips value={doorOn} set={setDoorOn}
                  opts={[{ value: true, label: "One door" }, { value: false, label: "None" }]} />
              </Field>
              {doorOn && (
                <>
                  <Field label="Wall"
                    hint={walls.walls.some((w) => w.clash)
                      ? "A door and a window are on the same wall. Only one opening per wall is modelled, so the door wins — move one of them."
                      : undefined}>
                    <Chips value={doorSide} set={setDoorSide}
                      opts={SIDES.map((s) => ({ value: s.id, label: s.label }))} />
                  </Field>
                  <Field label="Header"
                    hint={doorFullHeader
                      ? "Runs the full wall and replaces the top plate, sitting on the furring plane between the other two walls. Studs and trimmers stop under it, and the door reaches its underside."
                      : "Sits above the opening, with cripples or a deepened header up to the top plate."}>
                    <Chips value={doorFullHeader} set={setDoorFullHeader}
                      opts={[{ value: false, label: "Above the opening" },
                             { value: true, label: "Full width" }]} />
                  </Field>
                  <Field label="Header laid"
                    hint={`Set separately from the window's. On edge gives a ${fmt(headerSection.d)} mm deep header off a ${fmt(headerSection.t)} mm face; flat gives ${fmt(headerSection.t)} mm deep off ${fmt(headerSection.d)} mm. A deeper header eats headroom above the door; a flat one leaves more.${doorHeaderOnEdge === headerOnEdge ? "" : " Currently different from the window's."}`}>
                    <Chips value={doorHeaderOnEdge} set={setDoorHeaderOnEdge}
                      opts={[{ value: true, label: "On edge" }, { value: false, label: "Flat" }]} />
                  </Field>
                  <>
                      <Field label={doorFullHeader
                        ? "Door width (mm) — height is set by the header"
                        : "Door size, width × height (mm)"}>
                        <div className="pair">
                          <NumberField value={doorW} onCommit={setDoorW} min={400} max={4000}
                            ariaLabel="Door width" />
                          {!doorFullHeader && (
                            <NumberField value={doorH} onCommit={setDoorH} min={900} max={3000}
                              ariaLabel="Door height" />
                          )}
                        </div>
                      </Field>
                      <Field label="Set out from">
                        <Chips value={doorFrom} set={setDoorFrom}
                          opts={[{ value: "start", label: "One end" },
                                 { value: "end", label: "Other end" },
                                 { value: "centre", label: "Centred" }]} />
                      </Field>
                      {doorFrom !== "centre" && (
                        <Field label="Distance from that end (mm)"
                          hint={walls.doored
                            ? `Opening runs ${fmt(walls.doored.frame.opening.x0)}–${fmt(walls.doored.frame.opening.x1)} mm along a ${fmt(walls.doored.len)} mm wall.`
                            : ""}>
                          <NumberField value={doorOffset} onCommit={setDoorOffset} min={0}
                            max={8000} step={25} ariaLabel="Door offset" />
                        </Field>
                      )}
                  </>
                  {(() => {
                    const w = walls.doored;
                    if (!w || !wMinBay || w.frame.narrowestBay >= wMinBay) return null;
                    return (
                      <div className="flag">
                        The tightest bay on this wall is {fmt(w.frame.narrowestBay)} mm,
                        under the {fmt(wMinBay)} mm you asked for. The studs that could be
                        dropped already have been — what is left is the strip between the
                        door's trimmer and the wall end, and that trimmer is what the door
                        fixes to. Move the door further along, or accept foam there.
                      </div>
                    );
                  })()}
                  <Field label="Gap around door (mm)"
                    hint="Rough opening is the door plus this all round.">
                    <NumberField value={doorGap} onCommit={setDoorGap} min={0} max={30} step={1}
                      ariaLabel="Gap around door" />
                  </Field>
                  <Field label="Leaf">
                    <Chips value={doorSolid} set={setDoorSolid}
                      opts={[{ value: false, label: "Glazed" }, { value: true, label: "Solid" }]} />
                  </Field>
                  <Field label="Frame face width (mm)"
                    hint={`${doorSizes
                      ? `${fmt(doorSizes.frameW)} × ${fmt(doorSizes.frameH)} frame gives a ${fmt(doorSizes.glassW)} × ${fmt(doorSizes.glassH)} ${doorSolid ? "leaf" : "glazed panel"}. `
                      : ""}The door's own — a door liner is usually heavier than a window's bead. Depth is shared with the window at ${fmt(frameDepth)} mm, since the wall sets it.${doorFrameWidth === frameWidth ? "" : " Currently different from the window's."}`}>
                    <NumberField value={doorFrameWidth} onCommit={setDoorFrameWidth}
                      min={10} max={150} step={5} ariaLabel="Door frame face width" />
                  </Field>
                </>
              )}

              <h3>Sheathing</h3>
              <Field label="Thickness (mm)"
                hint={`${sheathBoard.label} sheets. Sides are cut to the exact face length; front and rear overhang ${wSheathT} mm at each end to lap the side edges.`}>
                <Chips value={wSheathT} set={setWSheathT} opts={OSB_T.map((v) => ({ value: v, label: String(v) }))} />
              </Field>
              <Field label="Sheet direction"
                hint={(() => {
                  const w = walls.walls[0];
                  return `${sideLabel(w.side)} wall: ${w.plan.pieces.length} pieces, ${w.plan.slivers} narrower than 300 mm, narrowest ${fmt(w.plan.narrowest)} mm.`;
                })()}>
                <Chips value={wSheathOrient} set={setWSheathOrient} opts={orientOpts} />
              </Field>
              <Field label="At the openings"
                hint={(() => {
                  const holes = walls.walls.reduce((n, w) => n + (w.holePieces || []).length, 0);
                  const area = walls.walls.reduce((n, w) =>
                    n + (w.holePieces || []).reduce((t, x) => t + x.dx * x.dz, 0), 0);
                  const alt = alloc.sheathAlt;
                  const now = alloc.sheathNow;
                  const diff = alt == null || now == null ? null : alt - now;
                  const pile = `${now} sheet${now === 1 ? "" : "s"} of ${wSheathT} mm in the pile, shared with the roof's top deck`;
                  const cost = diff == null || diff === 0
                    ? `${pile}; the other way comes out at the same.`
                    : diff > 0
                      ? `${pile}; the other way would buy ${diff} more.`
                      : `${pile}; the other way would buy ${-diff} fewer.`;
                  return wSheathCut === "over"
                    ? `Run the board over the opening and cut it out after — the easier way to work${holes ? `, and it throws ${m2(area)} m² away in ${holes} piece${holes === 1 ? "" : "s"}` : ""}. A cut-out is scrap, not stock: cut freehand against the frame it is rough on every edge, and squaring it up costs more board than it saves. ${cost}`
                    : `Sheets are planned around the opening, so no board is cut away — but every piece over and under it is one more to cut and fit. ${cost}`;
                })()}>
                <Chips value={wSheathCut} set={setWSheathCut}
                  opts={[{ value: "over", label: "Sheet over, cut out" },
                         { value: "around", label: "Piece around" }]} />
              </Field>
              <Field label="Sheet alignment"
                hint="Equal pieces divides each face into the fewest same-sized panels that fit — more sheet used, but no narrow ribbons to fix.">
                <Chips value={wSheathAlign} set={setWSheathAlign}
                  opts={[{ value: "auto", label: "Auto" }, { value: "centre", label: "Centre out" },
                         { value: "edge", label: "One end" }, { value: "equal", label: "Equal pieces" }]} />
              </Field>

              <h3>Breather membrane</h3>
              <Field label="Wrap the sheathing"
                hint={!wrap
                  ? "Off. The sheathing is left bare behind the battens."
                  : wrap.impossible
                    ? "The lap is not smaller than the roll, so every course would start where the last one did."
                    : `${wrap.n} course${wrap.n === 1 ? "" : "s"} round a ${(wrap.perimeter / 1000).toFixed(2)} m perimeter, ${(wrap.runM / 1000).toFixed(1)} m in ${wrap.rolls} roll${wrap.rolls === 1 ? "" : "s"}. Outside the sheathing and under the battens, laid bottom course first so each one laps over the one below.`}>
                <Chips value={wrapOn} set={setWrapOn}
                  opts={[{ value: true, label: "Wrapped" }, { value: false, label: "None" }]} />
              </Field>
              {wrap && (
                <>
                  <Field label="Roll width (mm)"
                    hint={`How tall one course is. ${(wrap.cover / 1000).toFixed(2)} m to cover — ${fmt(wrap.faceH)} mm of sheathed face plus the ${fmt(wrap.drop)} mm it hangs below — so a ${fmt(wrap.rollW)} mm roll takes ${wrap.n || "…"} course${wrap.n === 1 ? "" : "s"}.`}>
                    <Chips value={wrapRollW} set={setWrapRollW}
                      opts={WRAP_ROLL_W.map((v) => ({ value: v, label: String(v) }))} />
                  </Field>
                  <Field label="Lap between courses (mm)"
                    hint="How far each course comes down over the one below. It is also the closing lap where a course meets its own start, so it is added to the run once per course. Water runs down the outside, so the lap only works laid bottom first."
                    >
                    <NumberField value={wrapOverlap} onCommit={setWrapOverlap} min={50}
                      max={600} step={25} ariaLabel="Membrane lap" />
                  </Field>
                  {wrap.impossible && (
                    <div className="flag">
                      A {fmt(wrapOverlap)} mm lap on a {fmt(wrapRollW)} mm roll leaves nothing
                      to climb by — each course would start exactly where the last one did.
                      The lap has to be smaller than the roll is wide.
                    </div>
                  )}
                  <Field label="Drop below the sheathing (mm)"
                    hint="How far the bottom course hangs below the bottom of the sheathing, so its edge sheds clear of the floor frame rather than onto it."
                    >
                    <NumberField value={wrapDrop} onCommit={setWrapDrop} min={0} max={200}
                      step={5} ariaLabel="Membrane drop" />
                  </Field>
                  {wrapDrop === 0 && (
                    <div className="flag">
                      With no drop the membrane stops level with the sheathing, which puts
                      the joint between them exactly where the water is running. Hang it
                      past the floor frame instead.
                    </div>
                  )}
                  <Field label="Roll length (m)"
                    hint={wrap.n
                      ? `${(wrap.runM / 1000).toFixed(1)} m of run — ${wrap.n} × (${(wrap.perimeter / 1000).toFixed(2)} m round plus a ${fmt(wrap.overlap)} mm closing lap) — comes to ${wrap.rolls} roll${wrap.rolls === 1 ? "" : "s"}.`
                      : "What you buy it in."}>
                    <NumberField value={wrapRollLen} onCommit={setWrapRollLen} min={5}
                      max={100} step={5} ariaLabel="Membrane roll length" />
                  </Field>
                  {wrap.n > 0 && (
                    <>
                      <p className="hint">
                        {wrap.courses.map((c, i) => (
                          <span key={i} className="tapesum">
                            <b>{i + 1}</b> {fmt(c.at - wrap.drop)} → {fmt(c.top - wrap.drop)}
                            {c.trimmed ? " (trimmed)" : ""}
                          </span>
                        ))}
                      </p>
                      <h3>Membrane lap tape</h3>
                      <Field label="Tape width (mm)"
                        hint={`Its own tape, not the foil tape over the PIR. ${(wrap.tape.total / 1000).toFixed(1)} m in all: ${(wrap.tape.lap / 1000).toFixed(1)} m along the ${wrap.n - 1} horizontal lap${wrap.n - 1 === 1 ? "" : "s"}, ${(wrap.tape.join / 1000).toFixed(1)} m of vertical joins — one closing each course, plus one wherever a roll ran out — and ${(wrap.tape.opening / 1000).toFixed(1)} m back to the frames where it is cut at an opening.`}>
                        <NumberField value={wrapTapeW} onCommit={setWrapTapeW} min={25}
                          max={150} step={5} ariaLabel="Membrane tape width" />
                      </Field>
                      <Field label="Tape roll length (m)"
                        hint={`${(wrap.tape.total / 1000).toFixed(1)} m comes to ${wrap.tape.rolls} roll${wrap.tape.rolls === 1 ? "" : "s"}.`}>
                        <NumberField value={wrapTapeRoll} onCommit={setWrapTapeRoll} min={5}
                          max={100} step={5} ariaLabel="Membrane tape roll length" />
                      </Field>
                      <p className="hint">
                        <span className="tapesum">
                          <b>{(wrap.tape.lap / 1000).toFixed(1)} m</b> laps
                        </span>
                        <span className="tapesum">
                          <b>{(wrap.tape.join / 1000).toFixed(1)} m</b> joins
                        </span>
                        <span className="tapesum">
                          <b>{(wrap.tape.opening / 1000).toFixed(1)} m</b> openings
                        </span>
                        <span className="tapesum">
                          <b>{wrap.tape.rolls} roll{wrap.tape.rolls === 1 ? "" : "s"}</b> to buy
                        </span>
                      </p>
                    </>
                  )}
                </>
              )}
              <h3>Cladding</h3>
              <Field label="Clad the walls"
                hint={!clad
                  ? "Off. The battens are bare."
                  : clad.count === 0
                    ? "No side selected, so nothing to clad."
                    : `${clad.count} boards on ${clad.sides.length} side${clad.sides.length === 1 ? "" : "s"}, ${(clad.linear / 1000).toFixed(1)} m cut, covering ${m2(clad.area)} m². ${clad.ripped ? `${clad.ripped} ripped to width at the top. ` : ""}${clad.short ? `${clad.short} short piece${clad.short === 1 ? "" : "s"} round the openings, ${clad.shortFromOffcut} of them off the tail of a length already cut. ` : ""}${clad.angled ? `${clad.angled} cut to the ${clad.degrees}° fall at the top — set the saw once and work round.` : ""}`}>
                <Chips value={cladOn} set={setCladOn}
                  opts={[{ value: true, label: "Clad" }, { value: false, label: "None" }]} />
              </Field>
              <Field label="Which way the boards run"
                hint={cladding === "vertical"
                  ? "Vertical boards fix to horizontal battens, and those need verticals underneath or the drainage path is dammed at every rail — so it is two batten layers, set below."
                  : "Horizontal boards fix to vertical battens: one layer, and water that gets behind the cladding runs straight down and out."}>
                <Chips value={cladding} set={setCladding}
                  opts={[{ value: "horizontal", label: "Horizontal" },
                         { value: "vertical", label: "Vertical" }]} />
              </Field>
              {clad && (
                <>
                  <Field label="Sides to clad"
                    hint={clad.sides.length === 4
                      ? "All four. Drop one for a feature wall in another material, or for a wall tight against a boundary that nobody will ever see or reach — cladding that is money on a surface nobody looks at."
                      : `${clad.sides.map((x) => sideLabel(x)).join(", ")}. The rest are still built and still drawn, just bare.`}>
                    <MultiChips values={cladSides}
                      opts={SIDES.map((x) => ({ value: x.id, label: x.label }))}
                      toggle={(id) =>
                        setCladSides((cur) =>
                          cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id])} />
                  </Field>
                  <Field label="Board face × thickness (mm)"
                    hint={`What shows, and how proud it stands. The face is not the cover — see below.`}>
                    <div className="pair">
                      <NumberField value={cladFace} onCommit={setCladFace} min={50} max={300}
                        step={1} ariaLabel="Cladding face width" />
                      <NumberField value={cladT} onCommit={setCladT} min={10} max={40}
                        step={1} ariaLabel="Cladding thickness" />
                    </div>
                  </Field>
                  <Field label="Shadow gap (mm)"
                    hint={`The tongue is left longer than the groove is deep, so the boards cannot close up and a square groove is left between every pair. That makes the cover ${fmt(clad.cover)} mm, not ${fmt(cladFace)} — ${clad.per[0] ? clad.per[0].courses : 0} courses on the ${sideLabel(clad.per[0] ? clad.per[0].side : "rear").toLowerCase()} wall. Count coverage off the face and you under-order.`}>
                    <NumberField value={cladGap} onCommit={setCladGap} min={0} max={30}
                      step={1} ariaLabel="Shadow gap" />
                  </Field>
                  <Field label="Stock length you can get (m)"
                    hint={clad.over
                      ? `A board ${fmt(clad.overLen)} mm long will not come off a ${cladStock} m length, and a cladding board spliced mid-wall is a different building. You need ${(clad.minStock / 1000).toFixed(2)} m at least.`
                      : `${clad.lengths} lengths, ${(clad.bought / 1000).toFixed(1)} m bought against ${(clad.linear / 1000).toFixed(1)} m cut — ${clad.wastePct}% off the saw and in tails.`}>
                    <NumberField value={cladStock} onCommit={setCladStock} min={1.8} max={6}
                      step={0.3} ariaLabel="Cladding stock length" />
                  </Field>
                  {clad.over > 0 && (
                    <div className="flag">
                      {clad.over} board{clad.over === 1 ? "" : "s"} longer than the stock —{" "}
                      {fmt(clad.overLen)} mm against {fmt(clad.stock)} mm. Those are not on
                      the order, because there is nothing to cut them from. Use{" "}
                      {(clad.minStock / 1000).toFixed(2)} m or longer.
                    </div>
                  )}
                  {clad.best && clad.best.stock !== clad.stock
                    && clad.best.bought < clad.bought && (
                    <div className="hint">
                      <b>{(clad.best.stock / 1000).toFixed(1)} m lengths would buy{" "}
                      {((clad.bought - clad.best.bought) / 1000).toFixed(1)} m less</b> —{" "}
                      {clad.best.lengths} of them against {clad.lengths}. Fewer lengths is
                      not less timber: a {fmt(clad.cuts[0] ? clad.cuts[0].len : 0)} mm board
                      off a long length leaves a tail no second board fits, so the long
                      stock buys more in fewer pieces.
                    </div>
                  )}
                  <p className="hint">
                    <span className="tapesum"><b>{clad.count}</b> boards</span>
                    <span className="tapesum"><b>{m2(clad.area)} m²</b> covered</span>
                    <span className="tapesum"><b>{(clad.linear / 1000).toFixed(1)} m</b> cut</span>
                    <span className="tapesum"><b>{clad.lengths}</b> lengths to buy</span>
                    {clad.fromOffcut > 0 && (
                      <span className="tapesum">
                        <b>{clad.fromOffcut}</b> cut off a tail
                      </span>
                    )}
                  </p>
                </>
              )}
              <h3>Cladding battens</h3>
              <Field label="Battens"
                hint={battens
                  ? `${battens.count} pieces, ${battens.linearM.toFixed(1)} m of ${battenSection.label}, ${battens.layers === 2 ? "in two layers" : "in one layer"}. Every vertical on a stud, widest gap between them ${fmt(battens.widestGap)} mm.`
                  : "Off. Battens carry the cladding off the sheathing so water that gets behind it can get out again."}>
                <Chips value={battenOn} set={setBattenOn}
                  opts={[{ value: true, label: "Battened" }, { value: false, label: "None" }]} />
              </Field>
              {battenOn && (
                <>
                  <p className="hint">
                    {cladding === "vertical"
                      ? "Vertical cladding, so two layers: horizontal rails for the boards to fix to, on verticals that keep the drainage path open. One rail hard at the bottom and one hard at the top."
                      : "Horizontal cladding, so one layer of verticals: the water runs straight down behind the boards and out."}{" "}
                    Set which way the boards run under Cladding above.
                  </p>
                  <Field label="Batten section"
                    hint={`Laid ${battenOnEdge ? "on edge" : "flat"}: ${battens ? fmt(battens.width) : ""} mm on the face to fix through, ${battens ? fmt(battens.proud) : ""} mm of drainage gap behind the cladding.`}>
                    <select value={battenId} onChange={(e) => setBattenId(e.target.value)}>
                      {enabled.map((id) => {
                        const x = allSections.find((y) => y.id === id);
                        return <option key={id} value={id}>{x.label}</option>;
                      })}
                    </select>
                  </Field>
                  <Field label="Laid">
                    <Chips value={battenOnEdge} set={setBattenOnEdge}
                      opts={[{ value: false, label: "Flat" }, { value: true, label: "On edge" }]} />
                  </Field>
                  <Field label={`${cladding === "vertical" ? "Vertical batten" : "Batten"} gap (mm)`}
                    hint={`Clear gap between battens — the span the cladding has to bridge. It is a maximum rather than a setting out, because every vertical lands on a stud, because a batten fixed in a bay is a batten fixed to ${wSheathT} mm of sheathing and the cladding hangs off it. One goes over the end stud at each corner and one either side of every opening with its face in the plane of the reveal, which puts it on the trimmer.${
                      battens && battens.widestGap > battenSpacing + 1
                        ? ` The studs are the limit here: ${fmt(battens.widestGap)} mm is the widest gap they allow at ${fmt(wSpacing)} mm centres.`
                        : ""}`}>
                    <NumberField value={battenSpacing} onCommit={setBattenSpacing} min={200}
                      max={1200} step={50} ariaLabel="Batten spacing" />
                  </Field>
                  <Field label="Set out"
                    hint={battenMode === "equal"
                      ? "Equal gaps: as even as landing on studs allows. Neither mode can divide a run exactly any more, but this one spreads the odd bay rather than leaving it all at one end."
                      : "Fixed gap: step as far as the gap reaches and take the last stud inside it, which is what setting out with a tape and a pencil gives. The odd bay lands at the far end."}>
                    <Chips value={battenMode} set={setBattenMode}
                      opts={[{ value: "equal", label: "Equal gaps" },
                             { value: "fixed", label: "Fixed gap" }]} />
                  </Field>
                  {cladding === "vertical" && (
                    <>
                      <Field label="Horizontal batten centres (mm)"
                        hint="The rails the cladding actually fixes to. One hard at the bottom, one hard at the top, and the rest divide what is between.">
                        <NumberField value={battenHSpacing} onCommit={setBattenHSpacing}
                          min={200} max={1200} step={50} ariaLabel="Horizontal batten spacing" />
                      </Field>
                      <Field label="Rails set out">
                        <Chips value={battenHMode} set={setBattenHMode}
                          opts={[{ value: "equal", label: "Equal gaps" },
                                 { value: "fixed", label: "Fixed gap" }]} />
                      </Field>
                    </>
                  )}
                  <Field label="Batten stock length (mm)"
                    hint="Ordered in its own length — a 2 × 1 comes in 4.5 m, not the 4.8 m the framing is cut from.">
                    <NumberField value={battenStock} onCommit={setBattenStock} min={1800}
                      max={6000} step={100} ariaLabel="Batten stock length" />
                  </Field>
                  {battens && battens.stubs > 0 && (
                    <div className="flag">
                      {battens.stubs} batten{battens.stubs === 1 ? "" : "s"} worked out under{" "}
                      {BATTEN_MIN} mm — over a door head or under a sill — and {battens.stubs === 1 ? "is" : "are"} left
                      off the cut list rather than put on it as a piece nobody would cut. Fix the
                      cladding there to the trimmers instead.
                    </div>
                  )}
                  {battens && battens.widestGap > battenSpacing + 1 && (
                    <div className="flag">
                      The widest gap between battens is {fmt(battens.widestGap)} mm against the{" "}
                      {fmt(battenSpacing)} mm asked for. Battens land on studs, so the frame is
                      the limit — no stud was skipped that would have closed it. Bring the stud
                      centres in under Framing if the cladding needs more support.
                    </div>
                  )}
                  {battens && !battens.onStud && (
                    <div className="flag">
                      A batten worked out clear of every stud, which should not happen — it would
                      be fixed to the sheathing alone.
                    </div>
                  )}
                </>
              )}

            </>
          )}

          {nav === "roof" && (
            <>
              <h3>Overhangs beyond the walls</h3>
              <Field label=""
                hint={`Roof footprint ${fmt(roof.L)} × ${fmt(roof.W)} mm from a ${fmt(fL)} × ${fmt(fW)} building.`}>
                <div className="quad">
                  <div><label>Left</label>
                    <NumberField value={ohLeft} onCommit={setOhLeft} min={0} max={1500} ariaLabel="Left overhang" /></div>
                  <div><label>Right</label>
                    <NumberField value={ohRight} onCommit={setOhRight} min={0} max={1500} ariaLabel="Right overhang" /></div>
                  <div><label>Front</label>
                    <NumberField value={ohFront} onCommit={setOhFront} min={0} max={1500} ariaLabel="Front overhang" /></div>
                  <div><label>Rear</label>
                    <NumberField value={ohRear} onCommit={setOhRear} min={0} max={1500} ariaLabel="Rear overhang" /></div>
                </div>
              </Field>
              <Field label="Gutter side"
                hint={`${fmt(roof.gutter.run)} mm of gutter, ${fmt(roof.gutter.edgeTrim)} mm of edge trim elsewhere. Shown as a ${GUTTER} × ${GUTTER} placeholder.`}>
                <Chips value={gutterSide} set={setGutterSide}
                  opts={SIDES.map((s) => ({ value: s.id, label: s.label }))} />
              </Field>

              <h3>Framing</h3>
              <Field label="Joists">
                <select value={rSectionId} onChange={(e) => setRSectionId(e.target.value)}>
                  {enabled.map((id) => {
                    const s = allSections.find((x) => x.id === id);
                    return <option key={id} value={id}>{s.label} · nominal {s.nominal}</option>;
                  })}
                </select>
              </Field>
              <Field label={spacingMode === "clear" ? "Clear bay between joists" : "Maximum joist centres"}
                hint={`Widest bay ${fmt(roof.frame.maxBay)} mm centre to centre, narrowest ${fmt(roof.frame.narrowestBay)} mm clear.`}>
                <Chips value={rSpacing} set={setRSpacing}
                  opts={[300, 400, 600].map((v) => ({ value: v, label: String(v) }))} />
              </Field>
              {roof.frame.narrowestBay < 100 && (
                <div className="flag">
                  The narrowest bay is {fmt(roof.frame.narrowestBay)} mm. That is the sheet
                  layout, not the framing: a joint has landed close to the end, and a
                  joint-aligned joist follows it. Nothing cuts to fill a strip that
                  narrow. Try Equal pieces under Sheet alignment, or Even centres under
                  Joist layout if you would rather the bays were uniform than under
                  every joint.
                </div>
              )}
              <Field label="Joist layout">
                <Chips value={rJoist} set={setRJoist}
                  opts={[{ value: "deck", label: "Under sheet joints" }, { value: "even", label: "Even centres" }]} />
              </Field>
              <Field label="Noggin rows">
                <Chips value={rNoggins} set={setRNoggins}
                  opts={[{ value: 0, label: "None" }, { value: 1, label: "One" }, { value: 2, label: "Two" }]} />
              </Field>

              <h3>Fall</h3>
              <Field label="Furring strips"
                hint={fall.on
                  ? `Falls ${fmt(fall.perMetre)} mm per metre toward the ${sideLabel(gutterSide).toLowerCase()} — about 1:${fall.ratio}, ${fall.degrees.toFixed(1)}°.`
                  : "Without them the roof is dead level and won't drain."}>
                <Chips value={furrOn} set={setFurrOn}
                  opts={[{ value: true, label: "On" }, { value: false, label: "Off" }]} />
              </Field>
              {furrOn && (
                <>
                  <Field label="Height at each end (mm)"
                    hint={`Low at the ${sideLabel(gutterSide).toLowerCase()}, high at the far side — water runs to the gutter. ${
                      fall.on && fall.rise
                        ? `${fmt(fall.rise)} mm of rise over ${(fall.span / 1000).toFixed(2)} m is ${fall.degrees.toFixed(1)}°, about 1:${fall.ratio} — ${fmt(fall.perMetre)} mm per metre. That is the angle to set the saw to${clad && clad.angled ? `, and ${clad.angled} cladding board${clad.angled === 1 ? " takes" : "s take"} it` : ""}.`
                        : "Both ends the same is a flat roof with a packer on it: nothing drains and nothing is cut to an angle."
                    }`}>
                    <div className="quad">
                      <div>
                        <label>Gutter end</label>
                        <NumberField value={furrLow} onCommit={setFurrLow} min={0} max={300}
                          step={5} ariaLabel="Furring height at gutter end" />
                      </div>
                      <div>
                        <label>Far end</label>
                        <NumberField value={furrHigh} onCommit={setFurrHigh} min={0} max={400}
                          step={5} ariaLabel="Furring height at far end" />
                      </div>
                    </div>
                  </Field>
                  <Field label="Cut from"
                    hint={`${furrCover.n === 1 ? "One board" : `${furrCover.n} boards`} a wall: ${furringWidths(walls.wt, furrSection.t).map((x) => fmt(x.width)).join(" + ")} = ${fmt(furrCover.covered)} mm on a ${fmt(furrCover.wall)} mm plate${
                      furrCover.bare > 0
                        ? `, set out from the outside face so the ${fmt(furrCover.bare)} mm left over is on the inside, over the ceiling. Whole boards only — a strip ripped that narrow has nothing to fix through, and ${furrCover.n === 1 ? "one" : "the pair"} carries the roof.`
                        : ", which lands exactly."
                    }`}>
                    <select value={furrId} onChange={(e) => setFurrId(e.target.value)}>
                      {enabled.map((id) => {
                        const s = allSections.find((x) => x.id === id);
                        return <option key={id} value={id}>{s.label}</option>;
                      })}
                    </select>
                  </Field>
                </>
              )}

              <h3>Structural deck</h3>
              <Field label="Thickness (mm)" hint={`${roofBoard.label} sheets, set in Materials.`}>
                <Chips value={rDeckT} set={setRDeckT} opts={OSB_T.map((v) => ({ value: v, label: String(v) }))} />
              </Field>
              <Field label="Sheet direction"
                hint={roof.deck.alternative
                  ? `${roof.deck.cuts} cuts this way, ${roof.deck.alternative.cuts} the other.`
                  : "Locked to one direction."}>
                <Chips value={rOrient} set={setROrient} opts={orientOpts} />
              </Field>
              <Field label="Sheet alignment">
                <Chips value={rAlign} set={setRAlign}
                  opts={[{ value: "auto", label: "Auto" }, { value: "centre", label: "Centre out" },
                         { value: "edge", label: "One end" }, { value: "equal", label: "Equal pieces" }]} />
              </Field>

              <h3>Upstand &amp; insulation</h3>
              <Field label="Upstand"
                hint={roof.upstandMatchesPir
                  ? `${roof.upstand.uw} mm wide × ${roof.upstand.uh} mm tall — matches the insulation.`
                  : `${roof.upstand.uh} mm tall against ${roofPirT} mm insulation. Match them or the top deck rocks.`}>
                <select value={upstandId} onChange={(e) => setUpstandId(e.target.value)}>
                  {enabled.map((id) => {
                    const s = allSections.find((x) => x.id === id);
                    return <option key={id} value={id}>{s.label}</option>;
                  })}
                </select>
              </Field>
              <Field label="Laid">
                <Chips value={upstandOnEdge} set={setUpstandOnEdge}
                  opts={[{ value: true, label: "On edge" }, { value: false, label: "Flat" }]} />
              </Field>
              <Field label="Membrane under the insulation"
                hint={!roofWrap
                  ? "Off. The insulation sits straight on the deck."
                  : roofWrap.impossible
                    ? "The lap is not smaller than the roll, so every strip would start where the last one did. Set it on the Walls tab."
                    : `${roofWrap.n} strip${roofWrap.n === 1 ? "" : "s"} of ${(roofWrap.along / 1000).toFixed(2)} m, ${(roofWrap.runM / 1000).toFixed(1)} m in all. On the deck under the PIR, up the inside of the upstand and over its top, so the whole build-up sits in a tray. Same roll and tape as the walls — set on the Walls tab.`}>
                <Chips value={rWrapOn} set={setRWrapOn}
                  opts={[{ value: true, label: "Lined" }, { value: false, label: "None" }]} />
              </Field>
              {roofWrap && roofWrap.n > 0 && (
                <>
                  <Field label="Which way the strips run"
                    hint={`${(roofWrap.devL / 1000).toFixed(2)} × ${(roofWrap.devW / 1000).toFixed(2)} m developed — the roof plus a ${fmt(roofWrap.uh)} mm turn-up each side, since what the upstand takes off the tray it gives back going over the top. Running them the long way buys the fewest laps.`}>
                    <Chips value={rWrapOrient} set={setRWrapOrient}
                      opts={[{ value: "auto", label: "Auto" },
                             { value: "along", label: "Along length" },
                             { value: "across", label: "Across width" }]} />
                  </Field>
                  {roofWrap.perStrip > 1 && (
                    <div className="flag">
                      A strip is {(roofWrap.along / 1000).toFixed(2)} m and the roll is only{" "}
                      {wrapRollLen} m, so each one is made from {roofWrap.perStrip} lengths
                      with a lap and a cross join between them. A longer roll would run
                      them in one.
                    </div>
                  )}
                  <p className="hint">
                    <span className="tapesum">
                      <b>{(roofWrap.runM / 1000).toFixed(1)} m</b> membrane
                    </span>
                    <span className="tapesum">
                      <b>{(roofWrap.tape.lap / 1000).toFixed(1)} m</b> strip laps
                    </span>
                    {roofWrap.tape.join > 0 && (
                      <span className="tapesum">
                        <b>{(roofWrap.tape.join / 1000).toFixed(1)} m</b> cross joins
                      </span>
                    )}
                    <span className="tapesum">
                      <b>{(roofWrap.tape.upstand / 1000).toFixed(1)} m</b> over the upstand
                    </span>
                    <span className="tapesum">
                      <b>{(roofWrap.tape.total / 1000).toFixed(1)} m</b> tape in all
                    </span>
                  </p>
                </>
              )}
              <Field label="Roof insulation from"
                hint={roofPirUnset
                  ? "Two layers needs a wall thickness to double, and cavity insulation is off. Falling back to the figure set below."
                  : roofPirLayers > 1
                    ? `${roof.pirLayers} layers of ${roof.pirLayerT} mm, ${roofPirT} mm in total, off the same board the walls use — one pile, and each can cut from the others' offcuts. Joints in the upper layer clear the lower by ${fmt(roof.pirStagger || 0)} mm.`
                    : "Two layers of the wall's board reaches the same depth off one pile instead of a thickness nothing else can share."}>
                <Chips value={rPirMode} set={setRPirMode}
                  opts={[{ value: "thickness", label: "A thickness" },
                         { value: "wallLayers", label: "2 × the wall's" }]} />
              </Field>
              <Field label="PIR thickness (mm)"
                hint={roofPirLayers > 1
                  ? `Not in use — the roof is taking ${roof.pirLayers} × ${roof.pirLayerT} mm from the walls.`
                  : undefined}>
                <Chips value={pirT} set={setPirT} opts={PIR_T.map((v) => ({ value: v, label: String(v) }))} />
              </Field>

              <h3>Top deck &amp; EPDM</h3>
              <Field label="Thickness (mm)" hint={`${topBoard.label} sheets, set in Materials.`}>
                <Chips value={topT} set={setTopT} opts={OSB_T.map((v) => ({ value: v, label: String(v) }))} />
              </Field>
              <Field label="Sheet direction">
                <Chips value={topOrient} set={setTopOrient} opts={orientOpts} />
              </Field>
              <Field label="Sheet alignment">
                <Chips value={topAlign} set={setTopAlign}
                  opts={[{ value: "auto", label: "Auto" }, { value: "centre", label: "Centre out" },
                         { value: "edge", label: "One end" }, { value: "equal", label: "Equal pieces" }]} />
              </Field>
              <Field label="EPDM margin all round (mm)"
                hint="Extra membrane beyond the deck edge for dressing down and trimming.">
                <NumberField value={epdmMargin} onCommit={setEpdmMargin} min={0} max={500} step={25} ariaLabel="EPDM margin" />
              </Field>

            </>
          )}

          {nav === "energy" && (
            <>
              <h3>Site</h3>
              <Field label="Location"
                hint={`Design temperatures: ${outdoorWinter}\u00A0°C in winter, ${outdoorSummer}\u00A0°C in summer.`}>
                <select value={locationId} onChange={(e) => setLocationId(e.target.value)}>
                  {LOCATIONS.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
                </select>
              </Field>
              {locationId === "custom" && (
                <Field label="Outside, winter × summer (°C)">
                  <div className="pair">
                    <NumberField value={siteWinter} onCommit={setSiteWinter}
                      min={-25} max={20} step={1} ariaLabel="Winter design temperature" />
                    <NumberField value={siteSummer} onCommit={setSiteSummer}
                      min={0} max={50} step={1} ariaLabel="Summer design temperature" />
                  </div>
                </Field>
              )}
              <Field label="The front wall faces"
                hint={`So the ${sideLabel(winSide).toLowerCase()} wall, where the glazing is, faces ${
                  (ORIENTATIONS.find((o) => o.id === facingOf(frontFaces, winSide)) || {}).label
                }. Solar gain follows this.`}>
                <select value={frontFaces} onChange={(e) => setFrontFaces(e.target.value)}>
                  {ORIENTATIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>
              </Field>

              <h3>Inside</h3>
              <Field label="Kept at, winter × summer (°C)">
                <div className="pair">
                  <NumberField value={indoorWinter} onCommit={setIndoorWinter}
                    min={5} max={30} step={1} ariaLabel="Indoor winter temperature" />
                  <NumberField value={indoorSummer} onCommit={setIndoorSummer}
                    min={15} max={35} step={1} ariaLabel="Indoor summer temperature" />
                </div>
              </Field>
              <Field label="Air changes per hour"
                hint="Ventilation and leakage together. 0.5 is a reasonably tight small building; 1.0 is a draughty one.">
                <NumberField value={airChanges} onCommit={setAirChanges}
                  min={0} max={5} step={0.1} ariaLabel="Air changes per hour" />
              </Field>
              <Field label="Heat from people and kit (W)"
                hint="One person at a desk is roughly 100 W of body heat plus whatever is plugged in.">
                <NumberField value={internalGain} onCommit={setInternalGain}
                  min={0} max={2000} step={25} ariaLabel="Internal gains" />
              </Field>

              <h3>Glazing</h3>
              <Field label="U-value (W/m²K)"
                hint="From the window's own certificate — whole-unit, not centre-pane. Triple glazing is usually 0.7 to 0.9.">
                <NumberField value={uGlazing} onCommit={setUGlazing}
                  min={0.4} max={6} step={0.1} ariaLabel="Glazing U-value" />
              </Field>
              <Field label="Solar factor, g"
                hint="How much of the sun's heat gets through. Triple glazing is typically 0.5; a solar-control coating is lower and costs you winter gain.">
                <NumberField value={gGlazing} onCommit={setGGlazing}
                  min={0.1} max={0.9} step={0.05} ariaLabel="Glazing g-value" />
              </Field>
              <Field label="Shading factor"
                hint="1.0 is wide open. An overhang, a tree or a blind takes it down.">
                <NumberField value={shadingFactor} onCommit={setShadingFactor}
                  min={0.1} max={1} step={0.05} ariaLabel="Shading factor" />
              </Field>
              <Field label="PIR conductivity, λ (W/m·K)"
                hint="From the board's own certificate. A good foil-faced board is 0.022; plain ones reach 0.026 and premium ones 0.019. It is the dominant layer, so this moves the answer more than anything else here.">
                <NumberField value={lambdaPir} onCommit={setLambdaPir}
                  min={0.015} max={0.045} step={0.001} ariaLabel="PIR conductivity" />
              </Field>
              <Field label="Solid door U-value (W/m²K)"
                hint="Only used when the door is solid; a glazed one takes the glazing figure.">
                <NumberField value={uDoorSolid} onCommit={setUDoorSolid}
                  min={0.4} max={6} step={0.1} ariaLabel="Solid door U-value" />
              </Field>
            </>
          )}

          {nav === "materials" && (
            <>
              <h3>The building</h3>
              <Field label="Footprint, length × width (mm)">
                <div className="pair">
                  <NumberField value={fL} onCommit={setFL} min={500} max={12000} ariaLabel="Length" />
                  <NumberField value={fW} onCommit={setFW} min={500} max={8000} ariaLabel="Width" />
                </div>
              </Field>
              <Field label="Internal ceiling height (mm)"
                hint={`Overall height works out at ${fmt(heights.overall)} mm.`}>
                <NumberField value={ceilingHeight} onCommit={setCeilingHeight} min={1500} max={4000}
                  step={50} ariaLabel="Internal ceiling height" />
              </Field>
              <Field label="Ground clearance (mm)"
                hint="Underside of the floor frame to the ground. 50 mm minimum for airflow, and it counts toward the overall height.">
                <NumberField value={groundClear} onCommit={setGroundClear} min={0} max={600}
                  step={10} ariaLabel="Ground clearance" />
              </Field>
              <Field label="Overall height target (mm)"
                hint={heights.overTarget > 0
                  ? `${fmt(heights.overTarget)} mm over. Ceiling would need to come down to ${fmt(heights.maxCeiling)} mm.`
                  : `${fmt(-heights.overTarget)} mm under. Ceiling could go to ${fmt(heights.maxCeiling)} mm.`}>
                <div className="pair">
                  <NumberField value={targetHeight} onCommit={setTargetHeight} min={1800} max={5000}
                    step={25} ariaLabel="Overall height target" />
                  <button className="chip" style={{ whiteSpace: "nowrap" }}
                    disabled={heights.maxCeiling < 1500}
                    onClick={() => setCeilingHeight(Math.max(1500, heights.maxCeiling))}>
                    Fit to it
                  </button>
                </div>
              </Field>

              <h3>Timber sections you're buying</h3>
              <Field label=""
                hint="Floor, roof and upstand pick from this list, so one 45 × 120 serves them all. Enable only what you'll actually order.">
                <MultiChips values={enabled}
                  opts={allSections.map((s) => ({ value: s.id, label: s.label }))}
                  toggle={(id) =>
                    setEnabled((cur) =>
                      cur.includes(id)
                        ? cur.length > 1 ? cur.filter((x) => x !== id) : cur
                        : [...cur, id]
                    )} />
              </Field>
              <Field label="Add your own section (mm)"
                hint="Sawn, planed and treated all measure differently. Put in what your tape says and every cut length follows from it.">
                <div className="quad">
                  <div>
                    <label>Thickness</label>
                    <NumberField value={newT} onCommit={setNewT} min={10} max={400} step={1}
                      ariaLabel="Custom section thickness" />
                  </div>
                  <div>
                    <label>Depth</label>
                    <NumberField value={newD} onCommit={setNewD} min={10} max={500} step={1}
                      ariaLabel="Custom section depth" />
                  </div>
                </div>
                <div className="pair" style={{ marginTop: 8 }}>
                  <input type="text" value={newNote} placeholder="sawn, planed, treated…"
                    aria-label="Custom section note" maxLength={18}
                    onChange={(e) => setNewNote(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") addSection(); }} />
                  <button className="chip" style={{ whiteSpace: "nowrap" }} onClick={addSection}>
                    Add
                  </button>
                </div>
              </Field>
              {customSections.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  {customSections.map((s) => (
                    <div className="customrow" key={s.id}>
                      <b>{s.label}</b>
                      <span style={{ color: "var(--soft)" }}>
                        {enabled.includes(s.id) ? "in use" : "not enabled"}
                      </span>
                      <button className="linkbtn" onClick={() => removeSection(s.id)}>Remove</button>
                    </div>
                  ))}
                </div>
              )}

              <Field label="Stock length (mm)">
                <select value={stock} onChange={(e) => setStock(Number(e.target.value))}>
                  {STOCK.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>

              <h3>OSB sheet size, per thickness</h3>
              <Field label=""
                hint="One size per thickness across the whole build, so an 18 mm floor sheet and an 18 mm roof sheet are the same product.">
                <div className="assign">
                  {usedThicknesses.map((t) => (
                    <React.Fragment key={t}>
                      <span>{t} mm</span>
                      <select value={sheetFor[t] || "2397"}
                        onChange={(e) => setSheetFor((cur) => ({ ...cur, [t]: e.target.value }))}>
                        {SHEETS.map((s) => <option key={s.id} value={s.id}>{s.label} · {s.note}</option>)}
                      </select>
                    </React.Fragment>
                  ))}
                </div>
              </Field>
              <Field label="PIR board size (mm)">
                <select value={pirBoardId} onChange={(e) => setPirBoardId(e.target.value)}>
                  {PIR_BOARDS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </Field>
              <Field label="Wall insulation (mm)"
                hint={wPirT === 0
                  ? "No wall insulation. The bays are left open."
                  : `${wallPir.total} bays, ${m2(wallPir.area)} m² across four walls. ${wallPir.slivers ? `${wallPir.slivers} gap${wallPir.slivers === 1 ? "" : "s"} under 50 mm left to foam. ` : ""}Openings are skipped; a glazed wall keeps the band below its sill.`}>
                <Chips value={wPirT} set={setWPirT}
                  opts={[{ value: 0, label: "None" },
                         ...PIR_T.filter((v) => v <= wallSection.d)
                           .map((v) => ({ value: v, label: String(v) }))]} />
              </Field>
              {PIR_T.every((v) => v > wallSection.d) && (
                <p className="hint">
                  No stocked PIR fits a {wallSection.d} mm stud. Use a deeper wall
                  section, or insulate over the frame rather than between it.
                </p>
              )}
              <Field label="Floor insulation (mm)"
                hint={floor.pir.deeper
                  ? `${fPirT} mm will not go between ${floorSection.d} mm joists. Use ${floorSection.d} mm or shallower, or deepen the joists.`
                  : fPirT === 0
                    ? "No floor insulation. The bays are left open."
                    : floor.pir.airGap
                      ? `${floor.pir.bays} bays, ${m2(floor.pir.area)} m². Leaves a ${floor.pir.airGap} mm void in a ${floorSection.d} mm joist. Sits flush with the underside of the deck, so the void falls underneath.`
                      : `${floor.pir.bays} bays, ${m2(floor.pir.area)} m². Fills the ${floorSection.d} mm joist depth exactly.`}>
                <Chips value={fPirT} set={setFPirT}
                  opts={[{ value: 0, label: "None" },
                         ...PIR_T.filter((v) => v <= floorSection.d)
                           .map((v) => ({ value: v, label: String(v) }))]} />
              </Field>
              <Field label="Battens under the floor PIR"
                hint={fPirT === 0
                  ? "No floor insulation, so nothing to hold up."
                  : joistBat
                    ? `${joistBat.count} ledgers, ${joistBat.linearM.toFixed(1)} m of ${battenSection.label} — two per bay, ${fBattenPct}% of the bay's length, centred. ${fmt(joistBat.ledge)} mm of each carries the board and ${fmt(joistBat.deep)} mm goes against the joist.${joistBat.short ? ` ${joistBat.short} bay${joistBat.short === 1 ? "" : "s"} came out too short to be worth cutting and ${joistBat.short === 1 ? "is" : "are"} left out.` : ""}`
                    : "Off. The board sits high in the joist, flush under the deck, so there is an air gap beneath it and nothing stopping it dropping into that gap."}>
                <Chips value={fBattenOn} set={setFBattenOn}
                  opts={[{ value: true, label: "Ledgers" }, { value: false, label: "None" }]} />
              </Field>
              {fBattenOn && fPirT > 0 && (
                <>
                  <Field label="Length, as a share of the bay (%)"
                    hint="A board does not need carrying along its whole edge to stay up. 80% of a 500 mm bay is a 400 mm piece, centred, so the unsupported ends are equal.">
                    <NumberField value={fBattenPct} onCommit={setFBattenPct} min={20} max={100}
                      step={5} ariaLabel="Ledger length share" />
                  </Field>
                  <Field label="Laid"
                    hint={`On edge puts the ${fmt(battenSection.d)} mm face against the joist and carries the board on a ${fmt(battenSection.t)} mm edge — a deep fixing and a small ledge. Flat is the other way about: a ${fmt(battenSection.d)} mm ledge needing only ${fmt(battenSection.t)} mm of air gap, which is the way round to use when the board nearly fills the joist.`}>
                    <Chips value={fBattenOnEdge} set={setFBattenOnEdge}
                      opts={[{ value: true, label: "On edge" }, { value: false, label: "Flat" }]} />
                  </Field>
                  {joistBat && !joistBat.fits && (
                    <div className="flag">
                      The ledger is {fmt(joistBat.deep)} mm deep and there is only{" "}
                      {fmt(joistBat.airGap)} mm of air gap under the board, so it would hang
                      below the joist.{" "}
                      {joistBat.fitsFlat && fBattenOnEdge
                        ? "Laid flat it needs only " + fmt(battenSection.t) + " mm and would fit."
                        : "Take the insulation down a size, or deepen the joists."}
                    </div>
                  )}
                </>
              )}
              <Field label="Fit tolerance (mm)"
                hint={(() => {
                  const n = [...alloc.pir.values()].reduce((t, g) => t + (g.gaps || 0), 0);
                  const worst = [...alloc.pir.values()].reduce((t, g) => Math.max(t, g.gapMax || 0), 0);
                  const how = "How short a board may fall and still count as fitting. A millimetre is nothing behind a taped and foamed joint, and the leftovers are full of boards exactly that: a 1,200 board taken for a 694 mm bay leaves 506, and the next bay along is 507.";
                  return `${how}${pirTol > 0
                    ? n
                      ? ` ${n} piece${n === 1 ? "" : "s"} relying on it, worst gap ${fmt(worst)} mm.`
                      : " Nothing is relying on it at the moment."
                    : " Off, so a board a millimetre under the bay is no use."} Sheathing gets none of this — a structural sheet that short is a gap, not a tolerance.`;
                })()}>
                <NumberField value={pirTol} onCommit={setPirTol} min={0} max={25}
                  step={1} ariaLabel="Fit tolerance" />
              </Field>
              <Field label="Make pieces up from offcuts"
                hint={(() => {
                  const n = [...alloc.pir.values()].reduce((t, g) => t + (g.pieced || 0), 0);
                  const left = [...alloc.pir.values()].reduce((t, g) => t + g.pool.length, 0);
                  const over = `${left} offcut${left === 1 ? "" : "s"} left over.`;
                  if (pirPiece === "none")
                    return `Every bay and every tray piece needs one offcut big enough to take it whole, and anything smaller sits in the pool. ${over}`;
                  const where = pirPiece === "all"
                    ? "Walls too: a patchwork stood on edge and held by friction can slump and leave a void at the top, which is the one place you will not find it."
                    : "Floor and roof lie flat, so a made-up bay stays where it is put. A wall's does not, so it still takes whole pieces.";
                  if (!alloc.pirSaved) {
                    const s = alloc.pirSuggest;
                    return `${n ? `${n} piece${n === 1 ? "" : "s"} made up, but it` : "Nothing needed making up — it"} saves no boards at ${fmt(pirPieceL)} × ${fmt(pirPieceW)}: most leftovers are under ${fmt(pirPieceL)} mm in one direction. ${
                      s ? `${fmt(s.l)} × ${fmt(pirPieceW)} would save ${s.saves} board${s.saves === 1 ? "" : "s"}.`
                        : `Nothing smaller saves a board either${pirPiece === "flat" ? " — try Everywhere, since the walls are most of the demand" : ""}.`
                    } ${over} ${where}`;
                  }
                  return `${n} piece${n === 1 ? "" : "s"} made up from butted offcuts, saving ${alloc.pirSaved} board${alloc.pirSaved === 1 ? "" : "s"}. ${over} ${where}`;
                })()}>
                <Chips value={pirPiece} set={setPirPiece}
                  opts={[{ value: "none", label: "Whole pieces" },
                         { value: "flat", label: "Floor & roof" },
                         { value: "all", label: "Everywhere" }]} />
              </Field>
              {pirPiece !== "none" && (
                <>
                  <Field label="Smallest piece worth cutting, length × width (mm)"
                    hint="An offcut smaller than this in either direction stays in the pool rather than being butted in. Every joint is one to tape and foam, so the figure buys boards at the price of work.">
                    <div className="pair">
                      <NumberField value={pirPieceL} onCommit={setPirPieceL} min={100} max={2400}
                        step={50} ariaLabel="Smallest piece length" />
                      <NumberField value={pirPieceW} onCommit={setPirPieceW} min={25} max={1200}
                        step={25} ariaLabel="Smallest piece width" />
                    </div>
                  </Field>
                  <Field label="Most parts in one piece"
                    hint="A jigsaw has a limit. Uncapped, this will cover a 2,400 × 1,182 tray board from nineteen offcuts down to 50 mm slivers — which saves boards on paper and is not something anybody would cut, carry up a ladder and tape.">
                    <NumberField value={pirPieceMax} onCommit={setPirPieceMax} min={2} max={12}
                      step={1} ariaLabel="Most parts in one piece" />
                  </Field>
                </>
              )}
              <Field label="Foil tape over the PIR joints"
                hint={!tape
                  ? "Off. Every butt joint and every edge against the timber is an air path until it is taped."
                  : tape.total === 0
                    ? "No insulation to tape yet."
                    : `${(tape.total / 1000).toFixed(1)} m measured — ${(tape.joint / 1000).toFixed(1)} m of butt joints and ${(tape.perimeter / 1000).toFixed(1)} m of edges against the timber. A seam shared by two boards is one run, not two, so this is what you pull rather than the sum of the boards' perimeters.`}>
                <Chips value={tapeOn} set={setTapeOn}
                  opts={[{ value: true, label: "Taped" }, { value: false, label: "None" }]} />
              </Field>
              {tape && tape.total > 0 && (
                <>
                  <Field label="Tape width (mm)"
                    hint={`Half of it laps each side of the joint, so ${tape.width} mm gives ${fmt(tape.lap)} mm a side. The width does not change the metres — one run seals one seam whatever it is — it changes what that run covers.`}>
                    <Chips value={tapeW} set={setTapeW}
                      opts={TAPE_W.map((v) => ({ value: v, label: String(v) }))} />
                  </Field>
                  {tape.lapShort && (
                    <div className="flag">
                      {tape.width} mm leaves only {fmt(tape.lap)} mm of lap either side of a
                      joint, under the {TAPE_MIN_LAP} mm worth having. On a butt joint that
                      opens as the boards move, that is the seal gone. Use 100 mm.
                    </div>
                  )}
                  <Field label="Roll length (m)"
                    hint={`What you buy it in. ${(tape.ordered / 1000).toFixed(1)} m ordered comes to ${tape.rolls} roll${tape.rolls === 1 ? "" : "s"}.`}>
                    <NumberField value={tapeRoll} onCommit={setTapeRoll} min={5} max={100}
                      step={5} ariaLabel="Tape roll length" />
                  </Field>
                  <Field label="Waste allowance (%)"
                    hint={`Ends lapped, runs restarted, tape stuck to itself. Added to what is ordered and never to what is measured, so ${(tape.total / 1000).toFixed(1)} m stays the figure the geometry gives and ${(tape.ordered / 1000).toFixed(1)} m is the figure you buy.`}>
                    <NumberField value={tapeWaste} onCommit={setTapeWaste} min={0} max={50}
                      step={5} ariaLabel="Tape waste allowance" />
                  </Field>
                  {roof.pirLayers > 1 && (
                    <Field label="Which roof layers are taped"
                      hint={`${roof.pirLayers} layers up there. Taping the top one alone is ordinary practice — that is the layer carrying the seal — and taping both is belt and braces at ${((tape.byStage.roof ? tape.byStage.roof.total : 0) / 1000).toFixed(1)} m.`}>
                      <Chips value={tapeLayers} set={setTapeLayers}
                        opts={[{ value: "all", label: "Every layer" },
                               { value: "top", label: "Top layer only" }]} />
                    </Field>
                  )}
                  <p className="hint">
                    {["floor", "walls", "roof"].filter((k) => tape.byStage[k]).map((k) => (
                      <span key={k} className="tapesum">
                        <b>{(tape.byStage[k].total / 1000).toFixed(1)} m</b> {k}
                      </span>
                    ))}
                    <span className="tapesum">
                      <b>{tape.rolls} roll{tape.rolls === 1 ? "" : "s"}</b> to buy
                    </span>
                  </p>
                  {tapeCost && tapeCost.total > 0 && (() => {
                    /* Whether the joints the piecing added cost more in tape than
                       the boards it saved. Both prices come off the order sheet,
                       so an unpriced design is told nothing rather than guessed
                       at. */
                    const t = tapeTrade({
                      extraM: tapeCost.total,
                      tapePerRoll: Number(prices[`tape-${tape.width}-${tapeRoll}`]),
                      roll: tapeRoll * 1000,
                      boardsSaved: alloc.pirSaved,
                      boardPrice: Number(prices[`pir-${roof.pirLayerT}-${pirBoard.id}`]),
                    });
                    const where = ["roof", "floor", "walls"]
                      .filter((k) => tapeCost.stage[k] > 0)
                      .map((k) => `${(tapeCost.stage[k] / 1000).toFixed(1)} m in the ${k}`)
                      .join(", ");
                    if (!t)
                      return (
                        <p className="hint">
                          Cutting the pieces from offcuts added{" "}
                          {(tapeCost.total / 1000).toFixed(1)} m of butt joints —{" "}
                          {where} — so {(tapeCost.whole / 1000).toFixed(1)} m of tape whole
                          becomes {(tapeCost.cut / 1000).toFixed(1)} m as cut. Price the tape
                          and the PIR board on the order sheet and this will say which way
                          round is cheaper.
                        </p>
                      );
                    return (
                      <div className={t.boardCheaper ? "flag" : "hint"}>
                        The piecing added {(tapeCost.total / 1000).toFixed(1)} m of butt
                        joints — {where} — which is {money(t.tapeCost)} of tape at{" "}
                        {money(t.perM)} a metre, against {t.boardsSaved} board
                        {t.boardsSaved === 1 ? "" : "s"} saved at {money(t.boardCost)}.{" "}
                        {t.boardCheaper
                          ? "The tape costs more than the boards it saved. Set Whole pieces above and lay another board instead: fewer joints is also less to go wrong."
                          : "The boards still win, so those joints are worth taping."}
                      </div>
                    );
                  })()}
                </>
              )}
              <Field label="Roof insulation from"
                hint={roofPirUnset
                  ? "Two layers needs a wall thickness to double, and cavity insulation is off. Falling back to the figure set below."
                  : roofPirLayers > 1
                    ? `${roof.pirLayers} layers of ${roof.pirLayerT} mm, ${roofPirT} mm in total, off the same board the walls use — one pile, and each can cut from the others' offcuts. Joints in the upper layer clear the lower by ${fmt(roof.pirStagger || 0)} mm.`
                    : "Two layers of the wall's board reaches the same depth off one pile instead of a thickness nothing else can share."}>
                <Chips value={rPirMode} set={setRPirMode}
                  opts={[{ value: "thickness", label: "A thickness" },
                         { value: "wallLayers", label: "2 × the wall's" }]} />
              </Field>
              <Field label="Roof insulation (mm)"
                hint={`${roofPirLayers > 1
                  ? `Set by the walls: ${roof.pirLayers} × ${roof.pirLayerT} mm. `
                  : ""}${ROOF_PIR_MIN} mm is the sensible minimum up here — it is the biggest surface and the one the sun sits on all day. ${
                  alloc.pirShared
                    ? "Every insulated layer is on this thickness, so they come out of one pile and each can use the others' offcuts."
                    : `Floor is ${fPirT || "none"} and walls are ${wPirT || "none"}. Each distinct thickness is its own material and its own pile.`
                }`}>
                <Chips value={pirT} set={setPirT}
                  opts={PIR_T.map((v) => ({ value: v, label: String(v) }))} />
              </Field>
              {roofPirT < ROOF_PIR_MIN && (
                <div className="flag">
                  {roofPirT} mm in the roof is below the {ROOF_PIR_MIN} mm worth aiming
                  for. A warm roof is the one place insulation is continuous and
                  unbridged, so it is the cheapest place to buy performance — see
                  Energy for what it costs you.
                </div>
              )}
              {!roof.upstandMatchesPir && (
                <div className="flag">
                  Roof upstand is {roof.upstand.uh} mm and the roof insulation is {roofPirT} mm.
                  Match them, or the top deck sits proud of one and bridges the other. The
                  upstand section is set under Roof.
                </div>
              )}
              {wallPir.deeper && (
                <div className="flag">
                  Wall insulation is {wPirT} mm but the studs are only {wallSection.d} mm
                  deep. It will stand proud of the frame and hold the sheathing off the
                  studs.
                </div>
              )}
              {floor.pir.deeper && (
                <div className="flag">
                  Floor insulation is {fPirT} mm but the joists are only {floorSection.d} mm
                  deep. It will stand proud of the frame and hold the deck off the joists.
                </div>
              )}

              <h3>Cutting</h3>
              <Field label="Optimise sheets for"
                hint="Fewest cuts keeps whole sheets whole and leaves one usable remainder. Least waste squeezes the area harder. Largest pieces divides each face equally so nothing comes out as a ribbon — it uses more sheet and is usually the right call on walls.">
                <Chips value={goal} set={setGoal}
                  opts={[{ value: "cuts", label: "Fewest cuts" },
                         { value: "material", label: "Least waste" },
                         { value: "largest", label: "Largest pieces" }]} />
              </Field>
              <Field label="Spacing figures mean"
                hint={spacingMode === "clear"
                  ? `The clear bay between members, so a 400 asked for is a 400 cut. Insulation comes 1200 wide, so a 400 bay is three strips from a board with nothing left. Centres come out ${fmt(floorSection.t)} mm wider than the figure.`
                  : `Centre to centre, which is what every span table means. A 400 with ${fmt(floorSection.t)} mm members leaves a ${fmt(400 - floorSection.t)} mm bay — three strips from a 1200 board and ${fmt(1200 - 3 * (400 - floorSection.t))} mm off each one. Read as a clear bay instead and that waste goes.`}>
                <Chips value={spacingMode} set={setSpacingMode}
                  opts={[{ value: "centres", label: "Centres" },
                         { value: "clear", label: "Clear bay" }]} />
              </Field>
              <Field label="Expansion gap (mm)" hint="All OSB layers. Insulation is butted tight.">
                <NumberField value={gap} onCommit={setGap} min={0} max={10} step={1} ariaLabel="Expansion gap" />
              </Field>
              <Field label="Stagger sheet rows" hint="Only applies where a deck needs more than one row.">
                <Chips value={stagger} set={setStagger}
                  opts={[{ value: true, label: "On" }, { value: false, label: "Off" }]} />
              </Field>

              <h3>Fixings</h3>
              <p className="note">
                Counted off the framing and the sheet layouts rather than estimated, so
                the tally moves with the drawings. These are your numbers, not a
                schedule — there is no structural checking behind them.
              </p>
              <Field label="Count fixings"
                hint="Off removes them from the order summary and the drawings.">
                <Chips value={fixOn} set={setFixOn}
                  opts={[{ value: true, label: "On" }, { value: false, label: "Off" }]} />
              </Field>
              {fixOn && (
                <>
                  <Field label="Framing screws per joint"
                    hint={`Every joist, stud, trimmer and cripple end. Rims and plates are what they land in, so they are not counted twice. Floor: ${fmt(fixings.byStage.floor ? fixings.byStage.floor.framing : 0)} screws.`}>
                    <NumberField value={fixJointScrews} onCommit={setFixJointScrews}
                      min={0} max={12} step={1} ariaLabel="Framing screws per joint" />
                  </Field>
                  <Field label="120 mm screws per noggin end"
                    hint="External wood screws, counted at both ends of every noggin in the floor, walls and roof.">
                    <NumberField value={fixNogginScrews} onCommit={setFixNogginScrews}
                      min={0} max={12} step={1} ariaLabel="Screws per noggin end" />
                  </Field>
                  <Field label="Joist hangers"
                    hint="One per floor joist end. The roof gets truss clips instead, which are counted separately.">
                    <Chips value={fixHangers} set={setFixHangers}
                      opts={[{ value: true, label: "Fitted" }, { value: false, label: "None" }]} />
                  </Field>
                  <Field label="OSB screw pitch (mm)"
                    hint="50 mm screws, wherever a sheet crosses a member — floor deck, wall sheathing and roof deck alike.">
                    <NumberField value={fixSheetPitch} onCommit={setFixSheetPitch}
                      min={50} max={600} step={25} ariaLabel="OSB screw pitch" />
                  </Field>
                  <table>
                    <thead>
                      <tr><th>Fixing</th><th>Floor</th><th>Walls</th><th>Roof</th><th>Total</th></tr>
                    </thead>
                    <tbody>
                      {FIXINGS.map((f) => (
                        <tr key={f.id}>
                          <td>{f.label}</td>
                          {["floor", "walls", "roof"].map((st) => (
                            <td key={st}>
                              {fmt(fixings.byStage[st] ? fixings.byStage[st][f.id] : 0)}
                            </td>
                          ))}
                          <td><b>{fmt(fixings.total[f.id])}</b></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}

              <h3>Appearance</h3>
              <Field label="Model palette" hint="Affects the 3D views only — drawings stay neutral.">
                <Chips value={paletteId} set={setPaletteId}
                  opts={PALETTES.map((x) => ({ value: x.id, label: x.label }))} />
              </Field>
              <p className="note">
                How the model is coloured — by material, per wall or per layer — and what's
                visible, live in the bar at the top so they're one click away from any view.
              </p>
              <div className="pills" style={{ marginTop: 8 }}>
                {SIDES.map((s) => (
                  <span className="pill" key={s.id}>
                    <i className="swatch" style={{ background: palette.sides[s.id] }} />
                    {s.label}
                  </span>
                ))}
              </div>

              <h3>3D print export</h3>
              <Field label="Detail"
                hint="Framing exports every member. Massing is three solid blocks — no thin parts, prints at any scale.">
                <Chips value={asmMode} set={setAsmMode}
                  opts={[{ value: "framing", label: "Framing" }, { value: "massing", label: "Massing" }]} />
              </Field>
              {asmMode === "framing" && (
                <Field label="Include">
                  <MultiChips values={asmInclude}
                    opts={[{ value: "floor", label: "Floor" }, { value: "walls", label: "Wall studs" },
                           { value: "sheathing", label: "Sheathing" }, { value: "roof", label: "Roof" },
                           { value: "insulation", label: "PIR" }, { value: "glazing", label: "Glazing" }]}
                    toggle={(id) =>
                      setAsmInclude((cur) =>
                        cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id])} />
                </Field>
              )}

              <h3>Neighbouring wall</h3>
              <Field label="Wall on"
                hint="Shown in the model and plan. The roof overhang and gutter on that side eat into the gap.">
                <Chips value={wallSide} set={setWallSide}
                  opts={[{ value: "none", label: "None" }, ...SIDES.map((s) => ({ value: s.id, label: s.label }))]} />
              </Field>
              {wallSide !== "none" && (
                <>
                  <Field label="Measure the gap from"
                    hint={`The overhang reaches ${fmt(roof.clearance ? roof.clearance.reach : 0)} mm past the building face on that side${roof.clearance && roof.clearance.gutter ? ", the gutter included" : ""}, so the two are different figures. Measuring from the roof edge is what you do when the wall is already there: say how much clear space you want past the eaves and this works the frame back from it.`}>
                    <Chips value={wallGapFrom} set={setWallGapFrom}
                      opts={[{ value: "frame", label: "Building face" },
                             { value: "roof", label: "Roof edge" }]} />
                  </Field>
                  <Field
                    label={wallGapFrom === "roof"
                      ? "Gap, roof edge to wall (mm)"
                      : "Gap, building face to wall (mm)"}
                    hint={!roof.clearance ? "" : `Set the frame out ${fmt(roof.clearance.frameToWall)} mm from the wall and the roof lands ${fmt(roof.clearance.roofToWall)} mm off it. Battens and cladding take another 50–70 mm off the frame figure.`}>
                    <NumberField value={wallGap} onCommit={setWallGap} min={0} max={5000}
                      step={25} ariaLabel="Wall gap" />
                  </Field>
                  {roof.clearance && (
                    <div className={roof.clearance.clash ? "flag"
                      : roof.clearance.oversails ? "flag" : "flag good"}>
                      {roof.clearance.clash
                        ? `The roof runs into the wall by ${fmt(-roof.clearance.roofToWall)} mm. The wall stands ${fmt(roof.clearance.standsProud == null ? 0 : roof.clearance.standsProud)} mm above the underside of the roof there, so it is in the way — move the frame out to ${fmt(roof.clearance.frameToWall - roof.clearance.roofToWall)} mm to clear it.`
                        : roof.clearance.oversails
                          ? `The roof passes over the wall and oversails the boundary by ${fmt(-roof.clearance.roofToWall)} mm. Nothing collides — the wall stops ${fmt(-roof.clearance.standsProud)} mm below the underside of the roof — but an overhang across a boundary is somebody else's airspace.`
                          : `${fmt(roof.clearance.roofToWall)} mm clear past the eaves, frame set out ${fmt(roof.clearance.frameToWall)} mm.${roof.clearance.standsProud != null && roof.clearance.standsProud < 0 ? ` The wall is ${fmt(-roof.clearance.standsProud)} mm below the underside of the roof anyway.` : ""}`}
                    </div>
                  )}
                </>
              )}
              {wallSide !== "none" && (
                <Field label="Wall height above ground (mm)">
                  <NumberField value={boundaryH} onCommit={setBoundaryH} min={300} max={4000}
                    step={50} ariaLabel="Boundary wall height" />
                </Field>
              )}
            </>
          )}
        </aside>
        )}

        <section className="stage">
          {["floor", "walls", "roof"].includes(nav) && (
            <div className="tabs" role="tablist">
              {(nav === "floor"
                ? [["model", "Model"], ["plan", "Plan"], ["deck", "Deck layout"], ["cuts", "Cut list"]]
                : nav === "walls"
                ? [["model", "Model"], ["elev", "Drawings"], ["cuts", "Cut list"]]
                : [["model", "Model"], ["plan", "Plan"], ["deck", "Deck"], ["pir", "Insulation"],
                   ["top", "Top deck"], ["cuts", "Cut list"]]
              ).map(([k, l]) => {
                const active = nav === "floor" ? fTab === k : nav === "walls" ? wTab === k : rTab === k;
                const setter = nav === "floor" ? setFTab : nav === "walls" ? setWTab : setRTab;
                return (
                  <button key={k} role="tab" aria-selected={active} className={active ? "tab on" : "tab"}
                    onClick={() => setter(k)}>
                    {l}
                  </button>
                );
              })}
            </div>
          )}

          <div className="panelbody">
            {/* ---------------- START ---------------- */}
            {nav === "start" && (
              <div className="scroll">
                <h2>What this is</h2>
                <p className="note">
                  A parametric model of a garden pod — floor, stud walls and a warm flat roof.
                  Set the dimensions and the stock you're buying, and it works out the joist and
                  stud layout, the sheet cutting plan, the timber cut list and what to order. It
                  is a planning aid, not a set of construction drawings.
                </p>

                <h2>Start with an example</h2>
                <div className="exgrid">
                  {EXAMPLES.map((ex) => (
                    <button className="excard" key={ex.id} onClick={() => loadExample(ex)}>
                      <h4>{ex.name}</h4>
                      <p>{ex.blurb}</p>
                      <p className="exnote">{ex.notes}</p>
                    </button>
                  ))}
                </div>
                <div className="iorow">
                  <button className="navbtn" onClick={() => applyDesign(DEFAULT_DESIGN)}>
                    Back to defaults
                  </button>
                  <button className="navbtn" onClick={() => setNav("materials")}>
                    Load a saved design
                  </button>
                </div>

                <h2>Or set the basics and go</h2>
                <div className="setup">
                  <div>
                    <label>Length (mm)</label>
                    <NumberField value={fL} onCommit={setFL} min={500} max={12000} ariaLabel="Length" />
                  </div>
                  <div>
                    <label>Width (mm)</label>
                    <NumberField value={fW} onCommit={setFW} min={500} max={8000} ariaLabel="Width" />
                  </div>
                  <div>
                    <label>Internal ceiling (mm)</label>
                    <NumberField value={ceilingHeight} onCommit={setCeilingHeight} min={1500}
                      max={4000} step={50} ariaLabel="Internal ceiling height" />
                  </div>
                  <div>
                    <label>Ground clearance (mm)</label>
                    <NumberField value={groundClear} onCommit={setGroundClear} min={0} max={600}
                      step={10} ariaLabel="Ground clearance" />
                  </div>
                  <div>
                    <label>OSB sheet size</label>
                    <select value={sheetFor[18] || "2397"}
                      onChange={(e) =>
                        setSheetFor((cur) => {
                          const v = e.target.value;
                          return { ...cur, 11: v, 15: v, 18: v, 22: v };
                        })}>
                      {SHEETS.map((s) => (
                        <option key={s.id} value={s.id}>{s.label} · {s.note}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label>Timber stock length</label>
                    <select value={stock} onChange={(e) => setStock(Number(e.target.value))}>
                      {STOCK.map((s) => <option key={s} value={s}>{s} mm</option>)}
                    </select>
                  </div>
                </div>
                <p className="note">
                  That gives an overall height of <b>{fmt(heights.overall)} mm</b> from the ground to
                  the top of the roof deck. Everything else has a sensible default and can be
                  changed as you go — the rest live under <b>Settings</b>.
                </p>
                <div className="iorow">
                  <button className="navbtn on" onClick={() => setNav("floor")}>
                    Start with the floor
                  </button>
                  <button className="navbtn" onClick={() => setNav("materials")}>
                    Go to settings
                  </button>
                </div>

                <h2>The order to work in</h2>
                <p className="note">
                  <b>Settings</b> first — the building's overall size and height, which timber
                  sections you're buying, one sheet size per OSB thickness, and how you want the
                  cutting optimised. Then <b>Floor</b>, <b>Walls</b> and <b>Roof</b> assign from that
                  stock. Each view builds on the last: Floor shows the floor, Walls stands the walls
                  on that same floor, and Roof puts the roof on those same walls — one model, three
                  depths of it. Settings also holds the order summary, the offcut pool, the STL
                  export and save/load.
                </p>

                <h2>Three ideas worth knowing</h2>
                <div className="docgrid">
                  <div className="doc">
                    <h4>Sheets are planned before framing</h4>
                    <p>
                      Joists and studs are placed under the sheet joints rather than on a blind
                      grid, then bays are infilled so none exceeds your maximum spacing. Green
                      joints on the layout views have timber behind them, red don't — a sheet edge
                      landing on fresh air is what gives you a bouncy floor or a soft wall, and it
                      doesn't show up on a cut list. The counters in the header say how many are
                      off a joist or stud; you want nought.
                    </p>
                  </div>
                  <div className="doc">
                    <h4>One pile of stock</h4>
                    <p>
                      Sections and sheet sizes are global, so a 45 × 120 in the floor is the same
                      45 × 120 in the upstand and packs from the same lengths. Sheet offcuts go
                      into a shared pool that later layers draw on before opening a new sheet.
                    </p>
                  </div>
                  <div className="doc">
                    <h4>Height is a chain</h4>
                    <p>
                      Ground clearance, floor frame, deck, ceiling, roof joists, deck, insulation
                      and top deck all add up. Only the internal ceiling height is free — change a
                      section and the overall height moves with it.
                    </p>
                  </div>
                </div>

                <h2>Moving around the 3D views</h2>
                <p className="keys">
                  Drag to orbit · scroll or pinch to zoom · two fingers to pan. Click the view
                  first, then <kbd>↑</kbd><kbd>↓</kbd><kbd>←</kbd><kbd>→</kbd> to pan,{" "}
                  <kbd>Shift</kbd> + arrows to pan further, <kbd>Alt</kbd> + arrows to orbit,{" "}
                  <kbd>+</kbd> <kbd>−</kbd> to zoom and <kbd>Home</kbd> to reset the view.
                </p>

                <h2>What it doesn't do</h2>
                <div className="flag">
                  No door yet, and only one window. No structural checking — nothing here verifies
                  a section can span what you've asked of it. Cut lists assume timber milled to
                  exactly its nominal section, which it rarely is, so measure your actual stock
                  before cutting. Nothing produced here is building-regulations compliant or
                  suitable for a planning application.
                </div>
              </div>
            )}

            {/* ---------------- FLOOR ---------------- */}
            {nav === "floor" && fTab === "model" && (
              <>
                <Model boxes={floorBoxes} ghost={renderMode === "ghost"} dims={floorDims}
                  showDims={shown("dims")} view={viewFor("floor")} captureRef={camFloor} onPick={setPicked}
                  footprint={{ L: fL, W: fW, cx: geom.cxFloor, cz: geom.czFloor,
                    groundY: 0, focusY: geom.floorTop / 2 }} />
                <div className="legend">
                  <span>{pickInfo
                    ? <b className="picked">{pickInfo.what} · {pickInfo.size}</b>
                    : "Drag to orbit, scroll or pinch to zoom. Click a piece to identify it."}</span>
                  <span><i className="swatch" style={{ background: palette.timber }} />Joists</span>
                  <span><i className="swatch" style={{ background: palette.osb }} />Deck</span>
                  <span>{fmt(groundClear)} mm clear of the ground</span>
                  <span className="camctl">
                    <button className="linkbtn" onClick={() => saveView("floor", camFloor)}>Save view</button>
                    <button className="linkbtn" onClick={() => clearView("floor", camFloor)}>
                      {views && views.floor ? "Reset to default" : "Reset view"}
                    </button>
                  </span>
                </div>
              </>
            )}
            {nav === "floor" && fTab === "plan" && (
              <div className="drawwrap">
                <PlanView frame={floor.frame} plan={floor.deck} L={fL} W={fW} spacing={fSpacing}
                  wall={{ ...wall, bL: fL, bW: fW }}
                  fixings={show.includes("fixings") ? fixingsFor("floor") : null} />
              </div>
            )}
            {nav === "floor" && fTab === "deck" && (
              <>
                <div className="drawwrap">
                  <SheetView plan={floor.deck} support={floor.support} L={fL} W={fW} gap={gap}
                    caption={`${fDeckT} mm OSB3 · ${floorBoard.label} · ${floor.deck.cuts} pieces to cut`} />
                </div>
                <div className="scroll" style={{ flex: "0 0 auto", paddingTop: 0 }}>
                  {floor.deck.whole === 0 && (
                    <div className="flag">
                      No sheet lands whole in this layout — every piece gets cut.{" "}
                      {floor.deck.impossible
                        ? `${floorBoard.label} is longer than the frame in both directions, so no orientation can fix it. Try a smaller sheet in Materials.`
                        : floor.deck.alternative && floor.deck.alternative.whole > 0
                          ? `Turning the sheets the other way gives ${floor.deck.alternative.whole} whole sheet${floor.deck.alternative.whole === 1 ? "" : "s"} at the cost of ${floor.deck.alternative.cuts} cuts instead of ${floor.deck.cuts} — set Sheet direction rather than leaving it on Auto if you'd rather have the whole sheets.`
                          : `Neither orientation gets a whole sheet at ${fmt(fL)} × ${fmt(fW)}.`}
                    </div>
                  )}
                  {floor.support.unsupported.length === 0 ? (
                    <div className="flag good">Every sheet joint lands on a joist or rim.</div>
                  ) : (
                    <div className="flag">
                      {floor.support.unsupported.length} joint(s) with no timber underneath
                      {floor.support.unsupported.map((j) => ` · ${j.axis.toUpperCase()} ${fmt(j.pos)}`)}.
                      Switch joist layout to “under sheet joints”.
                    </div>
                  )}
                  <p className="note">
                    {fSheets.bought} new sheet{fSheets.bought === 1 ? "" : "s"}
                    {fSheets.reused ? `, ${fSheets.reused} piece(s) from offcuts` : ""} · deck area{" "}
                    {m2(fL * fW)} m² · {floor.deck.cuts} piece
                    {floor.deck.cuts === 1 ? "" : "s"} to cut.
                    {floor.deck.alternative &&
                      ` Turning the sheets the other way would give ${floor.deck.alternative.cuts}.`}
                  </p>
                </div>
              </>
            )}
            {nav === "floor" && fTab === "cuts" && (
              <div className="scroll">
                <h2>Floor timber — {floorSection.label} mm</h2>
                <table>
                  <thead><tr><th>Piece</th><th>Length (mm)</th><th>Qty</th></tr></thead>
                  <tbody>
                    {floorCuts.map((x, i) => (
                      <tr key={i}><td>{x.role}</td><td>{fmt(x.len)}</td><td>{x.qty}</td></tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td>Total</td>
                      <td>{floorCuts.reduce((s, x) => s + (x.len * x.qty) / 1000, 0).toFixed(1)} m</td>
                      <td>{floorCuts.reduce((s, x) => s + x.qty, 0)}</td>
                    </tr>
                  </tfoot>
                </table>
                {fPirT > 0 && floor.pir.pieces.length > 0 && (
                  <>
                    <h2>Floor insulation — {fPirT} mm PIR</h2>
                    <table>
                      <thead><tr><th>Bay</th><th>Size (mm)</th><th>Qty</th></tr></thead>
                      <tbody>
                        {pirBays.map((x, i) => (
                          <tr key={i}>
                            <td>{x.dx >= x.dz ? "Along the joists" : "Across the joists"}</td>
                            <td>{fmt(x.dx)} × {fmt(x.dz)}</td>
                            <td>{x.qty}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td>Total</td>
                          <td>{m2(floor.pir.area)} m²</td>
                          <td>{floor.pir.bays}</td>
                        </tr>
                      </tfoot>
                    </table>
                    <p className="note">
                      Cut to fit each bay. {floor.pir.airGap
                        ? `${fPirT} mm in a ${floorSection.d} mm joist leaves a ${floor.pir.airGap} mm void. Held up flush with the underside of the deck, so the void falls below it — set the battens ${floor.pir.airGap} mm down from the top of the joist.`
                        : `Fills the ${floorSection.d} mm joist depth, so it sits flush with the top of the frame.`}{" "}
                      Foam the edges; the bays are cut tight, not gapped like the OSB.
                      {floor.pir.slivers > 0 && ` ${floor.pir.slivers} gap${floor.pir.slivers === 1 ? "" : "s"} under ${floor.pir.minBay} mm (${m2(floor.pir.sliverArea)} m² in total) are left off the list — foam those rather than cutting a strip that narrow.`}
                    </p>
                  </>
                )}
                <h2>Also needed</h2>
                <table>
                  <tbody>
                    <tr><td>OSB3 {fDeckT} mm, {floorBoard.label}</td><td>{fSheets.bought} sheets</td></tr>
                    {fPirT > 0 && (
                      <tr>
                        <td>PIR {fPirT} mm, {pirBoard.label}</td>
                        <td>
                          {pirFor("Floor insulation").bought} boards
                          {pirFor("Floor insulation").reused
                            ? ` · ${pirFor("Floor insulation").reused} from offcuts`
                            : ""}
                        </td>
                      </tr>
                    )}
                    {fixHangers && fixOn && (
                      <tr><td>Joist hangers</td><td>{fixings.byStage.floor.hanger}</td></tr>
                    )}
                    <tr>
                      <td>Insulation battens, 25 × 50 × 500 mm</td>
                      <td>{floor.frame.nBays * 4} pieces · {((floor.frame.nBays * 4 * 500) / 1000).toFixed(1)} m</td>
                    </tr>
                    <tr><td>Vapour control layer</td><td>{m2(fL * fW)} m² plus laps</td></tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* ---------------- WALLS ---------------- */}
            {nav === "walls" && wTab === "model" && (
              <>
                <Model boxes={wallBoxes} ghost={renderMode === "ghost"} dims={wallDims}
                  showDims={shown("dims")} view={viewFor("walls")} captureRef={camWalls} onPick={setPicked}
                  footprint={{ L: fL, W: fW, cx: geom.cxFloor, cz: geom.czFloor,
                    groundY: 0, focusY: geom.wallTop / 2 }} />
                <div className="legend">
                  <span>{pickInfo
                    ? <b className="picked">{pickInfo.what} · {pickInfo.size}</b>
                    : "Drag to orbit, scroll or pinch to zoom. Click a piece to identify it."}</span>
                  {colourBy === "layer" ? (
                    [["framing", "Studs"], ["sheathing", "Sheathing"], ["floorDeck", "Floor deck"]].map(([k, lab]) => (
                      <span key={k}>
                        <i className="swatch" style={{ background: LAYER_COLOURS[k] }} />{lab}
                      </span>
                    ))
                  ) : colourBy === "wall" ? (
                    SIDES.map((sd) => (
                      <span key={sd.id}>
                        <i className="swatch" style={{ background: palette.sides[sd.id] }} />
                        {sd.label}
                      </span>
                    ))
                  ) : (
                    <>
                      <span><i className="swatch" style={{ background: palette.timber }} />Studs</span>
                      <span><i className="swatch" style={{ background: palette.osb }} />Sheathing</span>
                    </>
                  )}
                  <span><i className="swatch" style={{ background: palette.header }} />Header</span>
                  <span><i className="swatch" style={{ background: "#383E42" }} />Window frame</span>
                  <span><i className="swatch" style={{ background: palette.header }} />Header</span>
                  <span><i className="swatch" style={{ background: "#383E42" }} />Window frame</span>
                  <span className="camctl">
                    <button className="linkbtn" onClick={() => saveView("walls", camWalls)}>Save view</button>
                    <button className="linkbtn" onClick={() => clearView("walls", camWalls)}>
                      {views && views.walls ? "Reset to default" : "Reset view"}
                    </button>
                  </span>
                </div>
              </>
            )}
            {nav === "walls" && wTab === "elev" && (
              <div className="scroll">
                <div className="elevgrid">
                  {walls.walls.map((w, wi) => (
                    <div className="elevcell" key={w.side}>
                      <h4>
                        {sideLabel(w.side)} wall{" "}
                        <span>
                          {fmt(w.frame.len)} × {fmt(walls.height)} ·{" "}
                          {w.frame.glazed
                            ? "glazed wall"
                            : `${w.frame.n + 2} studs`}
                          {w.frame.opening ? " · opening" : ""}
                        </span>
                      </h4>
                      <ElevationView
                        battens={battens && show.includes("battens")
                          ? battens.per[wi] : null}
                        wall={{ ...w, gapMm: winGap, fall,
                          frameWidth: w.frame.opening && w.frame.opening.isDoor
                            ? doorFrameWidth : frameWidth }}
                        walls={walls} label={sideLabel(w.side)}
                        tint={colourBy === "wall" ? palette.sides[w.side] : null}
                        headerHex={palette.header}
                        fixings={show.includes("fixings") ? fixingsFor(`wall-${w.side}`) : null} />
                    </div>
                  ))}
                </div>

                {fall.on && walls.walls.some((w) => w.tapered) && (
                  <>
                    <h2>Marking out the angled sheets</h2>
                    <p className="note">
                      The two walls running down the fall have their top sheet cut to the angle.
                      Sheets arrive square, so set out from the bottom edge: measure up the two
                      heights below at each end of the piece and strike a line between them. The
                      hatched wedge on the drawings is what comes off.
                    </p>
                    <table>
                      <thead>
                        <tr>
                          <th>Piece</th><th>Along the wall</th><th>Width</th>
                          <th>Height, gutter end</th><th>Height, far end</th><th>Off the sheet</th>
                        </tr>
                      </thead>
                      <tbody>
                        {walls.walls.filter((w) => w.tapered).map((w) => {
                          const cuts = angledSheetCuts(w, fall) || [];
                          return cuts.map((c, i) => (
                            <tr key={`${w.side}-${i}`}>
                              <td>{sideLabel(w.side)} {cuts.length > 1 ? `#${i + 1}` : ""}</td>
                              <td>{fmt(c.from)} – {fmt(c.to)} mm</td>
                              <td>{fmt(c.width)}</td>
                              <td>{fmt(Math.min(c.hStart, c.hEnd))}</td>
                              <td>{fmt(Math.max(c.hStart, c.hEnd))}</td>
                              <td>{fmt(c.waste)} mm wedge</td>
                            </tr>
                          ));
                        })}
                      </tbody>
                    </table>
                    {walls.walls.filter((w) => w.tapered)
                      .flatMap((w) => angledSheetCuts(w, fall) || [])
                      .some((c) => !c.ok) && (
                      <div className="flag">
                        The fall eats the whole of the top sheet at the gutter end. Either reduce
                        the rise, or change the sheet size so the top row is deeper than the{" "}
                        {fmt(fall.rise)} mm rise.
                      </div>
                    )}
                    <p className="note">
                      Rows on these two walls are set out from the top, so the angle is cut on a
                      full sheet and any short piece lands at the bottom where the floor frame
                      covers it.
                    </p>
                  </>
                )}

                <h2>Every wall</h2>
                <table>
                  <thead>
                    <tr>
                      <th>Wall</th><th>Frame</th><th>Sheathing</th>
                      <th>Studs</th><th>Furring</th><th>Widest bay</th>
                    </tr>
                  </thead>
                  <tbody>
                    {walls.walls.map((w) => (
                      <tr key={w.side}>
                        <td>{sideLabel(w.side)}</td>
                        <td>{fmt(w.frame.len)} × {fmt(walls.height)}</td>
                        <td>{fmt(w.sheath)} × {fmt(walls.sheathH)}</td>
                        <td>
                          {w.frame.glazed
                            ? `4 jack`
                            : w.frame.n + 2}
                        </td>
                        <td>
                          {w.furringInHeader
                            ? "in the header"
                            : w.lift
                              ? w.tapered
                                ? `${fmt(fall.low)} → ${fmt(fall.high)} tapered`
                                : `${fmt(w.lift)} packer`
                              : "—"}
                        </td>
                        <td>{w.frame.maxBay ? fmt(w.frame.maxBay) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {walls.unsupported === 0 ? (
                  <div className="flag good">
                    Every vertical sheathing joint has timber behind it, on all four walls.
                  </div>
                ) : (
                  <div className="flag">
                    {walls.unsupported} sheathing joint(s) across the four walls have no stud
                    behind them. Switch stud layout to “under sheet joints”.
                  </div>
                )}

                {walls.doored && (() => {
                  const w = walls.doored;
                  const o = w.frame.opening;
                  return (
                    <>
                      {!o.fitsWidth && (
                        <div className="flag">
                          The door and its trimmers don't fit the{" "}
                          {sideLabel(w.side).toLowerCase()} wall at that position. Widest here is{" "}
                          {fmt(o.maxWidth)} mm; move it away from the corner or narrow it.
                        </div>
                      )}
                      {!o.fitsHeight && (
                        <div className="flag">
                          No room for the door header. A {fmt(doorH)} mm door with a{" "}
                          {fmt(o.headerH)} mm header needs more than the {fmt(walls.height)} mm
                          wall gives.
                        </div>
                      )}
                      {o.fitsWidth && o.fitsHeight && (
                        <div className="flag good">
                          Door on the {sideLabel(w.side).toLowerCase()} wall:{" "}
                          {fmt(o.roW)} × {fmt(o.roH)} rough opening from the floor, running{" "}
                          {fmt(o.x0)}–{fmt(o.x1)} mm along the wall. Bottom plate cut away across
                          it, no sill and no cripples below.{" "}
                          {o.fullHeader
                            ? ` The header runs the full ${fmt(o.headerLen || walls.doored.len)} mm of the wall and replaces the top plate, so the studs and trimmers stop under it and the door reaches its underside at ${fmt(o.roTop || o.headerY)} mm.`
                            : o.fillsToPlate
                            ? ` Only ${fmt(o.headroom)} mm was left above it, so the header is deepened to ${fmt(o.headerDepth)} mm and runs right up to the top plate — one piece rather than a header and a packer.`
                            : o.topCripple
                              ? ` ${o.cripples.length} top cripples above the header.`
                              : ""}
                          {doorSizes
                            ? ` Frame ${fmt(frameDepth)} mm deep on a ${fmt(doorSizes.fw)} mm face, giving a ${fmt(doorSizes.glassW)} × ${fmt(doorSizes.glassH)} ${doorSolid ? "leaf" : "glazed panel"}.`
                            : ""}
                        </div>
                      )}
                      {w.clash && (
                        <div className="flag">
                          A window is also set on this wall. Only one opening per wall is modelled,
                          so the window has been dropped — put it on another wall.
                        </div>
                      )}
                    </>
                  );
                })()}

                {walls.opened && walls.opened.frame.opening && (() => {
                  const w = walls.opened;
                  const o = w.frame.opening;
                  return (
                    <>
                      {o.glazed && (
                        <div className={o.fitsHeight ? "flag good" : "flag"}>
                          {o.fitsHeight
                            ? `${sideLabel(w.side)} wall is glazed: the ${fmt(o.headerLen)} mm header runs the full width and replaces the top plate, its top level with the single plate on the other three. A pair of ${fmt(walls.t)} mm studs at each end carries it, ${fmt(o.studLen)} mm long. ${o.cripples.length} bottom cripples, nothing above.`
                            : `Sill needs to sit between ${fmt(o.minSill)} and ${fmt(o.maxSill)} mm for this to work.`}
                        </div>
                      )}
                      {!o.glazed && !o.fitsWidth && (
                        <div className="flag">
                          Opening too wide for the {sideLabel(w.side).toLowerCase()} wall — maximum
                          is {fmt(o.maxWidth)} mm.
                        </div>
                      )}
                      {!o.glazed && !o.fitsHeight && (
                        <div className="flag">
                          No room for the header. Sill needs to be {fmt(o.maxSill)} mm or lower.
                        </div>
                      )}
                      {o.plyShort < -2 && (
                        <div className="flag">
                          Header is {o.plies} × {fmt(o.plyThickness)} mm = {fmt(o.plyTotal)} mm in
                          an {fmt(walls.wt)} mm wall, so it sits {fmt(-o.plyShort)} mm proud of the
                          studs. Lay it the other way or pick another section.
                        </div>
                      )}
                      {winSizes && (
                        <p className="note">
                          {fmt(winSizes.frameW)} × {fmt(winSizes.frameH)} frame with{" "}
                          {fmt(winSizes.glassW)} × {fmt(winSizes.glassH)} of glass, sill{" "}
                          {fmt(winSill)} mm above the floor. Sheathing is drawn with the opening cut
                          out; the sheet count {wSheathCut === "over"
                            ? "assumes whole sheets fitted and the hole cut after"
                            : "counts the pieces planned around it"}.
                        </p>
                      )}
                    </>
                  );
                })()}

                <p className="note">
                  Sheathing is shown ghosted behind the framing on each elevation, with the
                  {" "}{fmt(walls.skirt)} mm that laps down over the floor frame shaded below the
                  bottom plate. Green joint lines have a stud behind them, red don't.
                </p>
              </div>
            )}

            {nav === "walls" && wTab === "cuts" && (
              <div className="scroll">
                <h2>Wall timber — {wallSection.label} mm, all four walls</h2>
                <table>
                  <thead><tr><th>Piece</th><th>Length (mm)</th><th>Qty</th></tr></thead>
                  <tbody>
                    {walls.cuts.map((x, i) => (
                      <tr key={i}><td>{x.role}</td><td>{fmt(x.len)}</td><td>{x.qty}</td></tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td>Total</td>
                      <td>{walls.cuts.reduce((s, x) => s + (x.len * x.qty) / 1000, 0).toFixed(1)} m</td>
                      <td>{walls.cuts.reduce((s, x) => s + x.qty, 0)}</td>
                    </tr>
                  </tfoot>
                </table>

                {wPirT > 0 && wallPir.pieces.length > 0 && (
                  <>
                    <h2>Wall insulation — {wPirT} mm PIR</h2>
                    <table>
                      <thead><tr><th>Bay</th><th>Size (mm)</th><th>Qty</th></tr></thead>
                      <tbody>
                        {wallPirBays.map((x, i) => (
                          <tr key={i}>
                            <td>{x.dy >= x.dx ? "Upright" : "Between noggins"}</td>
                            <td>{fmt(x.dx)} × {fmt(x.dy)}</td>
                            <td>{x.qty}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td>Total</td>
                          <td>{m2(wallPir.area)} m²</td>
                          <td>{wallPir.total}</td>
                        </tr>
                      </tfoot>
                    </table>
                    <table>
                      <thead><tr><th>Wall</th><th>Bays</th><th>Area</th></tr></thead>
                      <tbody>
                        {walls.walls.map((w) => (
                          <tr key={w.side}>
                            <td>{sideLabel(w.side)}</td>
                            <td>{wallPir[w.side].bays}</td>
                            <td>{m2(wallPir[w.side].area)} m²</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="note">
                      Cut to fit between the studs and noggins. Openings are skipped —
                      a glazed wall still gets the band below its sill.{" "}
                      {wallPir[walls.walls[0].side].airGap
                        ? `${wPirT} mm in a ${wallSection.d} mm stud leaves a ${wallPir[walls.walls[0].side].airGap} mm void, which is a service gap on the warm side if you want one.`
                        : `Fills the ${wallSection.d} mm stud depth.`}
                      {wallPir.slivers > 0 && ` ${wallPir.slivers} gap${wallPir.slivers === 1 ? "" : "s"} under 50 mm are left off the list — foam those.`}
                    </p>
                    <table>
                      <tbody>
                        <tr>
                          <td>PIR {wPirT} mm, {pirBoard.label}</td>
                          <td>
                            {pirFor("Wall insulation").bought} boards
                            {pirFor("Wall insulation").reused
                              ? ` · ${pirFor("Wall insulation").reused} from offcuts`
                              : ""}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </>
                )}

                {walls.opened && walls.opened.frame.opening && (
                  <>
                    <h2>Opening — {sideLabel(walls.opened.side)} wall</h2>
                    <table>
                      <tbody>
                        <tr>
                          <td>Window frame</td>
                          <td>
                            {winSizes ? `${fmt(winSizes.frameW)} × ${fmt(winSizes.frameH)}` : `${fmt(winW)} × ${fmt(winH)}`} mm
                          </td>
                        </tr>
                        <tr>
                          <td>Rough opening (window + {winGap} mm all round)</td>
                          <td>{fmt(walls.opened.frame.opening.roW)} ×{" "}
                            {fmt(walls.opened.frame.opening.roH)} mm</td>
                        </tr>
                        <tr><td>Sill above floor</td><td>{fmt(winSill)} mm</td></tr>
                        {winSizes && (
                          <tr>
                            <td>Frame {frameDepth} mm deep, {winSizes.fw} mm face</td>
                            <td>
                              {fmt(winSizes.frameW)} × {fmt(winSizes.frameH)} frame ·{" "}
                              {fmt(winSizes.glassW)} × {fmt(winSizes.glassH)} glass
                            </td>
                          </tr>
                        )}
                        <tr>
                          <td>Header ({headerSection.label}, doubled)</td>
                          <td>{fmt(walls.opened.frame.opening.roW + 2 * walls.t)} mm long ×{" "}
                            {fmt(walls.opened.frame.opening.headerH)} mm deep</td>
                        </tr>
                        <tr>
                          <td>Headroom above header</td>
                          <td>{fmt(walls.opened.frame.opening.headroom)} mm</td>
                        </tr>
                      </tbody>
                    </table>
                    <p className="note">
                      Sheathing is drawn with the opening cut out either way. {wSheathCut === "over"
                        ? "The sheet count assumes whole sheets fitted and the hole cut afterwards; what comes out of the hole is scrap, and counted as thrown away."
                        : "The sheet count is of the pieces planned around the opening, so no board is cut away."} Set under Walls → At the openings.
                    </p>
                  </>
                )}

                <h2>Wall sizes</h2>
                <table>
                  <thead><tr><th>Wall</th><th>Frame</th><th>Sheathing</th><th>Studs</th></tr></thead>
                  <tbody>
                    {walls.walls.map((w) => (
                      <tr key={w.side}>
                        <td>{sideLabel(w.side)}</td>
                        <td>{fmt(w.frame.len)} × {fmt(walls.height)}</td>
                        <td>{fmt(w.sheath)} × {fmt(walls.sheathH)}</td>
                        <td>{w.frame.n + 2}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="note">
                  {wLayout === "lengthThrough"
                    ? `Front and rear run the full ${fmt(fL)} mm; left and right fit between them at ${fmt(walls.walls[2].len)} mm.`
                    : `Left and right run the full ${fmt(fW)} mm; front and rear fit between them at ${fmt(walls.walls[0].len)} mm.`}{" "}
                  {wLap === "lengthOver"
                    ? `Sides are sheathed first to their exact face length, then front and rear run ${walls.sheathT} mm long at each end to lap over them.`
                    : `Front and rear are sheathed first to their exact face length, then the sides run ${walls.sheathT} mm long at each end to lap over them.`}{" "}
                  All four run {fmt(walls.skirt)} mm below the bottom plate to cover the floor frame.
                </p>

                <h2>Sheathing</h2>
                <table>
                  <tbody>
                    <tr><td>OSB3 {wSheathT} mm, {sheathBoard.label}</td><td>{wSheets.bought} sheets</td></tr>
                    <tr><td>From earlier offcuts</td><td>{wSheets.reused || "—"}</td></tr>
                    <tr><td>Total sheathed area</td><td>{m2(walls.sheathArea)} m²</td></tr>
                  </tbody>
                </table>
                <div className="flag">
                  Simplified: no window or door openings, headers, cripples or cap plate yet, and no
                  breather membrane. Real wall frames change a lot once openings are in.
                </div>
              </div>
            )}

            {/* ---------------- ROOF ---------------- */}
            {nav === "roof" && rTab === "model" && (
              <>
                <Model boxes={roofBoxes} ghost={renderMode === "ghost"} dims={roofDims}
                  showDims={shown("dims")} view={viewFor("roof")} captureRef={camRoof} onPick={setPicked}
                  footprint={{ L: roof.L, W: roof.W, cx: geom.cxRoof, cz: geom.czRoof,
                    groundY: 0, focusY: geom.roofTop / 2 }} />
                <div className="legend">
                  <span>{pickInfo
                    ? <b className="picked">{pickInfo.what} · {pickInfo.size}</b>
                    : "Drag to orbit, scroll or pinch to zoom. Click a piece to identify it."}</span>
                  {colourBy === "layer"
                    ? [["framing", "Framing"], ["roofDeck", `Deck ${rDeckT}`],
                       ["upstand", "Upstand"], ["pir", `PIR ${roofPirT}`],
                       ["topDeck", `Top deck ${topT}`]].map(([k, lab]) => (
                        <span key={k}>
                          <i className="swatch" style={{ background: LAYER_COLOURS[k] }} />{lab}
                        </span>
                      ))
                    : (
                      <>
                        <span><i className="swatch" style={{ background: palette.timber }} />Framing</span>
                        <span><i className="swatch" style={{ background: palette.osb }} />OSB</span>
                        <span><i className="swatch" style={{ background: palette.upstand }} />Upstand</span>
                        <span><i className="swatch" style={{ background: palette.pir }} />PIR</span>
                      </>
                    )}
                  {explode ? <span>exploded {explode} mm</span> : <span>on the real floor and walls</span>}
                  <span className="camctl">
                    <button className="linkbtn" onClick={() => saveView("roof", camRoof)}>Save view</button>
                    <button className="linkbtn" onClick={() => clearView("roof", camRoof)}>
                      {views && views.roof ? "Reset to default" : "Reset view"}
                    </button>
                  </span>
                </div>
              </>
            )}
            {nav === "roof" && rTab === "plan" && (
              <div className="drawwrap">
                <PlanView frame={roof.frame} plan={roof.deck} L={roof.L} W={roof.W}
                  spacing={rSpacing} gutter={roof.gutter}
                  wall={{ ...wall, bL: fL, bW: fW, offsetX: ohLeft, offsetZ: ohRear }}
                  fixings={show.includes("fixings") ? fixingsFor("roof") : null} />
              </div>
            )}
            {nav === "roof" && rTab === "deck" && (
              <>
                <div className="drawwrap">
                  <SheetView plan={roof.deck} support={roof.support} L={roof.L} W={roof.W} gap={gap}
                    caption={`Structural deck · ${rDeckT} mm OSB3`} />
                </div>
                <div className="scroll" style={{ flex: "0 0 auto", paddingTop: 0 }}>
                  {roof.deck.whole === 0 && (
                    <div className="flag">
                      No sheet lands whole on the roof.{" "}
                      {roof.deck.impossible
                        ? `${roofBoard.label} is longer than ${fmt(roof.L)} × ${fmt(roof.W)} in both directions.`
                        : roof.deck.alternative && roof.deck.alternative.whole > 0
                          ? `The other orientation gives ${roof.deck.alternative.whole} whole, with ${roof.deck.alternative.cuts} cuts.`
                          : "Neither orientation manages one at this size."}
                    </div>
                  )}
                  {roof.support.unsupported.length === 0 ? (
                    <div className="flag good">Every deck joint lands on a joist or rim.</div>
                  ) : (
                    <div className="flag">
                      {roof.support.unsupported.length} unsupported joint(s)
                      {roof.support.unsupported.map((j) => ` · ${j.axis.toUpperCase()} ${fmt(j.pos)}`)}.
                    </div>
                  )}
                  {roof.clearance && roof.clearance.roofToWall < 0 && (
                    <div className="flag">
                      The {sideLabel(roof.clearance.side).toLowerCase()} overhang is{" "}
                      {fmt(roof.clearance.overhang)} mm
                      {roof.clearance.gutter ? ` plus ${fmt(roof.clearance.gutter)} mm of gutter` : ""}, but
                      the wall is only {fmt(roof.clearance.gap)} mm away. The roof overlaps it by{" "}
                      {fmt(-roof.clearance.roofToWall)} mm.
                    </div>
                  )}
                  <p className="note">
                    {rSheets.bought} new sheet{rSheets.bought === 1 ? "" : "s"}
                    {rSheets.reused ? `, ${rSheets.reused} piece(s) from earlier offcuts` : ""} ·{" "}
                    {roof.deck.cuts} piece{roof.deck.cuts === 1 ? "" : "s"} to cut.
                  </p>
                </div>
              </>
            )}
            {nav === "roof" && rTab === "pir" && (
              <>
                <div className="drawwrap">
                  {/* One drawing per layer: the whole point of a second one is
                      that its joints land somewhere else, which you can only
                      see side by side. */}
                  {pirAsCut.map((plan, i) => (
                    <SheetView key={i} plan={plan} L={roof.L} W={roof.W} gap={0} kind="pir"
                      outline={{ x: roof.upstand.uw, z: roof.upstand.uw,
                                 dx: roof.L - 2 * roof.upstand.uw, dz: roof.W - 2 * roof.upstand.uw }}
                      caption={`${roof.pirLayers > 1
                        ? `Layer ${i + 1} of ${roof.pirLayers} · ${roof.pirLayerT} mm PIR`
                        : `${roofPirT} mm PIR`} inside the upstand · ${plan.pieces.length} board${plan.pieces.length === 1 ? "" : "s"} as cut · butted tight, foam the gaps`} />
                  ))}
                </div>
                <div className="scroll" style={{ flex: "0 0 auto", paddingTop: 0 }}>
                  {!roof.upstandMatchesPir && (
                    <div className="flag">
                      Upstand is {roof.upstand.uh} mm and insulation is {roofPirT} mm. Match them, or the
                      top deck sits proud of one and bridges the other.
                    </div>
                  )}
                  {roof.pirLayers > 1 && (
                    <div className={roof.pirStagger >= PIR_STAGGER_MIN ? "flag good" : "flag"}>
                      {roof.pirStagger >= PIR_STAGGER_MIN
                        ? `Joints in layer 2 clear layer 1 by ${fmt(roof.pirStagger)} mm, so nothing runs straight through the tray.`
                        : `Joints in layer 2 come within ${fmt(roof.pirStagger)} mm of layer 1's — under the ${PIR_STAGGER_MIN} mm worth having. On this tray no layout offsets them further; tape the joints and foam them.`}
                    </div>
                  )}
                  <p className="note">
                    Tray inside the upstand: {fmt(roof.L - 2 * roof.upstand.uw)} ×{" "}
                    {fmt(roof.W - 2 * roof.upstand.uw)} mm, {m2(roof.pir.area)} m²
                    {roof.pirLayers > 1 ? ` per layer, ${roof.pirLayers} layers` : ""}.{" "}
                    {pirFor("Roof insulation").bought} board
                    {pirFor("Roof insulation").bought === 1 ? "" : "s"} of {pirBoard.label}
                    {pirFor("Roof insulation").reused
                      ? `, ${pirFor("Roof insulation").reused} piece(s) from offcuts`
                      : ""}.
                  </p>
                </div>
              </>
            )}
            {nav === "roof" && rTab === "top" && (
              <>
                <div className="drawwrap">
                  <SheetView plan={roof.top} L={roof.L} W={roof.W} gap={gap}
                    caption={`Top deck · ${topT} mm OSB3 over the insulation`} />
                </div>
                <div className="scroll" style={{ flex: "0 0 auto", paddingTop: 0 }}>
                  <p className="note">
                    {tSheets.bought} new sheet{tSheets.bought === 1 ? "" : "s"}
                    {tSheets.reused ? `, ${tSheets.reused} piece(s) from offcuts` : ""}. Fix with
                    thermally broken fixings through the insulation into the deck below, and 50 mm
                    screws into the upstand at the perimeter. Every fixing must finish flush — the
                    EPDM goes straight over it.
                  </p>
                </div>
              </>
            )}
            {nav === "roof" && rTab === "cuts" && (
              <div className="scroll">
                <h2>Height, ground to roof</h2>
                <div className="stack">
                  {heights.rows.map((row, i) => (
                    <div className={row.key ? "layer head" : "layer"} key={i}>
                      <span>{row.label}</span>
                      <b>{fmt(row.mm)} mm</b>
                    </div>
                  ))}
                  <div className="layer" style={{ borderColor: "var(--ink)" }}>
                    <span>Overall height</span>
                    <b>{fmt(heights.overall)} mm</b>
                  </div>
                </div>
                <p className="note">
                  Only the internal ceiling height is free — everything else is set by the sections
                  and thicknesses you've chosen. To lose height, take it off a section rather than
                  the ceiling. EPDM adds about 1.5 mm on top and isn't counted.
                </p>
                {heights.overTarget > 0 ? (
                  <div className="flag">
                    {fmt(heights.overTarget)} mm over your {fmt(targetHeight)} mm target. A{" "}
                    {fmt(heights.maxCeiling)} mm ceiling would get you there, or shallower joists
                    would.
                  </div>
                ) : (
                  <div className="flag good">
                    {fmt(-heights.overTarget)} mm under your {fmt(targetHeight)} mm target. The
                    ceiling could rise to {fmt(heights.maxCeiling)} mm.
                  </div>
                )}

                <h2>Roof timber — {roofSection.label} mm</h2>
                <table>
                  <thead><tr><th>Piece</th><th>Length (mm)</th><th>Qty</th></tr></thead>
                  <tbody>
                    {roofCuts.map((x, i) => (
                      <tr key={i}><td>{x.role}</td><td>{fmt(x.len)}</td><td>{x.qty}</td></tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td>Total</td>
                      <td>{roofCuts.reduce((s, x) => s + (x.len * x.qty) / 1000, 0).toFixed(1)} m</td>
                      <td>{roofCuts.reduce((s, x) => s + x.qty, 0)}</td>
                    </tr>
                  </tfoot>
                </table>

                {fall.on && (
                  <>
                    <h2>Furring — {furrSection.label} mm</h2>
                    <table>
                      <thead><tr><th>Piece</th><th>Length (mm)</th><th>Qty</th></tr></thead>
                      <tbody>
                        {furr.map((row, i) => (
                          <tr key={i}>
                            <td>
                              {row.kind === "tapered"
                                ? `Tapered strip ${fmt(row.width)} mm wide, ${fmt(row.h0)} → ${fmt(row.h1)} mm on edge`
                                : `Packer ${fmt(row.width)} mm wide, ${fmt(row.h0)} mm on edge`}{" "}
                              <span style={{ color: "var(--soft)" }}>· {sideLabel(row.side).toLowerCase()} wall</span>
                            </td>
                            <td>{fmt(row.len)}</td>
                            <td>{row.qty}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="note">
                      Falls {fmt(fall.perMetre)} mm per metre toward the{" "}
                      {sideLabel(gutterSide).toLowerCase()} — about 1:{fall.ratio}, or{" "}
                      {fall.degrees.toFixed(1)}°.{" "}
                      {furrCover.n === 1
                        ? "One board a wall — two of the section will not fit the plate — "
                        : `${furrCover.n} boards side by side a wall, `}
                      packers as well as tapered strips, both off the same section.{" "}
                      {furrCover.bare > 0
                        ? `${fmt(furrCover.covered)} mm of board on a ${fmt(furrCover.wall)} mm plate, set out from the outside face, so the ${fmt(furrCover.bare)} mm left over is on the inside over the ceiling. Whole boards only: a rip that narrow is not a piece anybody cuts.`
                        : `${fmt(furrCover.covered)} mm of board on a ${fmt(furrCover.wall)} mm plate, which lands exactly.`}{" "}
                      The two walls running down the fall get
                      the tapered strips; the other two get a constant packer, and their sheathing is
                      cut square to it while the tapered walls are cut to the angle. Each piece runs
                      only its own wall's frame length, so it stops short of the adjoining wall's
                      plate rather than running into it.
                      {walls.walls.some((w) => w.furringInHeader)
                        ? " A glazed wall needs neither: its header is deepened or cut to the fall and carries the roof directly."
                        : ""}
                    </p>
                  </>
                )}

                <h2>Upstand — {roof.upstand.label} mm, {roof.upstand.onEdge ? "on edge" : "flat"} ({roof.upstand.uw} wide × {roof.upstand.uh} tall)</h2>
                <table>
                  <thead><tr><th>Piece</th><th>Length (mm)</th><th>Qty</th></tr></thead>
                  <tbody>
                    {roof.upstand.pieces.map((x, i) => (
                      <tr key={i}><td>{x.role}</td><td>{fmt(x.len)}</td><td>{x.qty}</td></tr>
                    ))}
                  </tbody>
                </table>

                <h2>EPDM &amp; trims</h2>
                <table>
                  <tbody>
                    <tr>
                      <td>Membrane, deck plus {roof.epdm.margin} mm all round</td>
                      <td>{fmt(roof.epdm.l)} × {fmt(roof.epdm.w)} mm · {m2(roof.epdm.area)} m²</td>
                    </tr>
                    <tr>
                      <td>Smallest roll width that covers it</td>
                      <td>{roof.epdm.roll ? `${fmt(roof.epdm.roll)} mm × ${fmt(roof.epdm.long)} mm` : "wider than stock rolls — needs a seam"}</td>
                    </tr>
                    <tr>
                      <td>Drip trim, {sideLabel(gutterSide).toLowerCase()} (gutter side)</td>
                      <td>{fmt(roof.gutter.run)} mm</td>
                    </tr>
                    <tr><td>Edge trim, remaining three sides</td><td>{fmt(roof.gutter.edgeTrim)} mm</td></tr>
                    <tr><td>Gutter run and downpipe</td><td>{fmt(roof.gutter.run)} mm</td></tr>
                  </tbody>
                </table>
                <p className="note">
                  Fall comes from the walls, not the roof frame — the furring strips on the side walls
                  tilt the whole deck toward the {sideLabel(gutterSide).toLowerCase()}.
                  The joists themselves are cut level.
                </p>
              </div>
            )}

            {/* ---------------- ENERGY ---------------- */}
            {nav === "energy" && (
              <div className="scroll">
                <div className="flag">
                  Advisory only. These are nominal conductivities and a
                  proportional-area calculation, not a certified assessment and not
                  evidence of compliance with anything. Use it to compare one build-up
                  against another, not to sign anything off.
                </div>

                <h2>Envelope</h2>
                <table>
                  <thead>
                    <tr><th>Element</th><th>Area (m²)</th><th>U (W/m²K)</th><th>Heat loss (W/K)</th></tr>
                  </thead>
                  <tbody>
                    {energy.envelope.elements.map((e) => (
                      <tr key={e.id}>
                        <td>
                          {e.label}
                          {e.paths.length > 1 && (
                            <span className="hint" style={{ display: "block", margin: 0 }}>
                              {r(e.paths[0].fraction * 100)}% insulated,{" "}
                              {r(e.paths[1].fraction * 100)}% timber
                            </span>
                          )}
                        </td>
                        <td>{e.area.toFixed(2)}</td>
                        <td>{e.u.toFixed(3)}</td>
                        <td>{(e.area * e.u).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td>Whole envelope</td>
                      <td>{energy.envelope.area.toFixed(2)}</td>
                      <td>{energy.envelope.u.toFixed(3)}</td>
                      <td>{energy.envelope.ua.toFixed(2)}</td>
                    </tr>
                  </tfoot>
                </table>
                <p className="note">
                  The timber fraction is counted off the framing that is actually
                  drawn, not assumed. A calculator that guesses 15% will flatter a pod
                  at {fmt(wSpacing)} mm centres.
                </p>
                {roofPirT < ROOF_PIR_MIN && (
                  <div className="flag">
                    Roof insulation is {roofPirT} mm. {ROOF_PIR_MIN} mm is the sensible
                    minimum up there — the roof is the biggest single surface and the
                    one the sun sits on all day. Below that it is the weak link
                    whatever you do to the walls.
                  </div>
                )}
                {[["fPirT", fPirT, "Floor", floorSection.d],
                  ["wPirT", wPirT, "Walls", wallSection.d]]
                  .filter(([, t]) => t === 0)
                  .map(([k, , where]) => (
                    <div className="flag" key={k}>
                      No insulation in the {where.toLowerCase()} — those bays are
                      counted as open cavity, which is why the number above looks the
                      way it does. Set a thickness in Settings.
                    </div>
                  ))}

                <h2>Peak heating</h2>
                <table>
                  <tbody>
                    <tr>
                      <td>Fabric, {energy.envelope.ua.toFixed(2)} W/K over {r(energy.loads.heating.dt)} K</td>
                      <td>{r(energy.loads.heating.fabric)} W</td>
                    </tr>
                    <tr>
                      <td>Ventilation, {airChanges} air changes an hour</td>
                      <td>{r(energy.loads.heating.ventilation)} W</td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr>
                      <td>{indoorWinter} °C inside against {outdoorWinter} °C outside</td>
                      <td>{r(energy.loads.heating.total)} W</td>
                    </tr>
                  </tfoot>
                </table>
                <p className="note">
                  {r(energy.loads.heating.perM2)} W per m² of floor.{" "}
                  {energy.loads.heating.total <= 1000
                    ? "A single panel heater or a small heat pump covers that."
                    : "That wants a proper heat source, not a plug-in panel."}
                </p>
                {energy.loads.heating.internal > 0 && (
                  <p className="note">
                    Your {r(energy.loads.heating.internal)} W of people and kit is{" "}
                    <b>not</b> taken off that figure. A heater is sized for an empty
                    cold room on the coldest morning — credit body heat and it cannot
                    bring the pod up before you get there, or after a weekend away.
                    Once you are in it and working, the heater is only making up{" "}
                    <b>{r(energy.loads.heating.occupied)} W</b>. Size on{" "}
                    {r(energy.loads.heating.total)} W, expect to use less.
                  </p>
                )}

                <h2>Peak cooling</h2>
                <table>
                  <tbody>
                    {energy.loads.cooling.gains.map((g) => (
                      <tr key={g.id}>
                        <td>Sun on the {g.label.toLowerCase()}, facing{" "}
                          {(ORIENTATIONS.find((o) => o.id === g.facing) || {}).label.toLowerCase()}</td>
                        <td>{r(g.watts)} W</td>
                      </tr>
                    ))}
                    <tr><td>People and kit</td><td>{r(energy.loads.cooling.internal)} W</td></tr>
                    <tr>
                      <td>Fabric, {r(energy.loads.cooling.dt)} K {energy.loads.cooling.dt >= 0 ? "warmer" : "cooler"} outside</td>
                      <td>{r(energy.loads.cooling.fabric)} W</td>
                    </tr>
                    <tr>
                      <td>Ventilation</td>
                      <td>{r(energy.loads.cooling.ventilation)} W</td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr>
                      <td>Holding {indoorSummer} °C against {outdoorSummer} °C outside</td>
                      <td>{r(Math.max(0, energy.loads.cooling.total))} W</td>
                    </tr>
                  </tfoot>
                </table>
                <p className="note">
                  {energy.loads.cooling.solar > energy.loads.cooling.total * 0.5
                    ? "Solar gain is most of this. Shading the glass buys more than any amount of insulation — an overhang, a blind, or a lower g-value."
                    : "Fabric and ventilation dominate here rather than the sun."}
                </p>

                <h2>What it assumes</h2>
                <p className="note">
                  Timber and OSB are given a conductivity, not a U-value — a U-value
                  belongs to an assembly, not a material. The figure through a stud is
                  worked out from the whole stack, surfaces included, the same way the
                  insulated bay beside it is.
                </p>
                <table>
                  <thead><tr><th>Material</th><th>λ (W/m·K)</th></tr></thead>
                  <tbody>
                    {[["PIR", energy.envelope.lambda.pir, "yours, above"],
                      ["Softwood timber", energy.envelope.lambda.timber, "nominal"],
                      ["OSB3", energy.envelope.lambda.osb, "nominal"],
                      ["EPDM", energy.envelope.lambda.epdm, "nominal"]].map(([k, v, note]) => (
                      <tr key={k}><td>{k} <span className="hint">· {note}</span></td><td>{v}</td></tr>
                    ))}
                  </tbody>
                </table>
                <table>
                  <thead><tr><th>Surface resistance</th><th>Inside</th><th>Outside</th></tr></thead>
                  <tbody>
                    {[["Wall", "wall"], ["Roof", "roof"], ["Floor", "floor"]].map(([k, id]) => (
                      <tr key={id}>
                        <td>{k}</td>
                        <td>{energy.envelope.surfaces[id].in.toFixed(2)}</td>
                        <td>{energy.envelope.surfaces[id].out.toFixed(2)}</td>
                      </tr>
                    ))}
                    <tr>
                      <td>Cavity behind a shallow board</td>
                      <td colSpan={2}>{energy.envelope.cavityR.toFixed(2)} m²K/W</td>
                    </tr>
                  </tbody>
                </table>

                <h2>What this leaves out</h2>
                <ul className="note">
                  {energy.envelope.excludes.map((x, i) => <li key={i}>{x}</li>)}
                </ul>
              </div>
            )}

            {/* ---------------- MATERIALS ---------------- */}
            {nav === "materials" && (
              <div className="scroll">
                <h2>Materials &amp; settings in use</h2>
                <table>
                  <thead><tr><th>Where</th><th>Material</th></tr></thead>
                  <tbody>
                    <tr>
                      <td>Floor joists</td>
                      <td>{floorSection.label} mm{floorSection.custom ? " (yours)" : " CLS"}</td>
                    </tr>
                    <tr>
                      <td>Roof joists</td>
                      <td>{roofSection.label} mm{roofSection.custom ? " (yours)" : " CLS"}</td>
                    </tr>
                    <tr>
                      <td>Roof upstand</td>
                      <td>{upstandSection.label} mm CLS, {roof.upstand.onEdge ? "on edge" : "flat"}</td>
                    </tr>
                    <tr>
                      <td>Wall studs</td>
                      <td>{wallSection.label} mm{wallSection.custom ? " (yours)" : " CLS"}</td>
                    </tr>
                    <tr><td>Floor deck</td><td>{fDeckT} mm OSB3, {floorBoard.label}</td></tr>
                    <tr><td>Wall sheathing</td><td>{wSheathT} mm OSB3, {sheathBoard.label}</td></tr>
                    <tr><td>Roof structural deck</td><td>{rDeckT} mm OSB3, {roofBoard.label}</td></tr>
                    <tr><td>Roof top deck</td><td>{topT} mm OSB3, {topBoard.label}</td></tr>
                    <tr><td>Roof insulation</td><td>
                      {roofPirT} mm PIR, {pirBoard.label}
                      {roof.pirLayers > 1 ? ` · ${roof.pirLayers} layers of ${roof.pirLayerT} mm` : ""}
                    </td></tr>
                  </tbody>
                </table>
                {new Set(timber.map((t) => t.sec.id)).size === 1 ? (
                  <div className="flag good">
                    One timber section across the whole build — floor, roof and upstand all come off
                    the same pile.
                  </div>
                ) : (
                  <p className="note">
                    {new Set(timber.map((t) => t.sec.id)).size} different sections in play. Fewer
                    sections means fewer part-used lengths at the end.
                  </p>
                )}
                {wallSide !== "none" && roof.clearance && (
                  <div className={roof.clearance.oversails ? "flag" : "flag good"}>
                    Wall on the {sideLabel(wallSide).toLowerCase()}: set the frame out{" "}
                    {fmt(roof.clearance.frameToWall)} mm and the roof edge lands{" "}
                    {fmt(roof.clearance.roofToWall)} mm off it, the{" "}
                    {fmt(roof.clearance.overhang)} mm overhang
                    {roof.clearance.gutter ? " and gutter" : ""} taken off.{" "}
                    {roof.clearance.clash
                      ? "The wall stands above the underside of the roof there, so they are in each other's way."
                      : roof.clearance.oversails
                        ? "The roof passes over the wall but oversails the boundary."
                        : "Battens and cladding will take another 50–70 mm of that."}
                  </div>
                )}

                <h2>Sheet goods</h2>
                <p className="note">
                  Each layer's cut pieces are matched against offcuts already on site before a new
                  sheet is opened. Remnants are tracked as guillotine rectangles, so every reuse
                  below is a cut you could actually make on a track saw.
                </p>

                {[...alloc.osb.values()].map((g) => {
                  const cross = g.log.filter((l) => l.reused && l.from && l.from !== l.module);
                  const saved = g.demands.length - g.bought;
                  const byModule = new Map();
                  g.log.forEach((l) => {
                    if (!byModule.has(l.module)) byModule.set(l.module, { new: 0, reused: 0 });
                    const e = byModule.get(l.module);
                    l.reused ? (e.reused += 1) : (e.new += 1);
                  });
                  return (
                    <div key={g.key} style={{ marginTop: 20 }}>
                      <h2 style={{ marginBottom: 6 }}>OSB3 {g.key}</h2>
                      <table>
                        <thead>
                          <tr><th>Layer</th><th>Pieces from new sheets</th><th>From offcuts</th></tr>
                        </thead>
                        <tbody>
                          {[...byModule.entries()].map(([mod, e]) => (
                            <tr key={mod}><td>{mod}</td><td>{e.new}</td><td>{e.reused || "—"}</td></tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td>Buy</td>
                            <td>{g.bought} sheets</td>
                            <td>{g.reused ? `${g.reused} saved` : "—"}</td>
                          </tr>
                        </tfoot>
                      </table>
                      {cross.length > 0 && (
                        <div className="flag good">
                          {cross.length} piece{cross.length === 1 ? "" : "s"} came out of offcuts left
                          by another layer
                          {cross.map((l, i) => (
                            <span key={i}>
                              {i ? ", " : ": "}
                              {fmt(l.dx)} × {fmt(l.dz)} for {l.module.toLowerCase()} from {l.from.toLowerCase()}
                            </span>
                          ))}
                          .
                        </div>
                      )}
                      {cross.length === 0 && g.pool.length > 0 && (
                        <p className="note">
                          Nothing crossed between layers here — the leftovers below are real, but no
                          later layer needed a piece small enough to fit one. Change a footprint or an
                          overhang and that can flip.
                        </p>
                      )}
                      {saved > 0 && (
                        <p className="note">
                          Nesting cut pieces into part-used sheets saves {saved}{" "}
                          sheet{saved === 1 ? "" : "s"} against opening a fresh one for every cut piece.
                        </p>
                      )}
                      {g.pool.length > 0 && (
                        <>
                          <p className="note">Usable offcuts left over:</p>
                          <div className="pills">
                            {g.pool
                              .sort((a, b) => b.l * b.w - a.l * a.w)
                              .map((p, i) => (
                                <span className="pill" key={i}>
                                  {fmt(p.l)} × {fmt(p.w)}
                                </span>
                              ))}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}

                {[...alloc.pir.values()].map((g) => {
                  const byModule = new Map();
                  g.log.forEach((l) => {
                    if (!byModule.has(l.module)) byModule.set(l.module, { new: 0, reused: 0 });
                    const e = byModule.get(l.module);
                    l.reused ? (e.reused += 1) : (e.new += 1);
                  });
                  const cross = g.log.filter((l) => l.reused && l.from && l.from !== l.module);
                  return (
                    <div key={g.key} style={{ marginTop: 20 }}>
                      <h2 style={{ marginBottom: 6 }}>{g.key}</h2>
                      <table>
                        <thead>
                          <tr><th>Layer</th><th>Pieces from new boards</th><th>From offcuts</th></tr>
                        </thead>
                        <tbody>
                          {[...byModule.entries()].map(([mod, e]) => (
                            <tr key={mod}><td>{mod}</td><td>{e.new}</td><td>{e.reused || "—"}</td></tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td>Buy</td>
                            <td>{g.bought} boards</td>
                            <td>{g.reused ? `${g.reused} saved` : "—"}</td>
                          </tr>
                        </tfoot>
                      </table>
                      {cross.length > 0 && (
                        <div className="flag good">
                          {cross.length} piece{cross.length === 1 ? "" : "s"} came out of offcuts left
                          by another layer — floor and roof are on the same thickness, so they share
                          one pile.
                        </div>
                      )}
                      {g.gaps > 0 && (
                        <div className="flag good">
                          {g.gaps} piece{g.gaps === 1 ? "" : "s"} fall{g.gaps === 1 ? "s" : ""} up
                          to {fmt(g.gapMax)} mm short of the bay, inside the {fmt(pirTol)} mm
                          tolerance. Foam and tape those edges.
                        </div>
                      )}
                      {g.pieced > 0 && (
                        <div className="flag good">
                          {g.pieced} piece{g.pieced === 1 ? "" : "s"} made up from butted offcuts
                          rather than opening a board
                          {madeUp(g).map((m, i) => (
                            <span key={i}>
                              {i ? ", " : " — "}
                              {m.n} in {m.module.toLowerCase()}, up to {m.parts} part
                              {m.parts === 1 ? "" : "s"} each
                            </span>
                          ))}. Tape and foam the butts.
                        </div>
                      )}
                      <details style={{ marginTop: 10 }}>
                        <summary className="note" style={{ cursor: "pointer" }}>
                          How each board is cut
                        </summary>
                        {cutOrder(g).map((bd) => (
                          <div key={bd.board}>
                          {/* A cut-out from an opening is board in hand rather
                              than a sheet, so it has no board-sized outline to
                              draw against — its rows are listed instead. */}
                          {bd.board.startsWith("sheet") && (
                            <div className="drawwrap" style={{ marginTop: 8 }}>
                              <BoardView board={g.board}
                                cuts={bd.rows.filter((l) => l.at)}
                                left={bd.left}
                                label={`${bd.board.charAt(0).toUpperCase()}${bd.board.slice(1)} · ${g.board.label} · ${bd.rows.length} piece${bd.rows.length === 1 ? "" : "s"}${bd.left.length ? `, ${bd.left.length} offcut${bd.left.length === 1 ? "" : "s"}` : ", nothing left"}`} />
                            </div>
                          )}
                          <table style={{ marginTop: 8 }}>
                            <thead>
                              <tr>
                                <th>{bd.board.charAt(0).toUpperCase() + bd.board.slice(1)}</th>
                                <th>Cut</th><th>Out of</th><th>For</th>
                              </tr>
                            </thead>
                            <tbody>
                              {bd.rows.map((l, i) => (
                                <tr key={i}>
                                  <td>{i + 1}</td>
                                  <td>
                                    {fmt(l.dx)} × {fmt(l.dz)}
                                    {l.parts ? ` · part ${l.part} of ${l.parts}` : ""}
                                    {l.short ? ` · ${fmt(l.short)} mm short` : ""}
                                  </td>
                                  <td>
                                    {l.outOf
                                      ? `${fmt(l.outOf.l)} × ${fmt(l.outOf.w)}${l.reused ? " offcut" : " board"}`
                                      : "—"}
                                  </td>
                                  <td>{l.module}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          </div>
                        ))}
                      </details>
                      {g.pool.length > 0 && (
                        <>
                          <p className="note">PIR offcuts left over:</p>
                          <div className="pills">
                            {g.pool
                              .sort((a, b) => b.l * b.w - a.l * a.w)
                              .map((x, i) => (
                                <span className="pill" key={i}>{fmt(x.l)} × {fmt(x.w)}</span>
                              ))}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
                {!alloc.pirShared && alloc.pir.size > 1 && (
                  <div className="flag">
                    Floor {fPirT || "none"}, walls {wPirT || "none"}, roof {roofPirT} mm — each
                    distinct thickness is its own material and its own pile. Put two of them on
                    the same thickness and either can be cut from the other's offcuts.
                  </div>
                )}

                <h2>Timber</h2>
                <p className="note">
                  Floor, roof, upstand and furring are packed together per section, which is how
                  you'd buy it — a roof noggin can come out of a floor length's offcut.
                </p>
                {timber.map((t) => (
                  <div key={t.sec.id} style={{ marginTop: 18 }}>
                    <h2 style={{ marginBottom: 6 }}>
                      {t.sec.label} mm — {t.bars.length} × {(t.stock / 1000).toFixed(1)} m
                    </h2>
                    <p className="note" style={{ marginBottom: 8 }}>
                      Used by {t.sources.join(", ").toLowerCase()} · {t.linearM.toFixed(1)} m of timber.
                    </p>
                    <div className="bars">
                      {t.bars.map((b, i) => (
                        <div className="bar" key={i}>
                          {b.cuts.map((c, j) => (
                            <div className="barseg" key={j} style={{ flexGrow: c, flexBasis: 0 }} title={`${fmt(c)} mm`}>
                              {c / stock > 0.13 ? fmt(c) : ""}
                            </div>
                          ))}
                          {b.offcut > 20 && (
                            <div className="barwaste" style={{ flexGrow: b.offcut, flexBasis: 0 }}
                              title={`${fmt(b.offcut)} mm offcut`}>
                              {b.offcut / stock > 0.13 ? fmt(b.offcut) : ""}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    {t.offcuts.length > 0 && (
                      <>
                        <p className="note">Offcuts over 250 mm — rip noggins and packers from these:</p>
                        <div className="pills">
                          {t.offcuts.map((o, i) => (
                            <span className="pill" key={i}>{fmt(o)} mm</span>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                ))}

                {clad && clad.lengths > 0 && (
                  <div style={{ marginTop: 18 }}>
                    <h2 style={{ marginBottom: 6 }}>
                      Cladding — {clad.lengths} × {(clad.stock / 1000).toFixed(1)} m
                    </h2>
                    <p className="note" style={{ marginBottom: 8 }}>
                      {fmt(clad.face)} × {fmt(clad.thickness)} mm boards at {fmt(clad.cover)} mm
                      cover, {clad.cladding} · {clad.count} pieces, {(clad.linear / 1000).toFixed(1)} m
                      cut from {(clad.bought / 1000).toFixed(1)} m bought. Packed longest first, so
                      a short piece beside a door lands on the tail of a length already cut rather
                      than opening another.
                    </p>
                    <div className="bars">
                      {clad.bars.map((b, i) => (
                        <div className="bar" key={i}>
                          {b.cuts.map((c, j) => (
                            <div className="barseg" key={j}
                              style={{ flexGrow: c, flexBasis: 0 }} title={`${fmt(c)} mm`}>
                              {c / clad.stock > 0.13 ? fmt(c) : ""}
                            </div>
                          ))}
                          {b.offcut > 20 && (
                            <div className="barwaste" style={{ flexGrow: b.offcut, flexBasis: 0 }}
                              title={`${fmt(b.offcut)} mm offcut`}>
                              {b.offcut / clad.stock > 0.13 ? fmt(b.offcut) : ""}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    <table className="cuts" style={{ marginTop: 10 }}>
                      <thead>
                        <tr><th>Length</th><th>Qty</th><th>Total</th></tr>
                      </thead>
                      <tbody>
                        {clad.cuts.map((c) => (
                          <tr key={c.len}>
                            <td>{fmt(c.len)} mm</td>
                            <td>{c.qty}</td>
                            <td>{((c.len * c.qty) / 1000).toFixed(1)} m</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {clad.offcuts.length > 0 && (
                      <>
                        <p className="note">
                          Offcuts over {CLAD_MIN} mm left when every piece is cut — long enough
                          to be a board somewhere, and the packing already tried:
                        </p>
                        <div className="pills">
                          {clad.offcuts.map((o, i) => (
                            <span className="pill" key={i}>{fmt(o)} mm</span>
                          ))}
                        </div>
                      </>
                    )}
                    {clad.short > 0 && (
                      <p className="note">
                        {clad.short} of these are short pieces round the openings —{" "}
                        {clad.shortFromOffcut} of them come off the tail of a length already
                        cut rather than opening one of their own. A board beside a reveal is
                        still cladding, so none of it is left off the list.
                      </p>
                    )}
                  </div>
                )}

                <h2>Order summary and cost</h2>
                <p className="note">
                  Quantities come from the same allocations the cut lists use. Put a unit price
                  against anything you have a figure for; the running total sits in the top right
                  and follows you between sections. Prices are saved with the design.
                </p>
                <div className="pair" style={{ maxWidth: 220, margin: "12px 0" }}>
                  <div>
                    <label style={{ fontSize: 11.5, color: "var(--soft)" }}>Currency</label>
                    <input type="text" value={currency} maxLength={3} aria-label="Currency symbol"
                      onChange={(e) => setCurrency(e.target.value)} />
                  </div>
                </div>
                <table className="pricetable">
                  <thead>
                    <tr>
                      <th>Item</th><th>Quantity</th><th>Unit price</th><th>Line</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orderLines.map((l) => {
                      const unit = Number(prices[l.id]);
                      const has = Number.isFinite(unit) && unit > 0;
                      return (
                        <tr key={l.id}>
                          <td>{l.label}</td>
                          <td>{l.qty} {l.unit}{PLURAL_UNITS.has(l.unit) && l.qty !== 1 ? "s" : ""}</td>
                          <td>
                            <input type="number" min="0" step="0.01" inputMode="decimal"
                              value={prices[l.id] ?? ""} placeholder="—"
                              aria-label={`Unit price for ${l.label}`}
                              onChange={(e) => {
                                const v = e.target.value;
                                setPrices((cur) => {
                                  const next = { ...cur };
                                  if (v === "") delete next[l.id];
                                  else next[l.id] = Number(v);
                                  return next;
                                });
                              }} />
                          </td>
                          <td>{has ? money(unit * l.qty) : "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td>
                        Total
                        {cost.unpriced.length
                          ? ` — ${cost.priced} of ${orderLines.length} items priced`
                          : " — everything priced"}
                      </td>
                      <td /><td />
                      <td>{money(cost.total)}</td>
                    </tr>
                  </tfoot>
                </table>
                {cost.unpriced.length > 0 && (
                  <div className="flag">
                    No price yet for: {cost.unpriced.join(", ")}. The total is only what's been
                    filled in, so treat it as a partial figure until this list is empty.
                  </div>
                )}
                <div className="iorow">
                  <button className="navbtn" onClick={() => setPrices({})}>Clear all prices</button>
                </div>
                <p className="note">
                  Nothing here covers fixings by the box, membranes, tapes, foam, cladding,
                  insulation for the floor and walls, or labour. Buy one spare length of each
                  section, and remember every figure assumes timber milled to its nominal section.
                </p>

                <h2>3D print export</h2>
                <p className="note">
                  Binary STL in millimetres, Z up, built from the same model the drawings use.
                  Framing detail only survives generous scales — 11 mm OSB at 1:50 is 0.22 mm and
                  won't appear at all, so thin parts can be fattened to a printable minimum, or use
                  Massing for a solid block.
                </p>
                <table>
                  <tbody>
                    <tr>
                      <td>Actual size</td>
                      <td>{fmt(assembly.ext.dx)} × {fmt(assembly.ext.dz)} × {fmt(assembly.ext.dy)} mm</td>
                    </tr>
                    <tr>
                      <td>On the bed at 1:{asmScale}</td>
                      <td>
                        {printSize.x.toFixed(0)} × {printSize.y.toFixed(0)} ×{" "}
                        {printSize.z.toFixed(0)} mm
                      </td>
                    </tr>
                    <tr>
                      <td>Thinnest part ({assembly.ext.thinnest})</td>
                      <td>
                        {printSize.minFeature.toFixed(2)} mm
                        {asmMinFeature && printSize.minFeature < asmMinFeature
                          ? ` → ${printSize.effective.toFixed(2)} mm after fattening`
                          : ""}
                      </td>
                    </tr>
                    <tr><td>Separate solids</td><td>{assembly.boxes.length}</td></tr>
                  </tbody>
                </table>
                <div className="iorow">
                  {[10, 20, 25, 50, 100].map((v) => (
                    <button key={v} className={asmScale === v ? "navbtn on" : "navbtn"}
                      onClick={() => setAsmScale(v)}>1:{v}</button>
                  ))}
                </div>
                <div className="iorow">
                  {[{ v: 0, l: "No fattening" }, { v: 0.9, l: "Min 0.9 mm" }, { v: 1.2, l: "Min 1.2 mm" }].map((o) => (
                    <button key={o.v} className={asmMinFeature === o.v ? "navbtn on" : "navbtn"}
                      onClick={() => setAsmMinFeature(o.v)}>{o.l}</button>
                  ))}
                </div>
                {printSize.biggest > 250 && (
                  <div className="flag">
                    Longest dimension is {printSize.biggest.toFixed(0)} mm, past the bed on most
                    256 mm printers.
                  </div>
                )}
                <div className="iorow">
                  <button className="navbtn on" onClick={exportSTL}>
                    Download STL — 1:{asmScale} {asmMode}
                  </button>
                </div>

                <h2>Save &amp; load</h2>
                <p className="note">
                  The whole design — every dimension, section, sheet size, the window and the print
                  settings — as one JSON file. Load it back to pick up exactly where you left off.
                </p>
                <div className="iorow">
                  <button className="navbtn on" onClick={downloadConfig}>Download JSON</button>
                  <button className="navbtn" onClick={() => fileRef.current && fileRef.current.click()}>
                    Load from file
                  </button>
                  <button className="navbtn"
                    onClick={async () => {
                      const text = configJSON();
                      try {
                        await navigator.clipboard.writeText(text);
                        setIoMsg({ ok: true, text: "Copied to the clipboard." });
                      } catch (err) {
                        setPasted(text);
                        setIoMsg({ ok: false, text: "Clipboard blocked — the JSON is in the box below, copy it from there." });
                      }
                    }}>
                    Copy JSON
                  </button>
                  <input type="file" accept="application/json,.json" ref={fileRef}
                    onChange={readFile} style={{ display: "none" }} />
                </div>
                {ioMsg && (
                  <div className={ioMsg.ok ? "flag good" : "flag"}>{ioMsg.text}</div>
                )}
                <textarea className="iobox" value={pasted} spellCheck={false}
                  placeholder="Or paste a saved design here and press Load"
                  onChange={(e) => setPasted(e.target.value)} />
                <div className="iorow">
                  <button className="navbtn" disabled={!pasted.trim()}
                    onClick={() => applyConfig(pasted)}>
                    Load pasted
                  </button>
                  <button className="navbtn" onClick={() => { setPasted(""); setIoMsg(null); }}>
                    Clear
                  </button>
                </div>
                <p className="note">
                  If the download or file picker does nothing, the preview sandbox is blocking it —
                  use Copy JSON and the paste box instead.
                </p>

                <p className="note">
                  Buy one spare length of each section. Every figure here assumes timber milled to
                  exactly its nominal section, which it rarely is — measure your actual stock before
                  cutting, and treat these as an order list rather than a cutting instruction.
                </p>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
